import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Trash2, LayoutGrid, PlusCircle, Crosshair, FileSpreadsheet, X, ClipboardList, Hash, User, Calendar, FileText, ChevronRight, Package, Wrench, Zap, Camera, ImagePlus, Trash, Settings, ScanLine, Loader, Pencil, Bolt } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '@fontsource/barlow/400.css';
import '@fontsource/barlow/600.css';
import '@fontsource/barlow/700.css';
import '@fontsource/barlow/900.css';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/barlow-condensed/900.css';
import iconoApp from './assets/icono.png';
import wtgIcon from './assets/wtg_icon.png';
import { supabase } from './supabaseClient';
import { TechContext, useTech } from './TechContext';

const THEMES = {
  dark: {
    bg:          '#1a1c1e',
    bgCard:      '#222426',
    bgDark:      '#111314',
    bgHover:     '#272a2c',
    bgDone:      '#1e2820',
    bgSheet:     'rgba(17,19,20,0.97)',
    orange:      '#e07b1a',
    orangeHover: '#f08c2a',
    green:       '#5a9e45',
    border:      '#2a2c2e',
    borderMid:   '#3a3c3e',
    text:        '#f0f0ee',
    textMuted:   '#b0afa8',
    textDim:     '#7a7a72',
    overlay:     'rgba(0,0,0,0.85)',
  },
  light: {
    bg:          '#f4f2ed',
    bgCard:      '#ffffff',
    bgDark:      '#e8e5de',
    bgHover:     '#f0ede6',
    bgDone:      '#e8f5e4',
    bgSheet:     'rgba(232,229,222,0.97)',
    orange:      '#c96a10',
    orangeHover: '#e07b1a',
    green:       '#3d7a2e',
    border:      '#d4d0c8',
    borderMid:   '#bbb8b0',
    text:        '#1a1c1e',
    textMuted:   '#4a4a44',
    textDim:     '#7a7a72',
    overlay:     'rgba(0,0,0,0.5)',
  },
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const STORAGE_KEY = 'windmanager_proyectos';

// Migrate legacy storage key (typo fix: "winmanager" → "windmanager")
const datosViejos = localStorage.getItem('winmanager_proyectos');
if (datosViejos && datosViejos !== '[]') {
    const datosActuales = localStorage.getItem('windmanager_proyectos');
    if (!datosActuales || datosActuales === '[]') {
        localStorage.setItem('windmanager_proyectos', datosViejos);
    }
    localStorage.removeItem('winmanager_proyectos');
}

const herramientasDefault = () => ({ bombas:[], hidraulicas:[], tensionadores:[] });
const generarPin = (nombreParque) => {
  const primeras2Letras = (nombreParque || '').substring(0, 2).toUpperCase();
  const random4Digitos = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  return primeras2Letras + random4Digitos;
};
const migrarProyectos = (proyectos) => {
    return proyectos.map(p => ({
        ...p,
        maquinas: p.maquinas?.map(m => ({
            ...m,
            boltRegister: m.boltRegister || [],
            tareas: m.tareas?.map(f => {

                if (f.nombre === 'Cabling' || f.nombre === 'Cableado') {
                    const sinPit = f.subtareas.filter(s => s.nombre !== 'Pit' && s.nombre !== 'Foso');
                    const nuevas = ['Connect Transformer', 'Connect Switchgear', 'Cabling Controller & UPS'];
                    const yaExisten = nuevas.filter(n => !sinPit.some(s => s.nombre === n));
                    return {
                        ...f,
                        nombre: 'Cabling',
                        subtareas: [
                            ...sinPit,
                            ...yaExisten.map(nombre => ({
                                id: Date.now() + Math.random(),
                                nombre, hecho: false, fecha: '', operario: '', notas: '',
                                item: '', serial: '', fabricante: '', vui: '', fotos: [],
                                _tipo: 'libre',
                            }))
                        ]
                    };
                }

                if (f.nombre === 'Preparations' || f.nombre === 'Preparaciones') {
                    const yaExiste = f.subtareas.some(s => s.nombre === 'Installation of Turning Gear');
                    return {
                        ...f,
                        nombre: 'Preparations',
                        subtareas: yaExiste ? f.subtareas : [
                            ...f.subtareas,
                            { id: Date.now() + Math.random(), nombre: 'Installation of Turning Gear', hecho: false, fecha: '', operario: '', notas: '', item: '', serial: '', fabricante: '', vui: '', fotos: [], _tipo: 'operario' }
                        ]
                    };
                }

                if (f.nombre === 'Main Assembly' || f.nombre === 'Montaje Principal') {
                    const yaExiste = f.subtareas.some(s => s.nombre === 'Generator Alignment');
                    return {
                        ...f,
                        nombre: 'Main Assembly',
                        subtareas: yaExiste ? f.subtareas : [
                            ...f.subtareas,
                            { id: Date.now() + Math.random(), nombre: 'Generator Alignment', hecho: false, fecha: '', operario: '', notas: '', item: '', serial: '', fabricante: '', vui: '', fotos: [], _tipo: 'operario' }
                        ]
                    };
                }

                return f;
            })
            .map(f => ({
                ...f,
                subtareas: f.subtareas?.map(s => ({
                    ...s,
                    camposExtra:  s.camposExtra  || {},
                    modelType:    s.modelType    || '',
                    metrica:      s.metrica      || '',
                    bomGaugeSerial: s.bomGaugeSerial || '',
                })) ?? []
            }))
            ?? []
        })) ?? []
    }));
};
const cargarDesdeStorage = () => [];
const guardarEnStorage = () => {};

const cargarProyectosSupabase = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) { console.error('No user logged in'); return []; }
  const { data, error } = await supabase
    .from('proyectos')
    .select('*, maquinas(*)')
    .eq('owner_id', userId);
  if (error) { console.error('Error cargando proyectos:', error); return []; }
  return (data || []).map(p => ({
    ...p,
    nombre_parque: p.nombre || p.nombre_parque || '',
    componentesPersonalizados: p.componentes_personalizados || [],
    herramientas: { ...herramientasDefault(), ...(p.herramientas || {}) },
    maquinas: (p.maquinas || []).map(m => ({
      ...m,
      tareas: m.tareas || [],
      herramientas: { ...herramientasDefault(), ...(m.herramientas || {}) },
      componentesExtra: m.componentes_extra || componentesExtraDefault(),
      boltRegister: m.bolt_register || [],
    }))
  }));
};
const MSG_TRIAL_EXPIRED = 'Trial ended. Read-only mode. Subscribe to keep editing.';
const MSG_ACTIVATE_PARK = 'Activate this wind farm (€270) to edit it';
const MSG_TRIAL_PARK_LIMIT = 'You have reached the free trial limit of one wind farm. Subscribe to create more.';
const mensajeErrorTrial = (error) => {
  const txt = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''} ${error?.code || ''}`;
  if (txt.includes('TRIAL_EXPIRED')) return { codigo: 'TRIAL_EXPIRED', msg: MSG_TRIAL_EXPIRED };
  if (txt.includes('TRIAL_PARK_LIMIT')) return { codigo: 'TRIAL_PARK_LIMIT', msg: MSG_TRIAL_PARK_LIMIT };
  return null;
};
// Devuelve true si el error era de trial (y ya se ha avisado a la UI).
const avisarErrorEscritura = (error) => {
  const t = mensajeErrorTrial(error);
  if (t) window.dispatchEvent(new CustomEvent('windmanager:write-error', { detail: t }));
  return !!t;
};
const guardarProyectoSupabase = async (proyecto) => {
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) { console.error('No user logged in'); return; }
  const { error } = await supabase
    .from('proyectos')
    .upsert({
      id: String(proyecto.id),
      owner_id: userId,
      nombre: proyecto.nombre_parque || proyecto.nombre || '',
      cliente: proyecto.cliente || '',
      modelo_maquina: proyecto.modelo_maquina || proyecto.modeloMaquina || '',
      num_tramos: proyecto.num_tramos || proyecto.numTramos || 4,
      con_dt: proyecto.dt_aparte || proyecto.conDT || false,
      lat: proyecto.coords?.[0] || null,
      lng: proyecto.coords?.[1] || null,
      tema: proyecto.tema || 'dark',
      ...(proyecto.slug && { slug: proyecto.slug }),
      ...(proyecto.pin && { pin: proyecto.pin }),
      componentes_personalizados: proyecto.componentesPersonalizados || [],
      herramientas: proyecto.herramientas || herramientasDefault(),
    });
  if (error && !avisarErrorEscritura(error)) console.error('Error guardando proyecto:', error);
};
const guardarMaquinaSupabase = async (maquina, proyectoId) => {
  const id = typeof maquina.id === 'number' ? crypto.randomUUID() : maquina.id;
  const maquinaData = {
    id: id,
    proyecto_id: String(proyectoId),
    nombre: maquina.nombre || '',
    lat: maquina.lat || maquina.coords?.[0] || null,
    lng: maquina.lng || maquina.coords?.[1] || null,
    modelo_especifico: maquina.modelo_especifico || '',
    tareas: maquina.tareas || [],
    herramientas: maquina.herramientas || herramientasDefault(),
    componentes_extra: maquina.componentesExtra || componentesExtraDefault(),
    bolt_register: maquina.boltRegister || [],
  };

  const { error } = await supabase.rpc('update_maquina_in_proyecto', {
    p_proyecto_id: String(proyectoId),
    p_maquina_id: id,
    p_maquina_data: maquinaData,
  });

  if (error) {
    if (avisarErrorEscritura(error)) return;
    console.error('Error guardando máquina con jsonb_set:', error);
    const fallbackError = await supabase
      .from('maquinas')
      .upsert(maquinaData);
    if (fallbackError.error && !avisarErrorEscritura(fallbackError.error)) console.error('Fallback upsert error:', fallbackError.error);
  }
};
const comprimirImagen = (file, maxWidth = 1200, quality = 0.72) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (ev) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
            const ratio = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width  = Math.round(img.width  * ratio);
            canvas.height = Math.round(img.height * ratio);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            canvas.toBlob(blob => resolve({ dataUrl, base64: dataUrl.split(',')[1], blob }), 'image/jpeg', quality);
        };
        img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
});

const subirFotoSupabase = async (blob, maquinaId) => {
    const path = `${maquinaId}/${Date.now()}.jpg`;
    const { error } = await supabase.storage.from('fotos-wtg').upload(path, blob, { contentType: 'image/jpeg' });
    if (error) throw error;
    return supabase.storage.from('fotos-wtg').getPublicUrl(path).data.publicUrl;
};

const FABRICANTE_CAMPOS = {
    vestas: {
        detectar: (modelo) => /vestas|v\d{3}|v136|v150|v162|v117|v126/i.test(modelo),
        campos: ['VUI', 'Part No.', 'Drawing No.', 'Revision'],
    },
    sgamesa: {
        detectar: (modelo) => /siemens|gamesa|sg\s?\d|5\.0|6\.0|7\.0|8\.0|SG/i.test(modelo),
        campos: ['SAP No.', 'Drawing No.', 'Material No.', 'Revision'],
    },
    ge: {
        detectar: (modelo) => /ge\s|general electric|haliade|\bge\b/i.test(modelo),
        campos: ['GE Part No.', 'Serial Tag', 'PO No.'],
    },
    nordex: {
        detectar: (modelo) => /nordex|acciona|n\d{3}|aw\d{3}/i.test(modelo),
        campos: ['Part No.', 'Drawing No.', 'Project No.'],
    },
};

const detectarFabricante = (modeloMaquina) => {
    if (!modeloMaquina) return null;
    for (const [key, val] of Object.entries(FABRICANTE_CAMPOS)) {
        if (val.detectar(modeloMaquina)) return key;
    }
    return null;
};

const getCamposFabricante = (modeloMaquina) => {
    const fab = detectarFabricante(modeloMaquina);
    return fab ? FABRICANTE_CAMPOS[fab].campos : [];
};

const EXTRA_COMPONENTES = [
    'Transformer','Generator','Internal Crane',
    'Aviation Light 1','Aviation Light 2',
    'FT Wind Sensor 1','FT Wind Sensor 2',
    'Wind Vane','Anemometer','Rescue',
    'Coupling','Main Bearing','Tower Controller',
    'UPS','Light System','High-Voltage Cable',
    'Service Lift',
];
const componenteExtraVacio = () => ({ item:'', serial:'', fabricante:'', vui:'', operario:'', fecha:'', notas:'', fotos:[], hecho:false, camposExtra:{} });
const componentesExtraDefault = () => Object.fromEntries(EXTRA_COMPONENTES.map(n => [n, componenteExtraVacio()]));

const TIPO = {
    COMPONENTE: 'componente',
    PALA:       'pala',
    APRIETE:    'apriete',
    OPERARIO:   'operario',
    LIBRE:      'libre',
};

const tipoDeSubtarea = (nombre, faseId) => {
    const n = nombre.toLowerCase();
    if (faseId === 5) return TIPO.APRIETE;
    if (n.startsWith('blade ')) return TIPO.PALA;
    const esComponente = ['nacelle','hub','bottom','top','md1','md2','md3','md4','md5','md6',
        'dt','switchgear','top cooler','elevator','hv cable'].some(c => n.includes(c));
    if (esComponente && faseId === 1) return TIPO.COMPONENTE;
    if (esComponente) return TIPO.OPERARIO;
    return TIPO.LIBRE;
};

const formatearFecha = (iso) => { if (!iso) return ''; const [y,m,d] = iso.split('-'); return `${d}/${m}/${y}`; };
const fechaParaInput = (dmy) => { if (!dmy||!dmy.includes('/')) return dmy||''; const [d,m,y]=dmy.split('/'); return `${y}-${m}-${d}`; };
const hoyISO = () => { const h=new Date(); return `${h.getFullYear()}-${String(h.getMonth()+1).padStart(2,'0')}-${String(h.getDate()).padStart(2,'0')}`; };

const datosVacios = (tipo) => {
    switch(tipo) {
        case TIPO.COMPONENTE: return { item:'', serial:'', fabricante:'', modelType:'', vui:'', camposExtra:{}, fotos:[] };
        case TIPO.PALA:       return { item:'', serial:'', fabricante:'', modelType:'', vui:'', numSet:'', set:'', camposExtra:{}, fotos:[] };
        case TIPO.APRIETE:    return { tornItem:'', tornSerial:'', tornFabricante:'', tornBatch:'', tornMetrica:'', metrica:'', bomTipo:'', bomSerial:'', bomBar:'', bomGaugeSerial:'', llaveTipo:'', llaveSerial:'', valorApriete:'', unidadApriete:'Nm' };
        default:              return {};
    }
};

const nuevaSub = (nombre, faseId) => ({
    nombre, hecho: false,
    fecha: '', operario: '', notas: '',
    ...datosVacios(tipoDeSubtarea(nombre, faseId)),
    _tipo: tipoDeSubtarea(nombre, faseId),
});

const palas = (fId) => ['Blade A','Blade B','Blade C'].map(p => nuevaSub(p, fId));
const generarTareas = (tramos, conDT) => {
    const t   = parseInt(tramos) || 4;
    const mds = Array.from({length: t-2}, (_,i) => `MD${i+1}`);
    const flanges = Array.from({length: t-1}, (_,i) => `Flange ${i+1}`);
    const dt  = (fId) => conDT ? [nuevaSub('DT', fId)] : [];

    return [
        { id:1, nombre:'Unloading',        peso:10, hecho:false, subtareas:[
            nuevaSub('Site Parts',1), nuevaSub('Bolts',1), nuevaSub('Switchgear',1), nuevaSub('Top Cooler',1),
            nuevaSub('Bottom',1), ...mds.map(m=>nuevaSub(m,1)), nuevaSub('Top',1),
            nuevaSub('Nacelle',1), ...dt(1), nuevaSub('Hub',1), ...palas(1),
        ]},
        { id:2, nombre:'Preparations',    peso:15, hecho:false, subtareas:[
            nuevaSub('Top Cooler',2), nuevaSub('Access Ladder',2), nuevaSub('Bottom',2),
            ...mds.map(m=>nuevaSub(m,2)), nuevaSub('Top',2), nuevaSub('Nacelle',2),
            nuevaSub('Hub',2), ...dt(2), ...palas(2), nuevaSub('Installation of Turning Gear',2),
        ]},
        { id:3, nombre:'Pre-assembly',    peso:15, hecho:false, subtareas:[
            nuevaSub('Bottom',3),
            ...(t >= 4 ? [nuevaSub('MD1',3)] : []),
            nuevaSub('Access Ladder',3),
        ]},
        { id:4, nombre:'Main Assembly',   peso:40, hecho:false, subtareas:[
            ...mds.slice(-1).map(()=>nuevaSub(`MD${t-2}`,4)),
            nuevaSub('Top',4), nuevaSub('Elevator',4), nuevaSub('Nacelle',4),
            ...dt(4), nuevaSub('Hub',4), ...palas(4), nuevaSub('Generator Alignment',4),
        ]},
        { id:5, nombre:'Torque',          peso:5,  hecho:false, subtareas:[
            ...flanges.map(f=>nuevaSub(f,5)), nuevaSub('Top-Nacelle',5), ...dt(5),
            nuevaSub('Hub',5), ...palas(5), nuevaSub('Anchor Cage',5),
        ]},
        { id:6, nombre:'Cabling',         peso:10, hecho:false, subtareas:[
            nuevaSub('HV Cable',6), nuevaSub('LV Cable',6),
            nuevaSub('Connect Transformer',6), nuevaSub('Connect Switchgear',6), nuevaSub('Cabling Controller & UPS',6),
        ]},
        { id:7, nombre:'Finishing',       peso:5,  hecho:false, subtareas:[
            nuevaSub('Finishing works',7), nuevaSub('Inspection',7),
        ]},
    ];
};

function ClickToAddMachine({ onAdd, disabled, ignoreNextRef }) {
    useMapEvents({ click(e) {
        if (ignoreNextRef?.current) { ignoreNextRef.current = false; return; }
        if (!disabled) onAdd([e.latlng.lat, e.latlng.lng]);
    }});
    return null;
}
function RecenterMap({ coords }) {
    const map = useMap();
    useEffect(() => { if (coords) map.setView(coords, 13); }, [coords]);
    return null;
}
function FitBoundsMap({ bounds }) {
    const map = useMap();
    useEffect(() => { if (bounds && bounds.length > 0) map.fitBounds(bounds, { padding: [40, 40] }); }, [bounds]);
    return null;
}

const Campo = ({ label, icon, value, onChange, placeholder, mono, upper, maxLen, type='text', col='1', disabled, C }) => {
    const mLS = { display:'flex', alignItems:'center', fontWeight:700, fontSize:'0.68rem', color:C.textDim, marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'Barlow Condensed', sans-serif" };
    const mIS = { width:'100%', padding:'9px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' };
    return (
        <div style={{ gridColumn: col }}>
            <label style={mLS}>{icon && React.cloneElement(icon,{size:11,style:{marginRight:4}})}{label}</label>
            <input type={type} value={value}
                onChange={e => onChange(upper ? e.target.value.toUpperCase().slice(0,maxLen||99) : maxLen ? e.target.value.slice(0,maxLen) : e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                style={{ ...mIS, fontFamily: mono?'monospace':'inherit', letterSpacing: upper?'0.08em':'normal', fontWeight: upper?700:'normal', ...(disabled && { opacity:0.6, cursor:'not-allowed' }) }}
            />
        </div>
    );
};

const buscarSerialEnDescargas = (maquina, nombreSub) => {
    const faseDescargas = maquina?.tareas?.find(f =>
        f.nombre === 'Descargas' ||
        f.nombre === 'Unloading'
    );
    if (!faseDescargas) return null;

    const normalizar = (nombre) => nombre
        .toLowerCase()
        .replace('pala', 'blade')
        .trim();

    const sub = faseDescargas.subtareas?.find(s =>
        normalizar(s.nombre) === normalizar(nombreSub)
    );

    if (!sub || (!sub.serial && !sub.item)) return null;
    return { serial: sub.serial||'', item: sub.item||'', fabricante: sub.fabricante||'', vui: sub.vui||'', numSet: sub.numSet||'', set: sub.set||'', modelType: sub.modelType||'', camposExtra: sub.camposExtra || {} };
};

const crearTurbineIcon = (progreso) => {
    const color = progreso >= 100 ? '#5a9e45' : progreso > 0 ? '#e07b1a' : '#3a3c3e';
    return L.divIcon({
        className: '',
        html: `
            <div style="display:flex; flex-direction:column; align-items:center; width:36px;">
                <img src="${wtgIcon}" style="
                    width: 28px;
                    height: 82px;
                    object-fit: contain;
                    filter: drop-shadow(1px 3px 2px rgba(0,0,0,0.4));
                "/>
                <div style="
                    width: 36px;
                    height: 5px;
                    background: rgba(0,0,0,0.25);
                    border-radius: 3px;
                    margin-top: 2px;
                    overflow: hidden;
                ">
                    <div style="
                        width: ${progreso}%;
                        height: 100%;
                        background: ${color};
                        border-radius: 3px;
                        transition: width 0.3s ease;
                    "></div>
                </div>
            </div>
        `,
        iconSize:    [36, 95],
        iconAnchor:  [18, 89],
        popupAnchor: [0, -89],
    });
};

const PDF = {
  orange: [224, 123, 26],
  dark:   [26,  28,  30],
  green:  [90,  158, 69],
  red:    [192, 57,  43],
  white:  [255, 255, 255],
  light:  [248, 246, 241],
  border: [220, 218, 210],
  text:   [30,  30,  28],
  muted:  [100, 100, 95],
  dim:    [170, 170, 165],
};

const calcularProgresoPDF = (tareas) => {
  if (!tareas?.length) return 0;
  return Math.round(tareas.reduce((acc, fase) => {
    const h = fase.subtareas.filter(s => s.hecho).length;
    return acc + (fase.peso * (fase.subtareas.length > 0 ? h / fase.subtareas.length : 0));
  }, 0));
};

const pdfHeader = (doc, titulo, subtitulo) => {
  doc.setFillColor(...PDF.dark);
  doc.rect(0, 0, 210, 2, 'F');
  doc.addImage(iconoApp, 'PNG', 14, 3, 14, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(26, 28, 30);
  doc.text('Wind', 30, 12);
  const wWind = doc.getTextWidth('Wind');
  doc.setTextColor(...PDF.orange);
  doc.text('Manager', 30 + wWind, 12);
  const wManager = doc.getTextWidth('Manager');
  doc.setTextColor(26, 28, 30);
  doc.text('Pro', 30 + wWind + wManager, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...PDF.muted);
  doc.text('FIELD OPERATIONS', 30, 16.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PDF.dark);
  doc.text(titulo, 196, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...PDF.muted);
  doc.text(subtitulo, 196, 15, { align: 'right' });
  doc.setDrawColor(...PDF.border);
  doc.setLineWidth(0.3);
  doc.line(14, 20, 196, 20);
};

const pdfFooter = (doc, parque, extra) => {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...PDF.orange);
    doc.setLineWidth(0.4);
    doc.line(14, 285, 196, 285);
    doc.setFontSize(7);
    doc.setTextColor(...PDF.dim);
    doc.text(parque, 14, 290);
    doc.text(extra, 105, 290, { align: 'center' });
    doc.text(`${i} / ${pages}`, 196, 290, { align: 'right' });
  }
};

const pdfPortada = (doc, titulo, subtitulo, datos, progreso, parque, pt, proyecto) => {
  const _pt = pt || PDF_T.en;
  const hoy = new Date().toLocaleDateString('en-GB');
  doc.setFillColor(...PDF.dark);
  doc.rect(0, 0, 210, 18, 'F');
  doc.addImage(iconoApp, 'PNG', 14, 3, 14, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('Wind', 30, 12);
  const wW = doc.getTextWidth('Wind');
  doc.setTextColor(...PDF.orange);
  doc.text('Manager', 30 + wW, 12);
  const wM = doc.getTextWidth('Manager');
  doc.setTextColor(255, 255, 255);
  doc.text('Pro', 30 + wW + wM, 12);
  doc.setFillColor(...PDF.dark);
  doc.rect(0, 18, 210, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(...PDF.dark);
  doc.text(titulo, 14, 55);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(...PDF.muted);
  doc.text(subtitulo, 14, 65);
  doc.setFillColor(...PDF.orange);
  doc.rect(14, 72, 60, 1.2, 'F');
  let xd = 14, yd = 82;
  datos.forEach((d, i) => {
    const bw = 42, bh = 20;
    const xb = xd + i * (bw + 4);
    doc.setFillColor(...PDF.light);
    doc.setDrawColor(...PDF.border);
    doc.setLineWidth(0.3);
    doc.rect(xb, yd, bw, bh, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...PDF.dark);
    doc.text(String(d.valor), xb + bw / 2, yd + 11, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...PDF.muted);
    doc.text(d.label.toUpperCase(), xb + bw / 2, yd + 17, { align: 'center' });
  });
  if (progreso !== null) {
    const by = yd + 30;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...PDF.muted);
    doc.text('OVERALL PROGRESS', 14, by);
    doc.setFontSize(12);
    doc.setTextColor(...PDF.orange);
    doc.text(`${progreso}%`, 196, by, { align: 'right' });
    doc.setFillColor(...PDF.border);
    doc.rect(14, by + 3, 182, 5, 'F');
    doc.setFillColor(...PDF.orange);
    doc.rect(14, by + 3, 182 * progreso / 100, 5, 'F');
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF.muted);
  doc.text(parque, 14, 135);
  if (proyecto?.direccion) {
    doc.setFontSize(8);
    doc.setTextColor(...PDF.dim);
    const lineas = doc.splitTextToSize(proyecto.direccion, 120);
    doc.text(lineas, 14, 141);
  }
  doc.setFontSize(8);
  doc.setTextColor(...PDF.muted);
  doc.text(`${_pt.generado} ${hoy}`, 196, 135, { align: 'right' });
  doc.setFillColor(...PDF.dark);
  doc.rect(0, 270, 210, 27, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...PDF.dim);
  doc.text(`WINDMANAGER PRO · FIELD OPERATIONS · ${_pt.instalacion}`, 105, 282, { align: 'center' });
};

const PDF_T = {
  en: {
    subtitle_parque:      'Park status report',
    subtitle_wtg:         'Installed components report',
    subtitle_aprietes:    'Torque report',
    subtitle_componente:  'Component record',
    subtitle_herramientas:'Torque tools catalogue',
    wtgs: 'WTGs', modelo: 'Model', tramos: 'Sections',
    conDT: 'With DT', progreso: 'Progress', aprietes: 'Torques',
    resumen: 'WIND TURBINE SUMMARY',
    completadas: 'COMPLETED', pendientes: 'PENDING',
    si: 'Yes', no: 'No',
    componente: 'COMPONENT', estado: 'STATUS', fecha: 'DATE',
    operario: 'OP.', serial: 'SERIAL NUMBER', item: 'ITEM No.',
    fabricante: 'MANUFACTURER', vui: 'VUI',
    ok: 'OK', pend: '— PEND.',
    conexion: 'CONNECTION', valor: 'VALUE', unidad: 'UNIT',
    bomba: 'PUMP · S/N', presion: 'PRESSURE', llave: 'WRENCH · S/N',
    tornilleria: 'BOLT RECORD',
    batch: 'BATCH',
    fotosTitulo: 'PHOTO RECORD',
    herramientasTitulo: 'TOOLS CATALOGUE',
    bombasSec: 'TORQUE PUMPS', hidraulicasSec: 'HYDRAULIC WRENCHES',
    tensionadoresSec: 'TENSIONERS (PULLERS)',
    unidades: 'units', noHerramientas: 'No tools registered.',
    generado: 'Generated on', instalacion: 'INSTALLATION & TORQUE',
    completado: 'COMPLETED', pendiente: 'PENDING',
    serialNum: 'SERIAL NUMBER', nItem: 'ITEM No.',
    numSerie: 'SERIAL NUMBER',
  },
};
const generarPDFParque = (proyecto) => {
  const pt = PDF_T.en;
  const doc = new jsPDF();
  const hoy = new Date().toLocaleDateString('en-GB');
  const totalWTGs = proyecto.maquinas?.length;
  const progresoGlobal = totalWTGs > 0
    ? Math.round(proyecto.maquinas.reduce((a, m) => a + calcularProgresoPDF(m.tareas), 0) / totalWTGs)
    : 0;

  pdfPortada(doc,
    proyecto.nombre_parque.toUpperCase(),
    pt.subtitle_parque,
    [
      { valor: totalWTGs, label: pt.wtgs },
      { valor: proyecto.modelo_maquina || '—', label: pt.modelo },
      { valor: proyecto.num_tramos, label: pt.tramos },
      { valor: progresoGlobal + '%', label: pt.progreso },
    ],
    progresoGlobal,
    proyecto.nombre_parque,
    pt,
    proyecto
  );

  doc.addPage();
  pdfHeader(doc, proyecto.nombre_parque.toUpperCase(), pt.resumen);

  autoTable(doc, {
    startY: 28,
    head: [['WTG', pt.modelo, pt.tramos, pt.conDT, pt.progreso, pt.completadas, pt.pendientes]],
    body: proyecto.maquinas.map(m => {
      const pct = calcularProgresoPDF(m?.tareas || []);
      const total = (m?.tareas || []).reduce((a, f) => a + (f?.subtareas || []).length, 0);
      const hechas = (m?.tareas || []).reduce((a, f) => a + (f?.subtareas || []).filter(s => s?.hecho).length, 0);
      return [
        m?.nombre,
        m?.modelo_especifico || proyecto.modelo_maquina || '—',
        m?.tramos,
        proyecto.dt_aparte ? pt.si : pt.no,
        pct + '%',
        hechas,
        total - hechas,
      ];
    }),
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
    headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: PDF.light },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 22 },
      4: { halign: 'center', fontStyle: 'bold', textColor: PDF.orange },
      5: { halign: 'center', textColor: PDF.green },
      6: { halign: 'center', textColor: PDF.muted },
    },
    didDrawCell: (data) => {
      if (data.column.index === 4 && data.section === 'body') {
        const pct = parseInt(data.row.raw[4]);
        const bw = data.cell.width - 20;
        const by = data.cell.y + data.cell.height - 3;
        doc.setFillColor(...PDF.border);
        doc.rect(data.cell.x + 2, by, bw, 1.5, 'F');
        doc.setFillColor(...(pct >= 100 ? PDF.green : PDF.orange));
        doc.rect(data.cell.x + 2, by, bw * pct / 100, 1.5, 'F');
      }
    },
  });

  pdfFooter(doc, proyecto.nombre_parque, hoy);
  doc.save(`WINDMANAGER_PRO_${proyecto.nombre_parque.replace(/\s+/g, '_')}_PARQUE_${hoy.replace(/\//g, '')}.pdf`);
};

const generarPDFWTG = (proyecto, maquina) => {
  const pt = PDF_T.en;
  const doc = new jsPDF();
  const hoy = new Date().toLocaleDateString('en-GB');
  const progreso = calcularProgresoPDF(maquina.tareas);

  pdfPortada(doc,
    maquina.nombre,
    pt.subtitle_wtg,
    [
      { valor: maquina.modelo_especifico || proyecto.modelo_maquina || '—', label: pt.modelo },
      { valor: maquina.tramos, label: pt.tramos },
      { valor: proyecto.dt_aparte ? pt.si : pt.no, label: pt.conDT },
      { valor: progreso + '%', label: pt.progreso },
    ],
    progreso,
    `${proyecto.nombre_parque} · ${maquina.nombre}`,
    pt,
    proyecto
  );

  maquina.tareas.forEach(fase => {
    const hechas = (fase?.subtareas || []).filter(s => s?.hecho).length;
    const pctFase = (fase?.subtareas || []).length > 0 ? Math.round(hechas / (fase?.subtareas || []).length * 100) : 0;

    doc.addPage();
    pdfHeader(doc, maquina.nombre, fase?.nombre?.toUpperCase() + ` · Weight ${fase?.peso}%`);

    doc.setFontSize(8);
    doc.setTextColor(...PDF.muted);
    doc.text(`${hechas}/${(fase?.subtareas || []).length} completed · ${pctFase}%`, 14, 27);
    doc.setFillColor(...PDF.border);
    doc.rect(14, 29, 182, 3, 'F');
    doc.setFillColor(...(pctFase >= 100 ? PDF.green : PDF.orange));
    doc.rect(14, 29, 182 * pctFase / 100, 3, 'F');

    autoTable(doc, {
      startY: 36,
      head: [[pt.componente, pt.estado, pt.fecha, pt.operario, pt.serial, pt.item, pt.fabricante, pt.vui, 'NOTES']],
      body: (fase?.subtareas || []).map(sub => {
        if (!sub) return ['—', '—', '—', '—', '—', '—', '—', '—', ''];
        // For operario-type subtasks look up serial data from Descargas phase
        let sSerial = sub?.serial || sub?.tornSerial || '';
        let sItem   = sub?.item   || sub?.tornItem   || '';
        let sFab    = sub?.fabricante || sub?.tornFabricante || '';
        let sVui    = sub?.vui    || sub?.camposExtra?.['VUI'] || sub?.tornBatch  || '';
        if (sub?._tipo === 'operario' && !sSerial && !sItem) {
          const previo = buscarSerialEnDescargas(maquina, sub?.nombre);
          sSerial = previo?.serial    || '';
          sItem   = previo?.item      || '';
          sFab    = previo?.fabricante|| '';
          sVui    = previo?.vui       || previo?.camposExtra?.['VUI'] || '';
        }
        // For blade-type subtasks append set info to serial display
        let serialDisplay = sSerial;
        if (sub?._tipo === 'pala' && (sub?.numSet || sub?.set)) {
          const setLabel = [sub?.numSet, sub?.set].filter(Boolean).join('/');
          serialDisplay = [sSerial, setLabel ? `Set ${setLabel}` : ''].filter(Boolean).join(' · ');
        }
        const extraStr = Object.entries(sub?.camposExtra || {})
          .filter(([k,v]) => v)
          .map(([k,v]) => `${k}: ${v}`)
          .join(' · ');
        const notasCell = [sub?.notas, extraStr].filter(Boolean).join(' | ');
        return [
          sub?.nombre || '—',
          sub?.hecho ? pt.ok : pt.pend,
          sub?.fecha || '—',
          sub?.operario || '—',
          serialDisplay || '—',
          sItem   || '—',
          sFab    || '—',
          sVui    || '—',
          notasCell || '',
        ];
      }),
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
      headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: PDF.light },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 28 },
        1: { cellWidth: 16, halign: 'center', fontSize: 7 },
        2: { cellWidth: 16, halign: 'center', fontSize: 7 },
        3: { cellWidth: 10, halign: 'center' },
        4: { cellWidth: 32, font: 'courier', fontSize: 7 },
        5: { cellWidth: 18, font: 'courier', fontSize: 7 },
        6: { cellWidth: 22, fontSize: 7 },
        7: { cellWidth: 16, font: 'courier', fontSize: 7 },
        8: { cellWidth: 24, fontSize: 6, textColor: PDF.muted, overflow: 'linebreak' },
      },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === 'body') {
          if (data.row.raw[1] && data.row.raw[1].includes('OK')) {
            data.cell.styles.textColor = PDF.green;
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = PDF.dim;
          }
        }
        if (data.row.raw[1] && data.row.raw[1].includes('OK') && data.section === 'body') {
          data.cell.styles.fillColor = [240, 250, 235];
        }
      },
    });
  });

  const todasFotos = [];
  maquina.tareas.forEach(fase => {
    (fase?.subtareas || []).forEach(sub => {
      if (!sub) return;
      if (sub?.fotos?.length) {
        sub.fotos.forEach(f => todasFotos.push({ src: f?.src, desc: f?.desc || '', componente: sub?.nombre || '—', fase: fase?.nombre || '—' }));
      }
    });
  });

  if (todasFotos.length > 0) {
    doc.addPage();
    pdfHeader(doc, maquina.nombre, pt.fotosTitulo);
    let fx = 14, fy = 28, col = 0;
    const fw = 86, fh = 58;
    todasFotos.forEach(foto => {
      if (fy + fh + 20 > 280) { doc.addPage(); pdfHeader(doc, maquina.nombre, pt.fotosTitulo); fy = 28; col = 0; fx = 14; }
      try { doc.addImage(foto.src, 'JPEG', fx, fy, fw, fh); } catch {}
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...PDF.dark);
      doc.text(foto.componente + ' · ' + foto.fase, fx, fy + fh + 4);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...PDF.muted);
      if (foto.desc) doc.text(foto.desc, fx, fy + fh + 8);
      col++;
      if (col >= 2) { col = 0; fx = 14; fy += fh + 16; } else { fx = 110; }
    });
  }

  pdfFooter(doc, `${proyecto.nombre_parque} · ${maquina.nombre}`, hoy);
  doc.save(`WINDMANAGER_PRO_${proyecto.nombre_parque.replace(/\s+/g, '_')}_${maquina.nombre}_${hoy.replace(/\//g, '')}.pdf`);
};

const generarPDFFase = (proyecto, maquina, fase) => {
  const pt = PDF_T.en;
  const doc = new jsPDF();
  const hoy = new Date().toLocaleDateString('en-GB');
  const hechas = (fase?.subtareas || []).filter(s => s?.hecho).length;
  const pctFase = (fase?.subtareas || []).length > 0 ? Math.round(hechas / (fase?.subtareas || []).length * 100) : 0;

  pdfPortada(doc,
    maquina.nombre,
    fase?.nombre?.toUpperCase(),
    [
      { valor: maquina.modelo_especifico || proyecto.modelo_maquina || '—', label: pt.modelo },
      { valor: fase?.peso + '%', label: 'Weight' },
      { valor: `${hechas}/${(fase?.subtareas || []).length}`, label: 'Completed' },
      { valor: pctFase + '%', label: pt.progreso },
    ],
    pctFase,
    `${proyecto.nombre_parque} · ${maquina.nombre}`,
    pt,
    proyecto
  );

  doc.addPage();
  pdfHeader(doc, maquina.nombre, fase?.nombre?.toUpperCase() + ` · Weight ${fase?.peso}%`);

  doc.setFontSize(8);
  doc.setTextColor(...PDF.muted);
  doc.text(`${hechas}/${(fase?.subtareas || []).length} completed · ${pctFase}%`, 14, 27);
  doc.setFillColor(...PDF.border);
  doc.rect(14, 29, 182, 3, 'F');
  doc.setFillColor(...(pctFase >= 100 ? PDF.green : PDF.orange));
  doc.rect(14, 29, 182 * pctFase / 100, 3, 'F');

  autoTable(doc, {
    startY: 36,
    head: [[pt.componente, pt.estado, pt.fecha, pt.operario, pt.serial, pt.item, pt.fabricante, pt.vui, 'NOTES']],
    body: (fase?.subtareas || []).map(sub => {
      if (!sub) return ['—', '—', '—', '—', '—', '—', '—', '—', ''];
      let sSerial = sub?.serial || sub?.tornSerial || '';
      let sItem   = sub?.item   || sub?.tornItem   || '';
      let sFab    = sub?.fabricante || sub?.tornFabricante || '';
      let sVui    = sub?.vui    || sub?.camposExtra?.['VUI'] || sub?.tornBatch  || '';
      if (sub?._tipo === 'operario' && !sSerial && !sItem) {
        const previo = buscarSerialEnDescargas(maquina, sub?.nombre);
        sSerial = previo?.serial    || '';
        sItem   = previo?.item      || '';
        sFab    = previo?.fabricante|| '';
        sVui    = previo?.vui       || previo?.camposExtra?.['VUI'] || '';
      }
      let serialDisplay = sSerial;
      if (sub?._tipo === 'pala' && (sub?.numSet || sub?.set)) {
        const setLabel = [sub?.numSet, sub?.set].filter(Boolean).join('/');
        serialDisplay = [sSerial, setLabel ? `Set ${setLabel}` : ''].filter(Boolean).join(' · ');
      }
      const extraStr = Object.entries(sub?.camposExtra || {}).filter(([k,v]) => v).map(([k,v]) => `${k}: ${v}`).join(' · ');
      const notasCell = [sub?.notas, extraStr].filter(Boolean).join(' | ');
      return [
        sub?.nombre || '—',
        sub?.hecho ? pt.ok : pt.pend,
        sub?.fecha || '—',
        sub?.operario || '—',
        serialDisplay || '—',
        sItem   || '—',
        sFab    || '—',
        sVui    || '—',
        notasCell || '',
      ];
    }),
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
    headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: PDF.light },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 28 },
      1: { cellWidth: 16, halign: 'center', fontSize: 7 },
      2: { cellWidth: 16, halign: 'center', fontSize: 7 },
      3: { cellWidth: 10, halign: 'center' },
      4: { cellWidth: 32, font: 'courier', fontSize: 7 },
      5: { cellWidth: 18, font: 'courier', fontSize: 7 },
      6: { cellWidth: 22, fontSize: 7 },
      7: { cellWidth: 16, font: 'courier', fontSize: 7 },
      8: { cellWidth: 24, fontSize: 6, textColor: PDF.muted, overflow: 'linebreak' },
    },
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === 'body') {
        if (data.row.raw[1] && data.row.raw[1].includes('OK')) {
          data.cell.styles.textColor = PDF.green;
          data.cell.styles.fontStyle = 'bold';
        } else {
          data.cell.styles.textColor = PDF.dim;
        }
      }
      if (data.row.raw[1] && data.row.raw[1].includes('OK') && data.section === 'body') {
        data.cell.styles.fillColor = [240, 250, 235];
      }
    },
  });

  const faseFotos = [];
  (fase?.subtareas || []).forEach(sub => {
    if (!sub || !sub?.fotos?.length) return;
    sub.fotos.forEach(f => faseFotos.push({ src: f?.src, desc: f?.desc || '', componente: sub?.nombre || '—', fase: fase?.nombre || '—' }));
  });
  if (faseFotos.length > 0) {
    doc.addPage();
    pdfHeader(doc, maquina.nombre, pt.fotosTitulo);
    let fx = 14, fy = 28, col = 0;
    const fw = 86, fh = 58;
    faseFotos.forEach(foto => {
      if (fy + fh + 20 > 280) { doc.addPage(); pdfHeader(doc, maquina.nombre, pt.fotosTitulo); fy = 28; col = 0; fx = 14; }
      try { doc.addImage(foto.src, 'JPEG', fx, fy, fw, fh); } catch {}
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...PDF.dark);
      doc.text(foto.componente + ' · ' + foto.fase, fx, fy + fh + 4);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...PDF.muted);
      if (foto.desc) doc.text(foto.desc, fx, fy + fh + 8);
      col++;
      if (col >= 2) { col = 0; fx = 14; fy += fh + 16; } else { fx = 110; }
    });
  }

  pdfFooter(doc, `${proyecto.nombre_parque} · ${maquina.nombre} · ${fase?.nombre}`, hoy);
  doc.save(`WINDMANAGER_PRO_${proyecto.nombre_parque.replace(/\s+/g,'_')}_${maquina.nombre}_${fase?.nombre?.replace(/\s+/g,'_')}_${hoy.replace(/\//g,'')}.pdf`);
};

const generarPDFAprietes = (proyecto, maquina) => {
  const pt = PDF_T.en;
  const doc = new jsPDF();
  const hoy = new Date().toLocaleDateString('en-GB');
  const faseAprietes = maquina.tareas.find(f => f.id === 5);
  if (!faseAprietes) return;

  const hechasApr = (faseAprietes?.subtareas || []).filter(s => s?.hecho).length;
  const totalApr = (faseAprietes?.subtareas || []).length;

  pdfPortada(doc,
    maquina.nombre,
    pt.subtitle_aprietes,
    [
      { valor: maquina.modelo_especifico || proyecto.modelo_maquina || '—', label: pt.modelo },
      { valor: maquina.tramos, label: pt.tramos },
      { valor: `${hechasApr}/${totalApr}`, label: pt.aprietes },
      { valor: (faseAprietes.hecho ? 100 : Math.round(hechasApr / totalApr * 100)) + '%', label: pt.progreso },
    ],
    faseAprietes.hecho ? 100 : Math.round(hechasApr / totalApr * 100),
    `${proyecto.nombre_parque} · ${maquina.nombre}`,
    pt,
    proyecto
  );

  doc.addPage();
  pdfHeader(doc, maquina.nombre, 'TORQUE RECORD');

  autoTable(doc, {
    startY: 28,
    head: [[pt.conexion, pt.estado, pt.valor, pt.unidad, pt.bomba, pt.presion, pt.llave, pt.operario, pt.fecha]],
    body: (faseAprietes?.subtareas || []).map(sub => {
      if (!sub) return ['—', '—', '—', '—', '—', '—', '—', '—', '—'];
      return [
        sub?.nombre || '—',
        sub?.hecho ? pt.ok : pt.pend,
        sub?.valorApriete || '—',
        sub?.unidadApriete || 'Nm',
        sub?.bomTipo ? `${sub.bomTipo} · ${sub?.bomSerial || '—'}${sub?.bomGaugeSerial ? '\nGauge: ' + sub.bomGaugeSerial : ''}` : '—',
        sub?.bomBar ? sub.bomBar + ' bar' : '—',
        sub?.llaveTipo ? `${sub.llaveTipo} · ${sub?.llaveSerial || '—'}` : '—',
        sub?.operario || '—',
        sub?.fecha || '—',
      ];
    }),
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 3, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
    headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: PDF.light },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 24 },
      1: { cellWidth: 16, halign: 'center', fontSize: 7 },
      2: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 12, halign: 'center' },
      4: { cellWidth: 32, fontSize: 6, font: 'courier' },
      5: { cellWidth: 16, halign: 'center' },
      6: { cellWidth: 32, fontSize: 6, font: 'courier' },
      7: { cellWidth: 14, halign: 'center' },
      8: { cellWidth: 18, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === 'body') {
        if (data.row.raw[1].includes('OK')) {
          data.cell.styles.textColor = PDF.green;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 250, 235];
        } else {
          data.cell.styles.textColor = PDF.dim;
        }
      }
      if (data.column.index === 2 && data.section === 'body' && data.row.raw[2] !== '—') {
        data.cell.styles.textColor = PDF.orange;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  const conTornilleria = (faseAprietes?.subtareas || []).filter(s => s?.tornSerial || s?.tornItem);
  if (conTornilleria.length > 0) {
    const y = doc.lastAutoTable?.finalY || 100 + 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...PDF.dark);
    doc.text(pt.tornilleria, 14, y);
    doc.setFillColor(...PDF.orange);
    doc.rect(14, y + 2, 40, 0.8, 'F');
    autoTable(doc, {
      startY: y + 6,
      head: [[pt.conexion, pt.nItem, pt.serialNum, pt.fabricante, pt.batch]],
      body: conTornilleria.map(sub => {
        if (!sub) return ['—', '—', '—', '—', '—'];
        return [
          sub?.nombre || '—',
          sub?.tornItem || '—',
          sub?.tornSerial || '—',
          sub?.tornFabricante || '—',
          sub?.tornBatch || '—',
        ];
      }),
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
      headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 8 },
    });
  }

  pdfFooter(doc, `${proyecto.nombre_parque} · ${maquina.nombre} · ${pt.aprietes}`, hoy);
  doc.save(`WINDMANAGER_PRO_${proyecto.nombre_parque.replace(/\s+/g, '_')}_${maquina.nombre}_APRIETES_${hoy.replace(/\//g, '')}.pdf`);
};

const generarPDFComponente = (proyecto, maquina, faseNombre, sub) => {
  const pt = PDF_T.en;
  const doc = new jsPDF();
  const hoy = new Date().toLocaleDateString('en-GB');

  doc.setFillColor(...PDF.orange);
  doc.rect(0, 0, 210, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PDF.white);
  doc.text('WINDMANAGER PRO · FIELD OPERATIONS', 14, 12);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...PDF.dark);
  doc.text(sub.nombre.toUpperCase(), 14, 45);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...PDF.muted);
  doc.text(`${proyecto.nombre_parque} · ${maquina.nombre} · ${faseNombre}`, 14, 54);

  doc.setFillColor(...(sub.hecho ? PDF.green : PDF.orange));
  doc.rect(14, 59, sub.hecho ? 28 : 32, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF.white);
  doc.text(sub.hecho ? pt.completado : pt.pendiente, 17, 64.5);

  doc.setDrawColor(...PDF.border);
  doc.setLineWidth(0.3);
  doc.line(14, 72, 196, 72);

  const campos = [];
  if (sub.serial)       campos.push([pt.serialNum,          sub.serial]);
  if (sub.item)         campos.push([pt.nItem,              sub.item]);
  if (sub.fabricante)   campos.push([pt.fabricante,         sub.fabricante]);
  if (sub.vui || sub?.camposExtra?.['VUI']) campos.push([pt.vui, sub.vui || sub?.camposExtra?.['VUI']]);
  if (sub.numSet)       campos.push(['SET No.',             sub.numSet]);
  if (sub.set)          campos.push(['SET',                sub.set]);
  if (sub.tornSerial)   campos.push(['BOLT SERIAL',        sub.tornSerial]);
  if (sub.tornItem)     campos.push(['BOLT ITEM',          sub.tornItem]);
  if (sub.tornFabricante) campos.push(['BOLT MANUFACTURER', sub.tornFabricante]);
  if (sub.tornBatch)    campos.push(['BATCH',              sub.tornBatch]);
  if (sub.bomTipo)      campos.push(['PUMP',               `${sub.bomTipo} · S/N: ${sub.bomSerial || '—'}`]);
  if (sub.bomGaugeSerial) campos.push(['PRESSURE GAUGE S/N', sub.bomGaugeSerial]);
  if (sub.bomBar)       campos.push(['PUMP PRESSURE',      `${sub.bomBar} bar`]);
  if (sub.llaveTipo)    campos.push(['WRENCH',             `${sub.llaveTipo} · S/N: ${sub.llaveSerial || '—'}`]);
  if (sub.valorApriete) campos.push(['TORQUE VALUE',       `${sub.valorApriete} ${sub.unidadApriete || 'Nm'}`]);
  if (sub.operario)     campos.push(['OPERATOR',           sub.operario]);
  if (sub.fecha)        campos.push(['DATE',               sub.fecha]);
  if (sub.notas)        campos.push(['NOTES',              sub.notas]);

  let cy = 82;
  campos.forEach(([label, valor]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...PDF.muted);
    doc.text(label, 14, cy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...PDF.dark);
    doc.text(String(valor), 14, cy + 6);
    cy += 14;
  });

  Object.entries(sub.camposExtra || {}).filter(([k,v]) => v).forEach(([key, val]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...PDF.muted);
    doc.text(key.toUpperCase(), 14, cy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...PDF.dark);
    doc.text(String(val), 14, cy + 6);
    cy += 14;
  });

  if (sub.fotos?.length) {
    let fy = 80;
    sub.fotos.slice(0, 3).forEach(foto => {
      try {
        doc.addImage(foto.src, 'JPEG', 120, fy, 76, 52);
        doc.setFontSize(7);
        doc.setTextColor(...PDF.muted);
        if (foto.desc) doc.text(foto.desc, 120, fy + 55);
        fy += 62;
      } catch {}
    });
  }

  doc.setDrawColor(...PDF.orange);
  doc.setLineWidth(0.4);
  doc.line(14, 285, 196, 285);
  doc.setFontSize(7);
  doc.setTextColor(...PDF.dim);
  doc.text(`${proyecto.nombre_parque} · ${maquina.nombre}`, 14, 290);
  doc.text(hoy, 196, 290, { align: 'right' });

  doc.save(`WINDMANAGER_PRO_${maquina.nombre}_${sub.nombre.replace(/\s+/g, '_')}_${hoy.replace(/\//g, '')}.pdf`);
};

function ModalSubtarea({ sub, faseId, faseNombre, maquina, maquinaNombre, herramientas, onClose, onGuardar, proyecto, proyectoModelo, esTecnico, C }) {
    const { soloLectura } = useTech();
    const mLS = { display:'flex', alignItems:'center', fontWeight:700, fontSize:'0.68rem', color:C.textDim, marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'Barlow Condensed', sans-serif" };
    const mIS = { width:'100%', padding:'9px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' };
    const tipo = sub._tipo || tipoDeSubtarea(sub.nombre, faseId);
    const esPalaFasePosterior = tipo === TIPO.PALA && faseId !== 1;
    const serialPrevio = (tipo === TIPO.OPERARIO) ? buscarSerialEnDescargas(maquina, sub.nombre) : null;
    const datosDescargas = esPalaFasePosterior ? buscarSerialEnDescargas(maquina, sub.nombre) : null;
    const modoLectura = sub.hecho || !!(sub.serial || sub.item || sub.tornSerial || sub.fecha);
    const camposFab = getCamposFabricante(maquina?.modelo_especifico || proyectoModelo || '');

    const [hecho,       setHecho]       = useState(sub.hecho);
    const [fecha,       setFecha]       = useState(sub.fecha || '');
    const [operario,    setOperario]    = useState(sub.operario || '');
    const [notas,       setNotas]       = useState(sub.notas || '');
    const [item,        setItem]        = useState(sub.item        || '');
    const [serial,      setSerial]      = useState(sub.serial      || '');
    const [fabricante,  setFabricante]  = useState(sub.fabricante  || '');
    const [vui,         setVui]         = useState(sub.vui         || '');
    const [modelType,   setModelType]   = useState(sub.modelType   || '');
    const [numSet,      setNumSet]      = useState(sub.numSet      || '');
    const [set,         setSet]         = useState(sub.set         || '');
    const [metrica,     setMetrica]     = useState(sub.metrica     || '');
    const [tornItem,    setTornItem]    = useState(sub.tornItem    || '');
    const [tornSerial,  setTornSerial]  = useState(sub.tornSerial  || '');
    const [tornFab,     setTornFab]     = useState(sub.tornFabricante || '');
    const [tornBatch,   setTornBatch]   = useState(sub.tornBatch   || '');
    const [tornMetrica, setTornMetrica] = useState(sub.tornMetrica || '');
    const [bomSelId,    setBomSelId]    = useState(sub.bomSelId    || '');
    const [bomTipo,       setBomTipo]       = useState(sub.bomTipo       || '');
    const [bomSerial,     setBomSerial]     = useState(sub.bomSerial     || '');
    const [bomBar,        setBomBar]        = useState(sub.bomBar        || '');
    const [bomGaugeSerial,setBomGaugeSerial]= useState(sub.bomGaugeSerial|| '');
    const [llaveSelId,  setLlaveSelId]  = useState(sub.llaveSelId  || '');
    const [llaveTipo,   setLlaveTipo]   = useState(sub.llaveTipo   || '');
    const [llaveSerial, setLlaveSerial] = useState(sub.llaveSerial || '');
    const [valorApriete, setValorApriete] = useState(sub.valorApriete || '');
    const [unidadApriete, setUnidadApriete] = useState(sub.unidadApriete || 'Nm');

    const [fotos,       setFotos]       = useState(sub.fotos || []);
    const [camposExtra, setCamposExtra] = useState(sub.camposExtra || {});

    useEffect(() => {
        if (tipo !== TIPO.APRIETE) return;
        const boltData = maquina?.boltRegister?.find(
            b => b.connectionName === sub.nombre && b.registrado
        );
        if (!boltData) return;
        if (!tornItem && boltData.item)         setTornItem(boltData.item);
        if (!tornSerial && boltData.serial)     setTornSerial(boltData.serial);
        if (!tornFab && boltData.fabricante)    setTornFab(boltData.fabricante);
        if (!tornBatch && boltData.batch)       setTornBatch(boltData.batch);
    }, []); // eslint-disable-line

    const [escaneando,  setEscaneando]  = useState(false);
    const [scanPreview, setScanPreview] = useState(null);
    const [scanResult,  setScanResult]  = useState(null);    // {item,serial,fabricante,vui,numSet,set,tornItem,tornSerial,tornFabricante,tornBatch}
    const [scanError,   setScanError]   = useState('');
    const [scanMismatch, setScanMismatch] = useState(null); // {campos:[{key,label,anterior,nuevo}]}
    const [aplicados, setAplicados] = useState(new Set());

    const escanearEtiqueta = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setScanError('');
        setScanResult(null);
        setAplicados(new Set());
        setScanMismatch(null);
        setEscaneando(true);

        // Preview de la primera imagen
        const previewReader = new FileReader();
        previewReader.onload = ev => setScanPreview(ev.target.result);
        previewReader.readAsDataURL(files[0]);

        const esApriete = tipo === TIPO.APRIETE;
        const fabricanteKey = detectarFabricante(maquina?.modelo_especifico || proyectoModelo || '');
        const esVestas = fabricanteKey === 'vestas';

        const PROMPTS = {
            componente_vestas: `You are analyzing a wind turbine component label from Vestas.
Extract ONLY these fields. The label may use different names for the same field:

- "item": look for "Item", "Generic Item", "Article No", "Item No", "Material Number", "VESTAS ITEM NUMBER"
- "serial": look for "Serial", "S/N", "Manu S/N", "Manuf S/N"
  IMPORTANT: If serial has format "XXXXX - YYYYYYY - ZZZZZZ" (numbers separated by dashes/spaces), take ONLY the LAST number (ZZZZZZ). The middle number is the item number repeated.
  If serial has format "XXXXX/YY/ZZ" take the complete value as-is.
- "fabricante": look for logo brand name on the SMALL label (not the wooden crate logo).
  If two brands found, return BOTH as "fabricante" and "fabricante_2"
- "modelType": look for "Type", "Model", "Type/Model", "Designation". IGNORE if it's just a description.
- "vui": look for "VUI", "Traceability", "Traceability No", "Trace No", "Trace ID"

IGNORE COMPLETELY: dates from photo metadata, GPS location, turbine number written by hand (like "E4"),
technical specs (voltage, weight, speed, kW), addresses, websites, phone numbers,
batch fields (Batch is empty in Vestas component labels), QR/barcode values.

Return ONLY this JSON, no other text:
{"item":"","serial":"","fabricante":"","fabricante_2":"","modelType":"","vui":""}`,

            componente_otros: `You are analyzing a wind turbine component label (non-Vestas manufacturer).
Extract ONLY these fields:

- "item": look for "Item No", "Part No", "Material Number", "Article No", "Reference", "VESTAS ITEM NUMBER"
- "serial": look for "Serial", "S/N", "Serial Number", "Nº", "No."
  Take the complete value as-is including slashes or special characters.
- "fabricante": look for the BRAND NAME or MANUFACTURER on the label.
  If two manufacturer names found (e.g. brand logo + "Supplier/Manufacture" field), return BOTH as "fabricante" and "fabricante_2"
- "modelType": look for "Type", "Model", "Type/Model", "Designation".
  Example: "CM360-V", "AMV (3G)", "cgm.3-v"

IGNORE COMPLETELY: dates from photo metadata, GPS coordinates, technical specifications
(voltage, kV, Hz, kPa, weight, power), addresses, websites, phone numbers,
certification marks (CE, RoHS), descriptions of wind turbine systems.

Return ONLY this JSON, no other text:
{"item":"","serial":"","fabricante":"","fabricante_2":"","modelType":""}`,

            tornilleria_vestas: `You are analyzing a wind turbine BOLTING label from Vestas (fasteners/bolts/studs).
Extract ONLY these fields:

- "tornItem": look for "Item No", "Item", "Generic Item", "Article No", "ARTICLE NO."
- "tornSerial": look for "Manu S/N", "Manuf S/N", "Serial Number", "S/N".
  IMPORTANT: Copy the COMPLETE value exactly as it appears, including all characters, spaces, symbols and separators.
  Example: "HTF 84R = 18 & W12668" → tornSerial is "HTF 84R = 18 & W12668". Do NOT split, do NOT take only part of it.
- "tornFabricante": look for "Supplier / Manufacture", "Supplier", "Manufacturer", "Supplied by", "Made by".
  IMPORTANT: If the supplier field starts with a numeric code (e.g. "509946- Hiten Fasteners Pvt Ltd."), strip the numeric code and return ONLY the company name (e.g. "Hiten Fasteners Pvt Ltd.").
  The Vestas logo on the crate is the CUSTOMER, not the manufacturer. Look for the actual supplier name.
- "tornBatch": look for "Batch", "Batch No", "ID Mark", "ID No", "Heat No", "Heat Number", "Lot No", "Lot Number"
- "metrica": look for bolt size pattern like M64x525, M36x200, M20x80. Format is MXXxYYY where XX is diameter and YYY is length.
  Search in "COMPONENT" field, "Description" field, part name, or anywhere on the label.
  Example: "FL.B TOP M30x270 ISO4014 RAHT" → metrica is "M30x270". Example: "FL.STUD M64X525 (105)" → metrica is "M64x525".

IGNORE COMPLETELY: Customer Order Number, Gross Weight, Packing Date,
Container number, dates from photo metadata, GPS location,
technical standards (ISO, TPS, AND references), Wood Heat Treated info.

Return ONLY this JSON, no other text:
{"tornItem":"","tornSerial":"","tornFabricante":"","tornBatch":"","metrica":""}`,

            tornilleria_otros: `You are analyzing a wind turbine BOLTING label (fasteners/bolts/studs).
Extract ONLY these fields:

- "tornItem": look for "Item No", "Part No", "Article No", "Reference"
- "tornSerial": look for "Serial", "S/N", "Serial Number". Take complete value as-is.
- "tornFabricante": look for "Manufacturer", "Made by", "Supplier", brand logo name
- "tornBatch": look for "Batch", "Batch No", "ID Mark", "ID No", "Heat No", "Lot No"
- "metrica": look for bolt size pattern MXXxYYY (diameter x length).
  Example: M64x525, M36x200. Found in component description or part name.

IGNORE COMPLETELY: dates, GPS, addresses, technical standards, order numbers, weights.

Return ONLY this JSON, no other text:
{"tornItem":"","tornSerial":"","tornFabricante":"","tornBatch":"","metrica":""}`,
        };

        const seleccionarPrompt = () => {
            if (esApriete) return esVestas ? PROMPTS.tornilleria_vestas : PROMPTS.tornilleria_otros;
            return esVestas ? PROMPTS.componente_vestas : PROMPTS.componente_otros;
        };
        const prompt = seleccionarPrompt();

        const toStr = (v) => {
            if (v === null || v === undefined) return '';
            if (typeof v === 'object') return Object.values(v).filter(x => x && typeof x === 'string').join(' ') || JSON.stringify(v);
            return String(v);
        };

        const normalizarResultado = (parsed) => {
            const r = { ...parsed };
            Object.keys(r).forEach(k => {
                if (!r[k] || r[k] === 'N/A' || r[k] === 'n/a' || r[k] === '-' || r[k] === null) r[k] = '';
                if (typeof r[k] === 'object' && r[k] !== null) r[k] = toStr(r[k]);
                if (typeof r[k] === 'string') r[k] = r[k].trim();
            });
            if (!r.vui && r._extra?.traceability) { r.vui = r._extra.traceability; delete r._extra?.traceability; }
            if (!r.vui && r._extra?.['traceability no']) { r.vui = r._extra['traceability no']; delete r._extra?.['traceability no']; }
            if (r.metrica) {
                const match = r.metrica.toUpperCase().replace(/\s+/g, '').match(/M\d+[xX]\d+/i);
                if (match) r.metrica = match[0].replace('X', 'x');
            }
            if (r._extra && typeof r._extra === 'object') {
                r._extra = Object.fromEntries(
                    Object.entries(r._extra)
                        .map(([k, v]) => [k, typeof v === 'object' && v !== null ? toStr(v) : String(v ?? '')])
                        .filter(([, v]) => v && v.trim())
                );
            }
            return r;
        };

        try {
            let mergedResult = {};

            for (const file of files) {
                const { base64, dataUrl: fotoDataUrl, blob } = await comprimirImagen(file);
                const fotoId = Date.now() + Math.random();
                setFotos(prev => [...prev, { id: fotoId, src: fotoDataUrl, desc: 'Label scan', esEtiqueta: true }]);
                subirFotoSupabase(blob, maquina.id).then(url => {
                    setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, src: url } : f));
                }).catch(() => {});
                const response = await fetch('/api/scan', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        model: 'claude-haiku-4-5-20251001',
                        max_tokens: 500,
                        messages: [{ role: 'user', content: [
                            { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: base64 } },
                            { type: 'text', text: prompt }
                        ]}]
                    })
                });
                if (!response.ok) {
                    const errData = await response.json().catch(()=>({}));
                    throw new Error(errData?.error?.message || 'Error ' + response.status);
                }
                const data = await response.json();
                const text = data.content?.[0]?.text || '';
                const clean = text.replace(/```json|```/g, '').trim();
                let parsed;
                try { parsed = JSON.parse(clean); } catch { parsed = {}; }
                const normalizado = normalizarResultado(parsed);
                // Merge: primera imagen gana en cada campo; _extra se acumula
                Object.entries(normalizado).forEach(([k, v]) => {
                    if (k === '_extra') {
                        if (v && typeof v === 'object') {
                            Object.entries(v).forEach(([ek, ev]) => {
                                if (ev && !mergedResult._extra?.[ek]) {
                                    mergedResult._extra = { ...(mergedResult._extra || {}), [ek]: ev };
                                }
                            });
                        }
                    } else if (v && !mergedResult[k]) {
                        mergedResult[k] = v;
                    }
                });
            }

            const tieneData = Object.entries(mergedResult).some(([k, v]) => k !== '_extra' && v) ||
                              Object.keys(mergedResult._extra || {}).length > 0;
            if (!tieneData) {
                setScanError('No valid label recognised. Please fill in the fields manually.');
            } else {
                // Detectar mismatch con datos ya registrados en el formulario
                const mismatchCampos = [];
                if (!esApriete) {
                    if (mergedResult.item   && item   && mergedResult.item   !== item)
                        mismatchCampos.push({ key:'item',   label:'Item No.', anterior: item,   nuevo: mergedResult.item });
                    if (mergedResult.serial && serial && mergedResult.serial !== serial)
                        mismatchCampos.push({ key:'serial', label:'Serial',   anterior: serial, nuevo: mergedResult.serial });
                } else {
                    if (mergedResult.tornItem   && tornItem   && mergedResult.tornItem   !== tornItem)
                        mismatchCampos.push({ key:'tornItem',   label:'Item No. (bolt)', anterior: tornItem,   nuevo: mergedResult.tornItem });
                    if (mergedResult.tornSerial && tornSerial && mergedResult.tornSerial !== tornSerial)
                        mismatchCampos.push({ key:'tornSerial', label:'Serial (bolt)',   anterior: tornSerial, nuevo: mergedResult.tornSerial });
                }
                setScanMismatch(mismatchCampos.length > 0 ? { campos: mismatchCampos } : null);
                setScanResult(mergedResult);
            }
        } catch (err) {
            setScanError('Could not read label. Please fill in the fields manually.');
            console.error('Scan error:', err);
        } finally {
            setEscaneando(false);
            e.target.value = '';
        }
    };

    // Campo a campo: aceptar un campo individual del scan
    const aceptarCampo = (key) => {
        if (!scanResult) return;
        if (key.startsWith('_extra_')) {
            const realKey = key.slice(7);
            const val = scanResult._extra?.[realKey];
            if (!val) return;
            setCamposExtra(prev => ({ ...prev, [realKey]: val }));
            setAplicados(prev => new Set([...prev, key]));
            return;
        }
        if (key === '_extra') return;
        // fabricante_2 — al aceptar, sustituye al fabricante principal y deselecciona la opción 1
        if (key === 'fabricante_2') {
            const val2 = scanResult['fabricante_2'];
            if (!val2) return;
            setFabricante(val2);
            setAplicados(prev => { const n = new Set(prev); n.delete('fabricante'); n.add('fabricante_2'); return n; });
            return;
        }
        const val = scanResult[key];
        if (!val) return;

        const mismatchValores = { item, serial, tornItem, tornSerial };
        const mismatchLabels  = { item: 'Item No.', serial: 'Serial', tornItem: 'Item No. (bolt)', tornSerial: 'Serial (bolt)' };
        if (key in mismatchValores && mismatchValores[key] && mismatchValores[key] !== val) {
            const confirmar = window.confirm(
                `⚠️ MISMATCHED DATA\n\n${mismatchLabels[key]}:\n  Previously registered: "${mismatchValores[key]}"\n  New scan: "${val}"\n\nOverwrite with new value?`
            );
            if (!confirmar) return;
        }

        const settersFijos = {
            item: setItem, serial: setSerial, fabricante: setFabricante,
            modelType: setModelType,
            vui: setVui, numSet: setNumSet, set: setSet,
            metrica: setMetrica,
            tornItem: setTornItem, tornSerial: setTornSerial,
            tornFabricante: setTornFab, tornBatch: setTornBatch,
            tornMetrica: setTornMetrica,
        };
        if (settersFijos[key]) {
            settersFijos[key](val);
        } else {
            setCamposExtra(prev => ({ ...prev, [key]: val }));
        }
        setAplicados(prev => {
            const n = new Set([...prev, key]);
            if (key === 'fabricante' && scanResult.fabricante_2) n.delete('fabricante_2');
            return n;
        });
    };

    const rechazarCampo = (key) => {
        if (key.startsWith('_extra_')) {
            const realKey = key.slice(7);
            setCamposExtra(prev => ({ ...prev, [realKey]: '' }));
            setAplicados(prev => { const n = new Set(prev); n.delete(key); return n; });
            return;
        }
        const clearers = {
            item: () => setItem(''), serial: () => setSerial(''),
            fabricante: () => setFabricante(''), fabricante_2: () => setFabricante(''),
            modelType: () => setModelType(''), vui: () => setVui(''),
            numSet: () => setNumSet(''), set: () => setSet(''),
            metrica: () => setMetrica(''), tornItem: () => setTornItem(''),
            tornSerial: () => setTornSerial(''), tornFabricante: () => setTornFab(''),
            tornBatch: () => setTornBatch(''), tornMetrica: () => setTornMetrica(''),
        };
        if (clearers[key]) clearers[key]();
        setAplicados(prev => {
            const n = new Set(prev);
            n.delete(key);
            if (key === 'fabricante_2') n.delete('fabricante');
            return n;
        });
    };

    const aplicarScanResult = () => {
        if (!scanResult) return;

        let forzarSobreescribir = false;
        if (scanMismatch && scanMismatch.campos.length > 0) {
            const detalles = scanMismatch.campos.map(c =>
                `• ${c.label}:\n    Registered: "${c.anterior}"\n    New scan:   "${c.nuevo}"`
            ).join('\n\n');
            const confirmar = window.confirm(
                `⚠️ MISMATCHED DATA DETECTED\n\nThe scanned label has different data than what was previously registered:\n\n${detalles}\n\nDo you want to overwrite with the new scan data?`
            );
            if (!confirmar) return;
            forzarSobreescribir = true;
        }

        if ((forzarSobreescribir || !item)       && scanResult.item)           setItem(scanResult.item);
        if ((forzarSobreescribir || !serial)     && scanResult.serial)         setSerial(scanResult.serial);
        if ((forzarSobreescribir || !fabricante) && scanResult.fabricante)     setFabricante(scanResult.fabricante);
        if ((forzarSobreescribir || !modelType)  && scanResult.modelType)      setModelType(scanResult.modelType);
        if ((forzarSobreescribir || !vui)        && scanResult.vui)            setVui(scanResult.vui);
        if ((forzarSobreescribir || !numSet)     && scanResult.numSet)         setNumSet(scanResult.numSet);
        if ((forzarSobreescribir || !set)        && scanResult.set)            setSet(scanResult.set);
        if ((forzarSobreescribir || !metrica)    && scanResult.metrica)        setMetrica(scanResult.metrica);
        if ((forzarSobreescribir || !tornItem)   && scanResult.tornItem)       setTornItem(scanResult.tornItem);
        if ((forzarSobreescribir || !tornSerial) && scanResult.tornSerial)     setTornSerial(scanResult.tornSerial);
        if ((forzarSobreescribir || !tornFab)    && scanResult.tornFabricante) setTornFab(scanResult.tornFabricante);
        if ((forzarSobreescribir || !tornBatch)  && scanResult.tornBatch)      setTornBatch(scanResult.tornBatch);
        if ((forzarSobreescribir || !tornMetrica)&& scanResult.tornMetrica)    setTornMetrica(scanResult.tornMetrica);
        if (scanResult._extra) {
            Object.entries(scanResult._extra).forEach(([k, v]) => {
                if (v && (forzarSobreescribir || !camposExtra[k])) setCamposExtra(prev => ({ ...prev, [k]: v }));
            });
        }
        setScanResult(null);
        setScanPreview(null);
        setScanMismatch(null);
    };

    const handleGuardar = () => {
        const fechaFinal = fecha || formatearFecha(hoyISO());
        if (!fecha) setFecha(fechaFinal);

        if (tipo === TIPO.APRIETE && (!bomSelId || !llaveSelId)) {
            const continuar = window.confirm(
                (!bomSelId && !llaveSelId)
                    ? 'No torque pump or wrench selected.\nContinue without tools?'
                    : !bomSelId
                    ? 'No torque pump selected.\nContinue?'
                    : 'No torque wrench selected.\nContinue?'
            );
            if (!continuar) return;
        }

        const requiereValidacionCampos = !(
            tipo === TIPO.OPERARIO ||
            (tipo === TIPO.PALA && faseId !== 1) ||
            (serialPrevio && (serialPrevio.serial || serialPrevio.item))
        );
        const faltantes = [];
        if (!operario) faltantes.push('Operator');
        if (!fechaFinal) faltantes.push('Date');
        if (requiereValidacionCampos && (tipo === TIPO.COMPONENTE || tipo === TIPO.PALA) && !item) faltantes.push('Item No.');
        if (requiereValidacionCampos && (tipo === TIPO.COMPONENTE || tipo === TIPO.PALA) && !serial) faltantes.push('Serial Number');
        if (faltantes.length > 0) {
            const continuar = window.confirm(`Missing data: ${faltantes.join(', ')}.\nDo you want to continue anyway?`);
            if (!continuar) return;
        }

        const datos = {
            hecho: true, fecha: fechaFinal, operario, notas,
            ...(tipo===TIPO.COMPONENTE && { item, serial, fabricante, modelType, vui, camposExtra, fotos }),
            ...(tipo===TIPO.PALA       && { item, serial, fabricante, modelType, vui, numSet, set, camposExtra, fotos }),
            ...(tipo===TIPO.APRIETE    && { metrica, tornItem, tornSerial, tornFabricante: tornFab, tornBatch, tornMetrica, bomSelId, bomTipo, bomSerial, bomBar, bomGaugeSerial, llaveSelId, llaveTipo, llaveSerial, valorApriete, unidadApriete, fotos }),
            ...(tipo===TIPO.OPERARIO   && { fotos }),
            ...(tipo===TIPO.LIBRE      && { fotos }),
            _tipo: tipo,
        };
        onGuardar(datos);
        onClose();
    };

    const handleEliminarRegistro = () => {
        const confirmar = window.confirm(
            `Delete record for "${sub.nombre}"?\n\nAll recorded data will be erased and the task will be set back to pending.`
        );
        if (!confirmar) return;
        const datosVaciosReset = {
            hecho: false,
            locked: false,
            fecha: '', operario: '', notas: '',
            item: '', serial: '', fabricante: '', modelType: '', vui: '',
            numSet: '', set: '', camposExtra: {}, fotos: [],
            metrica: '', tornItem: '', tornSerial: '', tornFabricante: '', tornBatch: '',
            bomSelId: '', bomTipo: '', bomSerial: '', bomBar: '', bomGaugeSerial: '',
            llaveSelId: '', llaveTipo: '', llaveSerial: '',
            valorApriete: '', unidadApriete: 'Nm',
            _tipo: sub._tipo,
        };
        onGuardar(datosVaciosReset);
        onClose();
    };

    return (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:C.overlay, backdropFilter:'blur(3px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
            <div style={{ background:C.bgCard, border:`1px solid ${C.borderMid}`, width:'100%', maxWidth:'600px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 64px rgba(0,0,0,0.6)', overflow:'hidden', animation:'slideUp 0.2s ease', borderRadius:'12px' }}>

                <div style={{ background:C.bgDark, padding:'20px 24px', flexShrink:0, borderBottom:`3px solid ${hecho?C.green:C.orange}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                            <div style={{ fontSize:'10px', color:C.textDim, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px', fontFamily:"'Barlow Condensed', sans-serif" }}>
                                {maquinaNombre} · {faseNombre}
                            </div>
                            <h3 style={{ margin:0, color:C.text, fontSize:'1.4rem', fontWeight:900, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em' }}>{sub.nombre}</h3>
                            <div style={{ marginTop:'8px', display:'flex', gap:'8px', alignItems:'center' }}>
                                <span style={{ fontSize:'11px', fontWeight:700, background:'rgba(255,255,255,0.08)', color: hecho?C.green:C.orange, padding:'3px 10px', border:`1px solid ${hecho?C.green:C.orange}`, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em' }}>
                                    {hecho ? '✅ Completed' : '⏳ Pending'}
                                </span>
                                <span style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>
                                    {tipo === TIPO.COMPONENTE && '📦 Component'}
                                    {tipo === TIPO.PALA       && '🔰 Blade'}
                                    {tipo === TIPO.APRIETE    && '🔧 Torque'}
                                    {tipo === TIPO.OPERARIO   && '👤 Operator'}
                                    {tipo === TIPO.LIBRE      && '📋 General'}
                                </span>
                            </div>
                        </div>
                        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'7px', display:'flex', borderRadius:'8px' }}><X size={18}/></button>
                    </div>
                </div>

                <div style={{ overflowY:'auto', flex:1, padding:'20px 24px', background:C.bgCard }}>

                    {modoLectura && (
                        <div style={{ background:C.bgDark, border:`1px solid ${C.borderMid}`, padding:'10px 14px', marginBottom:'16px', fontSize:'11px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', display:'flex', alignItems:'center', gap:'8px' }}>
                            🔒 RECORD COMPLETED — Press "Delete record" to modify the data
                        </div>
                    )}

                    <SectionTitle icon={<User size={13}/>} title={'Execution record'} C={C} />
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
                        <Campo label={'Operators (initials)'} icon={<User/>} value={operario} onChange={setOperario} placeholder={'e.g. BLL, JMR, AL'} upper maxLen={30} disabled={modoLectura} C={C} />
                        <div>
                            <label style={mLS}><Calendar size={11} style={{marginRight:4}}/>{'Completion date'}</label>
                            <input type="date" value={fechaParaInput(fecha)} onChange={e=>setFecha(formatearFecha(e.target.value))} disabled={modoLectura} style={{ ...mIS, colorScheme: C.bg === '#1a1c1e' ? 'dark' : 'light', ...(modoLectura && { opacity:0.6, cursor:'not-allowed' }) }}/>
                        </div>
                    </div>

                    {tipo === TIPO.COMPONENTE && (
                        <>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                                <SectionTitle icon={<Package size={13}/>} title={'Component data'} C={C} />
                                <EscanearBtn onScan={escanearEtiqueta} escaneando={escaneando} C={C}/>
                            </div>
                            <ScanPanel preview={scanPreview} result={scanResult} error={scanError} escaneando={escaneando} tipo={tipo} onAceptar={aceptarCampo} onAplicarTodos={aplicarScanResult} onDescartar={()=>{setScanResult(null);setScanPreview(null);setScanError('');setScanMismatch(null);setAplicados(new Set());}} mismatch={scanMismatch} aplicados={aplicados} onRechazar={rechazarCampo} C={C}/>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                                <Campo label={'Item No.'}      icon={<Hash/>}    value={item}       onChange={setItem}       placeholder="Ej: 10023456" mono disabled={modoLectura} C={C} />
                                <Campo label={'Serial Number'}    icon={<Hash/>}    value={serial}     onChange={setSerial}     placeholder="Ej: SN-2024-00847" mono disabled={modoLectura} C={C} />
                                <Campo label={'Manufacturer'} icon={<Package/>} value={fabricante} onChange={setFabricante} placeholder="Ej: Vestas" disabled={modoLectura} C={C} />
                                <Campo label={'Model / Type'}   icon={<Package/>} value={modelType}  onChange={setModelType}  placeholder="e.g. CM360-V" disabled={modoLectura} C={C} />
                            </div>
                            {camposFab.length > 0 && (
                                <>
                                    <div style={{
                                        fontSize:'9px', color: C.orange, fontWeight:700,
                                        textTransform:'uppercase', letterSpacing:'0.1em',
                                        marginTop:'8px', marginBottom:'8px',
                                        display:'flex', alignItems:'center', gap:'6px',
                                    }}>
                                        <span style={{ flex:1, height:'1px', background: C.border }}/>
                                        {detectarFabricante(maquina?.modelo_especifico || proyectoModelo || '')?.toUpperCase()} SPECIFIC FIELDS
                                        <span style={{ flex:1, height:'1px', background: C.border }}/>
                                    </div>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                                        {camposFab.map(campo => (
                                            <div key={campo}>
                                                <label style={{
                                                    display:'block', fontSize:'10px', fontWeight:700,
                                                    color: C.orange, textTransform:'uppercase',
                                                    letterSpacing:'0.06em', marginBottom:'4px',
                                                }}># {campo}</label>
                                                <input
                                                    type="text"
                                                    value={campo === 'VUI' ? vui : (camposExtra[campo] || '')}
                                                    onChange={e => campo === 'VUI' ? setVui(e.target.value) : setCamposExtra(prev => ({ ...prev, [campo]: e.target.value }))}
                                                    readOnly={modoLectura}
                                                    placeholder={`e.g. ${campo}...`}
                                                    style={{
                                                        width:'100%', padding:'8px 10px', boxSizing:'border-box',
                                                        background: modoLectura ? C.bgDark : C.bgCard,
                                                        border:`1px solid ${C.borderMid}`,
                                                        color: C.text, fontSize:'13px',
                                                        fontFamily:'monospace',
                                                        outline:'none',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            <div style={{ marginBottom:'8px' }}/>
                        </>
                    )}

                    {esPalaFasePosterior && datosDescargas && (datosDescargas.serial || datosDescargas.item) && (
                        <>
                            <SectionTitle icon={<Package size={13}/>} title={'Blade data (recorded in Unloading)'} color={C.textDim} C={C} />
                            <div style={{ background:C.bgDark, border:`1px solid ${C.border}`, padding:'12px 16px', marginBottom:'20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                                {datosDescargas.item      && <ReadField label={'Item No.'}       value={datosDescargas.item} C={C} />}
                                {datosDescargas.serial    && <ReadField label={'Serial Number'}   value={datosDescargas.serial} mono C={C} />}
                                {datosDescargas.fabricante&& <ReadField label={'Manufacturer'}    value={datosDescargas.fabricante} C={C} />}
                                {datosDescargas.modelType && <ReadField label={'Model / Type'}    value={datosDescargas.modelType} C={C} />}
                                {(datosDescargas.vui || datosDescargas.camposExtra?.['VUI']) && (
                                    <ReadField label={'VUI'} value={datosDescargas.vui || datosDescargas.camposExtra?.['VUI']} mono C={C} />
                                )}
                                {datosDescargas.numSet    && <ReadField label={'Set No.'}         value={datosDescargas.numSet} mono C={C} />}
                                {datosDescargas.set       && <ReadField label={'Set'}             value={datosDescargas.set} C={C} />}
                            </div>
                        </>
                    )}

                    {tipo === TIPO.PALA && !esPalaFasePosterior && (
                        <>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                                <SectionTitle icon={<Package size={13}/>} title={'Blade data'} C={C} />
                                <EscanearBtn onScan={escanearEtiqueta} escaneando={escaneando} C={C}/>
                            </div>
                            <ScanPanel preview={scanPreview} result={scanResult} error={scanError} escaneando={escaneando} tipo={tipo} onAceptar={aceptarCampo} onAplicarTodos={aplicarScanResult} onDescartar={()=>{setScanResult(null);setScanPreview(null);setScanError('');setScanMismatch(null);setAplicados(new Set());}} mismatch={scanMismatch} aplicados={aplicados} onRechazar={rechazarCampo} C={C}/>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                                <Campo label={'Item No.'}       icon={<Hash/>}    value={item}       onChange={setItem}       placeholder="Ej: 10023456" mono disabled={modoLectura} C={C} />
                                <Campo label={'Serial Number'}     icon={<Hash/>}    value={serial}     onChange={setSerial}     placeholder="Ej: SN-2024-00847" mono disabled={modoLectura} C={C} />
                                <Campo label={'Manufacturer'} icon={<Package/>} value={fabricante} onChange={setFabricante} placeholder="Ej: LM Wind Power" disabled={modoLectura} C={C} />
                                <Campo label={'Model / Type'}   icon={<Package/>} value={modelType}  onChange={setModelType}  placeholder="e.g. B63.3" disabled={modoLectura} C={C} />
                                <Campo label={'Set No.'}        icon={<Hash/>}    value={numSet}     onChange={setNumSet}     placeholder="Ej: SET-2024-007" mono disabled={modoLectura} C={C} />
                                <Campo label={'Set'}     icon={<Package/>} value={set}        onChange={setSet}        placeholder="Ej: SET-A" disabled={modoLectura} C={C} />
                            </div>
                            {camposFab.length > 0 && (
                                <>
                                    <div style={{
                                        fontSize:'9px', color: C.orange, fontWeight:700,
                                        textTransform:'uppercase', letterSpacing:'0.1em',
                                        marginTop:'8px', marginBottom:'8px',
                                        display:'flex', alignItems:'center', gap:'6px',
                                    }}>
                                        <span style={{ flex:1, height:'1px', background: C.border }}/>
                                        {detectarFabricante(maquina?.modelo_especifico || proyectoModelo || '')?.toUpperCase()} SPECIFIC FIELDS
                                        <span style={{ flex:1, height:'1px', background: C.border }}/>
                                    </div>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                                        {camposFab.map(campo => (
                                            <div key={campo}>
                                                <label style={{
                                                    display:'block', fontSize:'10px', fontWeight:700,
                                                    color: C.orange, textTransform:'uppercase',
                                                    letterSpacing:'0.06em', marginBottom:'4px',
                                                }}># {campo}</label>
                                                <input
                                                    type="text"
                                                    value={campo === 'VUI' ? vui : (camposExtra[campo] || '')}
                                                    onChange={e => campo === 'VUI' ? setVui(e.target.value) : setCamposExtra(prev => ({ ...prev, [campo]: e.target.value }))}
                                                    readOnly={modoLectura}
                                                    placeholder={`e.g. ${campo}...`}
                                                    style={{
                                                        width:'100%', padding:'8px 10px', boxSizing:'border-box',
                                                        background: modoLectura ? C.bgDark : C.bgCard,
                                                        border:`1px solid ${C.borderMid}`,
                                                        color: C.text, fontSize:'13px',
                                                        fontFamily:'monospace',
                                                        outline:'none',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            <div style={{ marginBottom:'8px' }}/>
                        </>
                    )}

                    {/* ── OPERARIO: serial ya registrado, mostrar en lectura ── */}
                    {tipo === TIPO.OPERARIO && serialPrevio && (serialPrevio.serial || serialPrevio.item) && (
                        <>
                            <SectionTitle icon={<Package size={13}/>} title={'Component data (recorded in Unloading)'} color={C.textDim} C={C} />
                            <div style={{ background:C.bgDark, border:`1px solid ${C.border}`, padding:'12px 16px', marginBottom:'20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                                {serialPrevio.item      && <ReadField label={'Item No.'}       value={serialPrevio.item} C={C} />}
                                {serialPrevio.serial    && <ReadField label={'Serial Number'}     value={serialPrevio.serial} mono C={C} />}
                                {serialPrevio.fabricante&& <ReadField label={'Manufacturer'} value={serialPrevio.fabricante} C={C} />}
                                {(serialPrevio.vui || serialPrevio.camposExtra?.['VUI']) && <ReadField label={'VUI'} value={serialPrevio.vui || serialPrevio.camposExtra?.['VUI']} mono C={C} />}
                                {serialPrevio.numSet    && <ReadField label={'Set No.'}        value={serialPrevio.numSet} mono C={C} />}
                                {serialPrevio.set       && <ReadField label={'Set'}     value={serialPrevio.set} C={C} />}
                            </div>
                        </>
                    )}

                    {/* ── APRIETE ── */}
                    {tipo === TIPO.APRIETE && (
                        <>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                                <SectionTitle icon={<Package size={13}/>} title={'Bolts'} C={C} />
                                <EscanearBtn onScan={escanearEtiqueta} escaneando={escaneando} C={C}/>
                            </div>
                            <ScanPanel preview={scanPreview} result={scanResult} error={scanError} escaneando={escaneando} tipo={tipo} onAceptar={aceptarCampo} onAplicarTodos={aplicarScanResult} onDescartar={()=>{setScanResult(null);setScanPreview(null);setScanError('');setScanMismatch(null);setAplicados(new Set());}} mismatch={scanMismatch} aplicados={aplicados} onRechazar={rechazarCampo} C={C}/>
                            <Campo label={'Bolt Size (metric)'} icon={<Hash/>} value={metrica} onChange={setMetrica} placeholder="e.g. M64x525" mono disabled={modoLectura} C={C} />
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px', marginTop:'12px' }}>
                                <Campo label={'Item No.'}       icon={<Hash/>}    value={tornItem}   onChange={setTornItem}   placeholder="Ej: 10023456" mono disabled={modoLectura} C={C} />
                                <Campo label={'Serial'}  icon={<Hash/>}    value={tornSerial} onChange={setTornSerial} placeholder="Ej: SN-BOLT-001" mono disabled={modoLectura} C={C} />
                                <Campo label={'Manufacturer'} icon={<Package/>} value={tornFab}    onChange={setTornFab}    placeholder="Ej: Nord-Lock" disabled={modoLectura} C={C} />
                                <Campo label={'Batch'}   icon={<Hash/>}    value={tornBatch}  onChange={setTornBatch}  placeholder="Ej: BATCH-2024-33" mono disabled={modoLectura} C={C} />
                                <div style={{ gridColumn:'1 / -1' }}>
                                    <label style={mLS}><Hash size={11} style={{marginRight:4}}/>{'Bolt metric'}</label>
                                    <input list="metricas-list" value={tornMetrica} onChange={e=>setTornMetrica(e.target.value)}
                                        placeholder={'E.g: M24'}
                                        disabled={modoLectura}
                                        style={{ ...mIS, fontFamily:'monospace', fontWeight: tornMetrica?700:'normal', ...(modoLectura && { opacity:0.6, cursor:'not-allowed' }) }}/>
                                    <datalist id="metricas-list">
                                        {['M12','M16','M20','M24','M27','M30','M36','M42','M48','M52','M56','M64'].map(m=>(
                                            <option key={m} value={m}/>
                                        ))}
                                    </datalist>
                                </div>
                            </div>

                            <SectionTitle icon={<Zap size={13}/>} title={'Torque pump'} C={C} />
                            <HerramientaSelector
                                catalogo={herramientas?.bombas||[]}
                                tipo="Bomba"
                                selectedId={bomSelId}
                                onSelect={item => {
                                    if (!item) { setBomSelId(''); setBomTipo(''); setBomSerial(''); setBomGaugeSerial(''); return; }
                                    setBomSelId(String(item.id)); setBomTipo(item.modelo); setBomSerial(item.serial); setBomGaugeSerial(item.gaugeSerial||'');
                                }}
                                C={C}
                            />
                            {/* Bares de la bomba */}
                            {bomSelId && (
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                                    <div>
                                        <label style={mLS}><Zap size={11} style={{marginRight:4}}/>{'Applied pressure (bar)'}</label>
                                        <input type="number" value={bomBar} onChange={e=>setBomBar(e.target.value)}
                                            placeholder="Ej: 680"
                                            disabled={modoLectura}
                                            style={{ ...mIS, fontFamily:'monospace', fontWeight:700, ...(modoLectura && { opacity:0.6, cursor:'not-allowed' }) }}/>
                                    </div>
                                    <div style={{ background:C.bgDark, padding:'8px 12px', border:`1px solid ${C.border}`, marginBottom:'16px' }}>
                                        <div style={{ fontSize:'10px', color:C.textDim, fontWeight:700, textTransform:'uppercase', marginBottom:'3px', fontFamily:"'Barlow Condensed', sans-serif" }}>{'Selected'}</div>
                                        <div style={{ fontSize:'0.82rem', color:C.text, fontWeight:600 }}>{bomTipo}</div>
                                        <div style={{ fontSize:'11px', color:C.textDim, fontFamily:'monospace' }}>{bomSerial}</div>
                                    </div>
                                    <div>
                                        <label style={mLS}><Hash size={11} style={{marginRight:4}}/>{'Pressure gauge S/N'}</label>
                                        <input type="text" value={bomGaugeSerial} onChange={e=>setBomGaugeSerial(e.target.value)}
                                            placeholder="Ej: G-2024-00123"
                                            disabled={modoLectura}
                                            style={{ ...mIS, fontFamily:'monospace', ...(modoLectura && { opacity:0.6, cursor:'not-allowed' }) }}/>
                                    </div>
                                </div>
                            )}

                            <SectionTitle icon={<Wrench size={13}/>} title={'Torque wrench'} C={C} />
                            <LlaveSelector
                                herramientas={herramientas}
                                llaveSelId={llaveSelId}
                                onLlaveSelect={item => {
                                    if (!item) { setLlaveSelId(''); setLlaveTipo(''); setLlaveSerial(''); return; }
                                    setLlaveSelId(String(item.id)); setLlaveTipo(item.modelo); setLlaveSerial(item.serial);
                                }}
                                C={C}
                            />
                            {llaveSelId && (
                                <div style={{ background:C.bgDark, padding:'8px 12px', border:`1px solid ${C.border}`, marginBottom:'16px' }}>
                                    <div style={{ fontSize:'10px', color:C.textDim, fontWeight:700, textTransform:'uppercase', marginBottom:'3px', fontFamily:"'Barlow Condensed', sans-serif" }}>{'Selected'}</div>
                                    <div style={{ fontSize:'0.82rem', color:C.text, fontWeight:600 }}>{llaveTipo}</div>
                                    <div style={{ fontSize:'11px', color:C.textDim, fontFamily:'monospace' }}>{llaveSerial}</div>
                                </div>
                            )}

                            <SectionTitle icon={<Zap size={13}/>} title={'Applied torque value'} C={C} />
                            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px', marginBottom:'20px' }}>
                                <div>
                                    <label style={mLS}><Zap size={11} style={{marginRight:4}}/>{'Value'}</label>
                                    <input type="number" value={valorApriete} onChange={e=>setValorApriete(e.target.value)}
                                        placeholder="Ej: 2850" disabled={modoLectura} style={{ ...mIS, fontFamily:'monospace', fontWeight:700, fontSize:'1rem', ...(modoLectura && { opacity:0.6, cursor:'not-allowed' }) }}/>
                                </div>
                                <div>
                                    <label style={mLS}>{'Unit'}</label>
                                    <select value={unidadApriete} onChange={e=>setUnidadApriete(e.target.value)}
                                        disabled={modoLectura}
                                        style={{ ...mIS, cursor: modoLectura?'not-allowed':'pointer', ...(modoLectura && { opacity:0.6 }) }}>
                                        <option value="Nm">Nm</option>
                                        <option value="bar">bar</option>
                                        <option value="kN">kN</option>
                                        <option value="ft·lb">ft·lb</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── Notas (siempre) ── */}
                    <SectionTitle icon={<FileText size={13}/>} title={'Notes'} C={C} />
                    <textarea value={notas} onChange={e=>setNotas(e.target.value)} placeholder={'Incidents, special conditions, applied torques...'} rows={3}
                        disabled={modoLectura}
                        style={{ ...mIS, resize:'vertical', lineHeight:'1.5', width:'100%', boxSizing:'border-box', marginBottom:'20px', ...(modoLectura && { opacity:0.6, cursor:'not-allowed' }) }}
                    />

                    {/* ── Fotos ── */}
                    {(tipo === TIPO.COMPONENTE || tipo === TIPO.PALA || tipo === TIPO.OPERARIO || tipo === TIPO.LIBRE) && (
                        <>
                            <SectionTitle icon={<Camera size={13}/>} title={`Photos (${fotos.length})`} C={C} />

                            <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', padding:'10px', border:`2px dashed ${C.borderMid}`, cursor:'pointer', background:C.bgDark, marginBottom:'14px', color:C.textDim, fontWeight:600, fontSize:'0.82rem', transition:'all 0.2s' }}>
                                <ImagePlus size={16} color={C.orange}/>
                                <span>{'Add photo'}</span>
                                <input type="file" accept="image/*" multiple style={{ display:'none' }}
                                    onChange={async e => {
                                        const files = Array.from(e.target.files);
                                        for (const file of files) {
                                            const { dataUrl, blob } = await comprimirImagen(file);
                                            const fotoId = Date.now() + Math.random();
                                            setFotos(prev => [...prev, { id: fotoId, src: dataUrl, desc: '' }]);
                                            subirFotoSupabase(blob, maquina.id).then(url => {
                                                setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, src: url } : f));
                                            }).catch(() => {});
                                        }
                                        e.target.value = '';
                                    }}
                                />
                            </label>

                            {/* Grid de fotos */}
                            {fotos.length > 0 && (
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'10px', marginBottom:'20px' }}>
                                    {fotos.map((foto, fi) => (
                                        <div key={foto.id} style={{ overflow:'hidden', border:`1px solid ${C.border}`, background:C.bgDark, borderRadius:'4px' }}>
                                            {/* Imagen */}
                                            <div style={{ position:'relative', paddingTop:'75%', background:C.bgDark }}>
                                                <img src={foto.src} alt={foto.desc||`Foto ${fi+1}`}
                                                    style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}/>
                                                    <button
                                                    onClick={() => setFotos(prev => prev.filter(f => f.id !== foto.id))}
                                                    style={{ position:'absolute', top:'5px', right:'5px', background:'rgba(239,68,68,0.9)', border:'none', color:'white', width:'24px', height:'24px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                                                    <Trash size={12}/>
                                                </button>
                                            </div>
                                            <div style={{ padding:'6px 8px' }}>
                                                <input
                                                    type="text"
                                                    value={foto.desc}
                                                    onChange={e => setFotos(prev => prev.map(f => f.id===foto.id ? {...f, desc: e.target.value} : f))}
                                                    placeholder="Description..."
                                                    disabled={modoLectura}
                                                    style={{ width:'100%', border:'none', background:'transparent', fontSize:'11px', color:C.textDim, outline:'none', padding:0, boxSizing:'border-box', ...(modoLectura && { cursor:'not-allowed' }) }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Footer fijo ── */}
                <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.border}`, flexShrink:0, background:C.bgDark, display:'flex', gap:'10px' }}>
                    {modoLectura ? (
                        <>
                            {!esTecnico && !soloLectura && (
                            <button onClick={handleEliminarRegistro} style={{ flex:1, padding:'12px', background:'transparent', border:`2px solid #c0392b`, color:'#c0392b', cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.08em', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', borderRadius:'8px' }}>
                                🗑 DELETE RECORD
                            </button>
                            )}
                            {proyecto && <button onClick={()=>generarPDFComponente(proyecto, maquina, faseNombre, sub)} style={{ padding:'12px 16px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                📄 PDF
                            </button>}
                            <button onClick={onClose} style={{ padding:'12px 20px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                Close
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={handleGuardar} disabled={soloLectura} title={soloLectura ? MSG_TRIAL_EXPIRED : undefined} style={{ opacity: soloLectura ? 0.4 : 1, flex:1, padding:'14px', border:'none', background:C.orange, color:C.bgDark, cursor:'pointer', fontWeight:700, fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                <ClipboardList size={16}/> {'Save record'}
                            </button>
                            <button onClick={onClose} style={{ padding:'14px 20px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'1rem', textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </div>
            <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
    );
}

function HerramientaSeccion({ titulo, icono, descripcion, items, onAdd, onSelect, showGauge, tipoHerramienta, C }) {
    const [form, setForm]           = React.useState(null);
    const [escaneando, setEscaneando] = React.useState(false);
    const [scanError, setScanError] = React.useState('');
    const [certFile, setCertFile]   = React.useState(null); // {name, data (base64)}

    const esBomba = tipoHerramienta === 'bomba';

    const handleConfirmar = () => {
        if (!form) return;
        const m = form.modelo.trim();
        const s = form.serial.trim();
        if (!m && !s) return;
        onAdd(m, s, form.fabricante.trim(), certFile, (form.gaugeSerial||'').trim(), (form.pressureGaugeModel||'').trim(), (form.calibrationValid||'').trim());
        setForm(null);
        setCertFile(null);
        setScanError('');
    };

    const escanearHerramienta = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setScanError('');
        setEscaneando(true);
        try {
            const { base64 } = await comprimirImagen(file);

            const promptBomba =`You are analyzing a torque pump label or calibration certificate.
This may be a physical label on the pump OR a calibration certificate document.

Extract ONLY these fields:

FOR PHYSICAL LABEL:
- "fabricante": brand/manufacturer name (e.g. "Texas Controls", "ITH", "Hytorc")
- "modelo": look for "Model", "MODEL", "Type" (e.g. "MK31c", "MK41C")
- "serial": look for "S/N", "Serial Number", "Serial" (e.g. "00299R22001")

FOR CALIBRATION CERTIFICATE:
- "fabricante": brand/manufacturer name
- "modelo": look for "Related Equipment" field (the pump description, e.g. "MK31C AUTOMATIC TORQUE PUMP 800 BAR")
- "serial": look for "Related Equipment Serial Number"
- "pressureGaugeSN": look for "Identification/Serial Number" (this is the gauge, not the pump)
- "pressureGaugeModel": look for "Model" field when it describes a pressure gauge (e.g. "PRESSURE GAUGE WITH CALIBRATION TEST CERT. 0-1000 BAR")
- "calibrationValid": look for "Date of Validity", "VALIDEZ", "Valid Until" — format as DD/MM/YYYY

IGNORE COMPLETELY: Certificate number, Date of Issue, Result (PASS/FAIL),
operator name, addresses, phone numbers, emails, websites.

Return ONLY this JSON, no other text:
{"fabricante":"","modelo":"","serial":"","pressureGaugeSN":"","pressureGaugeModel":"","calibrationValid":""}`;

            const promptLlave = `You are analyzing a hydraulic torque wrench or tensioner label or calibration certificate.
This may be a physical label, a sticker on the tool, or a calibration certificate document.
Labels may be in English or Spanish.

Extract ONLY these fields:

- "fabricante": brand/manufacturer name (e.g. "Texas Controls", "ITH", "Enerpac")
- "modelo": look for "Model", "MODEL", "Type" (e.g. "RSL8", "RSL8 Hydraulic Torque Wrench 1.1/2 SQD")
- "serial": look for "S/N", "Serial Number", "Identification/Serial Number", "CERT S/N"
  IMPORTANT: Serial may contain TWO numbers separated by space — take the COMPLETE value as-is
- "calibrationValid": look for "Date of Validity", "VALIDEZ", "Valid Until" — format as DD/MM/YYYY

IGNORE COMPLETELY: Certificate number ("CERT"), operator name ("OPERARIO"),
Date of Issue, Result (PASS/FAIL), engravings on metal body if sticker is available.

Return ONLY this JSON, no other text:
{"fabricante":"","modelo":"","serial":"","calibrationValid":""}`;

            const prompt = esBomba ? promptBomba : promptLlave;

            const response = await fetch('/api/scan', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model: 'claude-haiku-4-5-20251001',
                    max_tokens: 400,
                    messages: [{ role: 'user', content: [
                        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: base64 } },
                        { type: 'text', text: prompt }
                    ]}]
                })
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData?.error?.message || 'Error ' + response.status);
            }
            const data = await response.json();
            const text = data.content?.[0]?.text || '';
            const clean2 = text.replace(/```json|```/g, '').trim();
            let parsed;
            try { parsed = JSON.parse(clean2); } catch { parsed = {}; }
            if (parsed && (parsed.modelo || parsed.serial || parsed.fabricante)) {
                const clean = (v) => (!v || v === 'N/A' || v === '-') ? '' : String(v).trim();
                // Normalizar calibrationValid
                let calValid = clean(parsed.calibrationValid);
                if (calValid) {
                    const dm = calValid.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
                    if (dm) calValid = `${dm[1].padStart(2,'0')}/${dm[2].padStart(2,'0')}/${dm[3]}`;
                }
                setForm(f => ({
                    ...f,
                    modelo:             clean(parsed.modelo)             || f.modelo,
                    serial:             clean(parsed.serial)             || f.serial,
                    fabricante:         clean(parsed.fabricante)         || f.fabricante,
                    ...(esBomba && {
                        gaugeSerial:        clean(parsed.pressureGaugeSN)  || f.gaugeSerial,
                        pressureGaugeModel: clean(parsed.pressureGaugeModel) || f.pressureGaugeModel,
                    }),
                    calibrationValid:   calValid || f.calibrationValid,
                }));
            }
        } catch(err) {
            setScanError('Error al escanear: ' + err.message);
        } finally {
            setEscaneando(false);
            e.target.value = '';
        }
    };

    const handleCertificado = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => setCertFile({ name: file.name, data: ev.target.result });
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    return (
        <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, marginBottom:'16px', overflow:'hidden' }}>

            {/* Cabecera */}
            <div style={{ padding:'14px 18px', borderBottom: (items.length>0||form)?`1px solid ${C.border}`:'none', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <span style={{ fontSize:'1.1rem' }}>{icono}</span>
                        <h3 style={{ margin:0, fontSize:'0.85rem', color:C.text, fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em' }}>{titulo}</h3>
                        <span style={{ fontSize:'11px', background:C.bgDark, color:C.orange, padding:'2px 8px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700 }}>{items.length}</span>
                    </div>
                    <p style={{ margin:'2px 0 0 30px', fontSize:'11px', color:C.textDim }}>{descripcion}</p>
                </div>
                {!form && (
                    <button onClick={()=>{ setForm({modelo:'', serial:'', fabricante:'', gaugeSerial:'', pressureGaugeModel:'', calibrationValid:''}); setCertFile(null); setScanError(''); }}
                        style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 12px', background:C.orange, color:C.bgDark, border:'none', cursor:'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', flexShrink:0 }}>
                        <PlusCircle size={14}/> {'New'}
                    </button>
                )}
            </div>

            {/* Formulario nueva herramienta */}
            {form && (
                <div style={{ padding:'14px 18px', background:C.bgDark, borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                        <div style={{ fontSize:'10px', color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', fontWeight:700 }}>
                            {'New tool'}
                        </div>
                        <EscanearBtn onScan={escanearHerramienta} escaneando={escaneando} C={C}/>
                    </div>
                    {scanError && (
                        <div style={{ fontSize:'11px', color:'#ef4444', marginBottom:'8px', fontFamily:"'Barlow Condensed', sans-serif" }}>{scanError}</div>
                    )}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                        <div>
                            <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em' }}>{'Model'}</label>
                            <input
                                autoFocus
                                type="text"
                                value={form.modelo}
                                onChange={e => setForm(f => ({...f, modelo: e.target.value}))}
                                onKeyDown={e => e.key==='Enter' && handleConfirmar()}
                                placeholder="Ej: Enerpac ZU4 700 bar"
                                style={{ width:'100%', padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em' }}>{'Serial'}</label>
                            <input
                                type="text"
                                value={form.serial}
                                onChange={e => setForm(f => ({...f, serial: e.target.value}))}
                                onKeyDown={e => e.key==='Enter' && handleConfirmar()}
                                placeholder="Ej: EN-2023-00456"
                                style={{ width:'100%', padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', fontFamily:'monospace', outline:'none', boxSizing:'border-box', borderRadius:'6px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em' }}>Manufacturer</label>
                            <input
                                type="text"
                                value={form.fabricante}
                                onChange={e => setForm(f => ({...f, fabricante: e.target.value}))}
                                onKeyDown={e => e.key==='Enter' && handleConfirmar()}
                                placeholder="Ej: ITH, Hytorc, Atlas Copco..."
                                style={{ width:'100%', padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' }}
                            />
                        </div>
                        {showGauge && (
                            <div>
                                <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em' }}>{'Pressure Gauge S/N'}</label>
                                <input
                                    type="text"
                                    value={form.gaugeSerial||''}
                                    onChange={e => setForm(f => ({...f, gaugeSerial: e.target.value}))}
                                    onKeyDown={e => e.key==='Enter' && handleConfirmar()}
                                    placeholder="e.g. TC-MP-666"
                                    style={{ width:'100%', padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', fontFamily:'monospace', outline:'none', boxSizing:'border-box', borderRadius:'6px' }}
                                />
                            </div>
                        )}
                        {showGauge && (
                            <div>
                                <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em' }}>{'Pressure Gauge Model'}</label>
                                <input
                                    type="text"
                                    value={form.pressureGaugeModel||''}
                                    onChange={e => setForm(f => ({...f, pressureGaugeModel: e.target.value}))}
                                    onKeyDown={e => e.key==='Enter' && handleConfirmar()}
                                    placeholder="e.g. Pressure Gauge 0-1000 BAR"
                                    style={{ width:'100%', padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' }}
                                />
                            </div>
                        )}
                        <div style={{ gridColumn: showGauge ? 'auto' : '1 / -1' }}>
                            <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em' }}>{'Calibration Valid Until'}</label>
                            <input
                                type="date"
                                value={form.calibrationValid||''}
                                onChange={e => setForm(f => ({...f, calibrationValid: e.target.value}))}
                                style={{ width:'100%', padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', colorScheme:'dark', borderRadius:'6px' }}
                            />
                        </div>
                    </div>

                    {/* Certificado */}
                    <div style={{ marginBottom:'12px' }}>
                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.orange, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', marginBottom:'6px', letterSpacing:'0.04em' }}>Certificado</label>
                        {certFile ? (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', background:C.bg, border:`1px solid ${C.green}`, fontSize:'12px', color:C.green }}>
                                <FileText size={13}/>
                                <span style={{ flex:1, fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{certFile.name}</span>
                                <button onClick={()=>setCertFile(null)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', padding:0, display:'flex' }}><X size={13}/></button>
                            </div>
                        ) : (
                            <label style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 12px', cursor:'pointer', background:C.bg, border:`1px dashed ${C.borderMid}`, color:C.textMuted, fontWeight:700, fontSize:'0.72rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', userSelect:'none' }}>
                                <FileText size={13}/> Add certificate
                                <input type="file" accept=".pdf,image/*" style={{display:'none'}} onChange={handleCertificado}/>
                            </label>
                        )}
                    </div>

                    <div style={{ display:'flex', gap:'8px' }}>
                        <button
                            onClick={handleConfirmar}
                            disabled={!form.modelo.trim() && !form.serial.trim()}
                            style={{ padding:'8px 18px', background:(form.modelo.trim()||form.serial.trim())?C.green:C.borderMid, color:(form.modelo.trim()||form.serial.trim())?'white':C.textDim, border:'none', cursor:(form.modelo.trim()||form.serial.trim())?'pointer':'not-allowed', fontWeight:700, fontSize:'0.8rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', borderRadius:'6px' }}>
                            {'✓ Add'}
                        </button>
                        <button onClick={()=>{ setForm(null); setCertFile(null); setScanError(''); }}
                            style={{ padding:'8px 12px', background:'transparent', border:`1px solid ${C.borderMid}`, cursor:'pointer', color:C.textMuted, fontSize:'0.8rem', fontFamily:"'Barlow Condensed', sans-serif", borderRadius:'6px' }}>
                            {'Cancel'}
                        </button>
                    </div>
                </div>
            )}

            {items.length === 0 && !form && (
                <div style={{ padding:'16px', textAlign:'center', color:C.textDim, fontSize:'0.82rem' }}>
                    {`No ${titulo.toLowerCase()} registered. Click "New" to add.`}
                </div>
            )}

            {/* Items guardados */}
            {items.map((h, idx) => (
                <div key={h.id}
                    onClick={() => onSelect(h)}
                    onMouseEnter={e => e.currentTarget.style.background = C.bgHover}
                    onMouseLeave={e => e.currentTarget.style.background = C.bgDark}
                    style={{ padding:'12px 18px', borderBottom: idx<items.length-1?`1px solid ${C.border}`:'none', display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:'12px', alignItems:'center', background:C.bgDark, cursor:'pointer' }}>
                    <div>
                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.textDim, textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em', fontFamily:"'Barlow Condensed', sans-serif" }}>{'Model'}</label>
                        <div style={{ fontSize:'0.875rem', color:C.text, fontFamily:'monospace' }}>{h.modelo||'—'}</div>
                        {h.fabricante && (
                            <div style={{ fontSize:'10px', color:C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:'2px' }}>
                                {h.fabricante}
                            </div>
                        )}
                    </div>
                    <div>
                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.textDim, textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em', fontFamily:"'Barlow Condensed', sans-serif" }}>{'Serial'}</label>
                        <div style={{ fontSize:'0.875rem', color:C.text, fontFamily:'monospace' }}>{h.serial||'—'}</div>
                    </div>
                    <div>
                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:C.textDim, textTransform:'uppercase', marginBottom:'4px', letterSpacing:'0.04em', fontFamily:"'Barlow Condensed', sans-serif" }}>Certificate</label>
                        <div style={{ fontSize:'0.875rem', fontWeight:700, color: h.certificado ? C.green : C.textDim }}>{h.certificado ? 'Yes' : 'No'}</div>
                        {h.calibrationValid && (() => {
                            const expired = new Date(h.calibrationValid) < new Date();
                            return (
                                <div style={{ fontSize:'10px', fontWeight:700, color: expired ? '#c0392b' : '#5a9e45', marginTop:'4px' }}>
                                    {expired ? `⚠️ CALIB. EXPIRED` : `✓ Valid ${h.calibrationValid}`}
                                </div>
                            );
                        })()}
                    </div>
                    <span style={{ fontSize:'9px', background:C.orange, color:C.bgDark, padding:'2px 7px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>VIEW</span>
                </div>
            ))}
        </div>
    );
}

function HerramientaSelector({ catalogo, tipo, selectedId, onSelect, C }) {
    const tieneItems = catalogo && catalogo.length > 0;
    return (
        <div style={{ marginBottom:'16px' }}>
            {tieneItems ? (
                <select
                    value={selectedId||''}
                    onChange={e => {
                        if (!e.target.value) { onSelect(null); return; }
                        const item = catalogo.find(h => String(h.id) === e.target.value);
                        if (item) onSelect(item);
                    }}
                    style={{ width:'100%', padding:'10px 12px', background:C.bgDark, border:`2px solid ${selectedId?C.green:C.orange}`, fontSize:'0.875rem', outline:'none', color: selectedId?C.green:C.textMuted, cursor:'pointer', fontWeight:600, fontFamily:"'Barlow', sans-serif" }}
                >
                    <option value="">{`— Select ${tipo} from catalogue —`}</option>
                    {catalogo.map(h => (
                        <option key={h.id} value={String(h.id)}>
                            {h.modelo || 'No model'}{h.serial ? ` · S/N: ${h.serial}` : ''}
                        </option>
                    ))}
                </select>
            ) : (
                <div style={{ padding:'10px 14px', background:C.bgDark, border:`1px solid ${C.orange}`, fontSize:'12px', color:C.orange }}>
                    {`⚠️ No ${tipo.toLowerCase()}s registered in this project. Add them via the Tools button.`}
                </div>
            )}
        </div>
    );
}

function LlaveSelector({ herramientas, llaveSelId, onLlaveSelect, C }) {
    const [tipoCategoria, setTipoCategoria] = React.useState('hidraulicas');
    const catalogoActual = herramientas?.[tipoCategoria] || [];
    return (
        <div style={{ marginBottom:'16px' }}>
            <div style={{ display:'flex', gap:'8px', marginBottom:'10px' }}>
                <button onClick={()=>setTipoCategoria('hidraulicas')}
                    style={{ flex:1, padding:'7px', border:`2px solid ${tipoCategoria==='hidraulicas'?C.orange:C.borderMid}`, background: tipoCategoria==='hidraulicas'?C.bgDark:'transparent', color: tipoCategoria==='hidraulicas'?C.orange:C.textDim, cursor:'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>
                    {'🔧 Hydraulic Wrench'}
                </button>
                <button onClick={()=>setTipoCategoria('tensionadores')}
                    style={{ flex:1, padding:'7px', border:`2px solid ${tipoCategoria==='tensionadores'?C.orange:C.borderMid}`, background: tipoCategoria==='tensionadores'?C.bgDark:'transparent', color: tipoCategoria==='tensionadores'?C.orange:C.textDim, cursor:'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>
                    {'⚙️ Tensioner (Puller)'}
                </button>
            </div>
            <HerramientaSelector
                catalogo={catalogoActual}
                tipo={tipoCategoria==='hidraulicas' ? 'Hydraulic wrench' : 'Tensioner'}
                selectedId={llaveSelId}
                onSelect={onLlaveSelect}
                C={C}
            />
        </div>
    );
}

function EscanearBtn({ onScan, escaneando, C }) {
    const { soloLectura } = useTech();
    if (soloLectura) return null;
    return (
        <label style={{
            display:'flex', alignItems:'center', gap:'6px',
            padding:'5px 12px', cursor: escaneando ? 'wait' : 'pointer',
            background: escaneando ? C.bgDark : 'linear-gradient(135deg,#4c1d95,#7c3aed)',
            color: escaneando ? C.textDim : 'white',
            fontWeight:700, fontSize:'0.72rem', flexShrink:0,
            border:`1px solid ${escaneando?C.borderMid:'#7c3aed'}`, transition:'all 0.2s', userSelect:'none',
            marginBottom:'10px',
            opacity: escaneando ? 0.7 : 1,
            fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em',
        }}>
            {escaneando
                ? <><Loader size={13} style={{animation:'spin 1s linear infinite'}}/> {'Analysing...'}</>
                : <><ScanLine size={13}/> {'Scan label'}</>
            }
            <input type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={onScan} disabled={escaneando}/>
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </label>
    );
}

function ScanPanel({ preview, result, error, escaneando, tipo, onAceptar, onAplicarTodos, onDescartar, mismatch, aplicados = new Set(), onRechazar, C }) {
    if (!preview && !result && !error && !escaneando) return null;

    const esApriete = tipo === TIPO.APRIETE;
    const dosManufacturers = result?.fabricante && result?.fabricante_2;

    const definicion = esApriete ? [
        { key:'metrica',         label:'Bolt Size' },
        { key:'tornItem',        label:'Item No.' },
        { key:'tornSerial',      label:'Serial' },
        { key:'tornFabricante',  label:'Manufacturer' },
        { key:'tornBatch',       label:'Batch' },
    ] : [
        { key:'item',       label:'Item No.' },
        { key:'serial',     label:'Serial' },
        { key:'fabricante', label:'Manufacturer' },
        { key:'fabricante_2', label:'Manufacturer (option 2)' },
        { key:'modelType',  label:'Model / Type' },
        { key:'vui',        label:'VUI' },
        { key:'numSet',     label:'Set No.' },
        { key:'set',        label:'Set' },
    ];

    const camposConValor = result
        ? definicion.filter(d => result[d.key])
        : [];
    const camposExtraDetectados = result?._extra
        ? Object.entries(result._extra).filter(([k,v]) => v).map(([k,v]) => ({ key: `_extra_${k}`, label: k, value: v }))
        : [];
    const tieneAlgoCampo = camposConValor.length > 0 || camposExtraDetectados.length > 0;
    const todosAceptados = result && tieneAlgoCampo && camposConValor.every(({key}) => aplicados.has(key)) && camposExtraDetectados.every(({key}) => aplicados.has(key));

    const estiloFila = {
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:C.bg, border:`1px solid #4c1d95`, padding:'8px 10px', gap:'10px', borderRadius:'6px'
    };
    const estiloUse = {
        flexShrink:0, padding:'5px 12px', background:'#7c3aed', color:'white',
        border:'none', cursor:'pointer', fontWeight:700, fontSize:'0.72rem',
        fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'4px'
    };

    return (
        <div style={{ background:C.bgDark, border:`2px solid #7c3aed`, padding:'14px', marginBottom:'16px', borderRadius:'8px' }}>
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    {preview && (
                        <img src={preview} alt="etiqueta"
                            style={{ width:'48px', height:'36px', objectFit:'cover', border:'1px solid #4c1d95' }}/>
                    )}
                    <span style={{ fontSize:'11px', fontWeight:700, color:'#a855f7', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'Barlow Condensed', sans-serif" }}>
                        {escaneando ? 'Analysing...' : error ? '⚠️ Error' : todosAceptados ? '✅ All accepted' : '✨ Data detected'}
                    </span>
                </div>
                <button onClick={onDescartar} style={{ background:'none', border:'none', color:C.textDim, cursor:'pointer', padding:'2px', display:'flex' }}>
                    <X size={14}/>
                </button>
            </div>

            {escaneando && (
                <div style={{ display:'flex', alignItems:'center', gap:'8px', color:'#a855f7', fontSize:'0.85rem', fontWeight:600 }}>
                    <Loader size={15} style={{animation:'spin 1s linear infinite'}}/>
                    {'Claude is analysing the label...'}
                </div>
            )}

            {/* ── Alerta de mismatch ── */}
            {mismatch && mismatch.campos.length > 0 && !escaneando && (
                <div style={{ background:'rgba(234,179,8,0.08)', border:'1px solid #ca8a04', padding:'10px 12px', marginBottom:'10px', borderRadius:'6px' }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:'#fbbf24', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px', fontFamily:"'Barlow Condensed', sans-serif" }}>
                        ⚠️ Mismatched data — different from previous registration
                    </div>
                    {mismatch.campos.map(c => (
                        <div key={c.key} style={{ fontSize:'11px', marginBottom:'3px', fontFamily:'monospace' }}>
                            <span style={{ color:'#9ca3af' }}>{c.label}: </span>
                            <span style={{ textDecoration:'line-through', color:'#6b7280' }}>{c.anterior}</span>
                            <span style={{ color:'#fbbf24' }}> → {c.nuevo}</span>
                        </div>
                    ))}
                    <div style={{ fontSize:'10px', color:'#ca8a04', marginTop:'6px' }}>
                        Review carefully. Applying will ask for confirmation before overwriting.
                    </div>
                </div>
            )}

            {error && !escaneando && (
                <div style={{ color:'#dc2626', fontSize:'0.82rem' }}>
                    {'Could not read the label. Fill in the fields manually.'}
                </div>
            )}

            {todosAceptados && !escaneando && (
                <div style={{ color:C.green, fontSize:'0.82rem', fontWeight:600 }}>
                    {'All fields have been applied correctly.'}
                </div>
            )}

            {result && !escaneando && (camposConValor.length > 0 || camposExtraDetectados.length > 0) && (
                <>
                    <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'10px' }}>
                        {camposConValor.map(({ key, label }) => {
                            if (dosManufacturers && (key === 'fabricante' || key === 'fabricante_2')) {
                                const otherKey = key === 'fabricante' ? 'fabricante_2' : 'fabricante';
                                if (aplicados.has(otherKey)) return null;
                                const optLabel = key === 'fabricante' ? 'MANUFACTURER (option 1)' : 'MANUFACTURER (option 2)';
                                const usado = aplicados.has(key);
                                const labelColor = usado ? '#3d7a2e' : '#c0392b';
                                const labelIcon  = usado ? '✓' : '⚠️';
                                return (
                                    <div key={key} style={{ ...estiloFila, border:`1px solid ${usado ? '#3d7a2e' : '#c0392b'}` }}>
                                        <div style={{ minWidth:0 }}>
                                            <div style={{ fontSize:'10px', color:labelColor, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', fontFamily:"'Barlow Condensed', sans-serif" }}>{labelIcon} {optLabel}</div>
                                            <div style={{ fontSize:'13px', color:C.text, fontFamily:'monospace', fontWeight:700, marginTop:'1px', wordBreak:'break-all' }}>{result[key]}</div>
                                        </div>
                                        <button onClick={() => usado ? onRechazar?.(key) : onAceptar(key)} style={{ ...estiloUse, background: usado ? '#16a34a' : '#7c3aed' }}>{usado ? '✓ USED' : '✓ USE'}</button>
                                    </div>
                                );
                            }
                            const usado = aplicados.has(key);
                            return (
                                <div key={key} style={estiloFila}>
                                    <div style={{ minWidth:0 }}>
                                        <div style={{ fontSize:'10px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', fontFamily:"'Barlow Condensed', sans-serif" }}>{label}</div>
                                        <div style={{ fontSize:'13px', color:C.text, fontFamily:'monospace', fontWeight:700, marginTop:'1px', wordBreak:'break-all' }}>{result[key]}</div>
                                    </div>
                                    <button onClick={() => usado ? onRechazar?.(key) : onAceptar(key)} style={{ ...estiloUse, background: usado ? '#16a34a' : '#7c3aed' }}>{usado ? '✓ USED' : '✓ USE'}</button>
                                </div>
                            );
                        })}
                        {camposExtraDetectados.length > 0 && (
                            <>
                                <div style={{ fontSize:'9px', color:'#a855f7', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginTop:'4px' }}>
                                    — Additional detected fields —
                                </div>
                                {camposExtraDetectados.map(({ key, label, value }) => {
                                    const usado = aplicados.has(key);
                                    return (
                                        <div key={key} style={{ ...estiloFila, border:'1px solid #4c1d95' }}>
                                            <div style={{ minWidth:0 }}>
                                                <div style={{ fontSize:'10px', color:'#a855f7', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', fontFamily:"'Barlow Condensed', sans-serif" }}>{label}</div>
                                                <div style={{ fontSize:'13px', color:C.text, fontFamily:'monospace', fontWeight:700, marginTop:'1px', wordBreak:'break-all' }}>{value}</div>
                                            </div>
                                            <button onClick={() => usado ? onRechazar?.(key) : onAceptar(key)} style={{ ...estiloUse, background: usado ? '#16a34a' : '#7c3aed' }}>{usado ? '✓ USED' : '✓ USE'}</button>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                    <button onClick={onAplicarTodos}
                        style={{ width:'100%', padding:'12px 16px', background:'#e07b1a', color:'#ffffff', border:'2px solid #c96a10', cursor:'pointer', fontWeight:900, fontSize:'0.9rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px', boxShadow:'0 3px 10px rgba(224,123,26,0.45)', display:'flex', alignItems:'center', justifyContent:'center', gap:'7px' }}>
                        <span style={{ fontSize:'1rem' }}>✓</span>{'USE ALL (EMPTY FIELDS ONLY)'}
                    </button>
                </>
            )}

            {result && !escaneando && camposConValor.length === 0 && !todosAceptados && (
                <div style={{ color:C.orange, fontSize:'0.82rem', fontWeight:600 }}>
                    {'⚠️ No data detected. Fill in the fields manually.'}
                </div>
            )}
        </div>
    );
}

function ModalFase({ fase, maquinaId, onClose, onAbrirSubtarea, infoBadge, tipoDeSubtarea, modoLecturaCard, hoveredSubKey, setHoveredSubKey, proyectoSeleccionado, maquina, generarPDFFase, esTecnico, C }) {
    const hechas = fase.subtareas.filter(s => s.hecho).length;
    const total  = fase.subtareas.length;
    const pct    = total > 0 ? Math.round((hechas / total) * 100) : 0;

    return (
        <div style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{ background:C.bgCard, width:'100%', maxWidth:'640px', maxHeight:'88vh', display:'flex', flexDirection:'column', borderRadius:'16px 16px 0 0', boxShadow:'0 -8px 40px rgba(0,0,0,0.5)', animation:'slideUp 0.22s ease' }}>
                {/* Handle bar */}
                <div style={{ display:'flex', justifyContent:'center', padding:'10px 0 4px' }}>
                    <div style={{ width:'36px', height:'4px', background:C.borderMid, borderRadius:'2px' }}/>
                </div>
                {/* Header */}
                <div style={{ padding:'8px 20px 14px', borderBottom:`3px solid ${fase.hecho ? C.green : C.orange}`, flexShrink:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                        <div style={{ width:'28px', height:'28px', flexShrink:0, background: fase.hecho?C.green:C.orange, color: fase.hecho?'#fff':C.bgDark, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", borderRadius:'4px' }}>
                            {fase.hecho ? '✓' : fase.id}
                        </div>
                        <span style={{ flex:1, fontSize:'18px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', color:C.text }}>{fase.nombre}</span>
                        <span style={{ fontSize:'12px', fontWeight:700, color:C.orange, background:'rgba(224,123,26,0.12)', padding:'3px 10px', fontFamily:"'Barlow Condensed', sans-serif", flexShrink:0 }}>{fase.peso}%</span>
                        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'7px', display:'flex', borderRadius:'8px', flexShrink:0 }}><X size={18}/></button>
                    </div>
                    <div style={{ marginTop:'10px' }}>
                        <div style={{ height:'5px', background:C.bg, borderRadius:'3px', overflow:'hidden' }}>
                            <div style={{ width:pct+'%', height:'100%', background: fase.hecho?C.green:C.orange, transition:'width 0.4s' }}/>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', marginTop:'5px' }}>
                            <span style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif" }}>{hechas}/{total} completed</span>
                            <span style={{ fontSize:'11px', fontWeight:700, color: pct>0?C.orange:C.textDim, fontFamily:"'Barlow Condensed', sans-serif" }}>{pct}%</span>
                        </div>
                    </div>
                </div>
                {/* Subtask list */}
                <div style={{ overflowY:'auto', flex:1, padding:'12px 16px' }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                        {fase.subtareas.map((sub, idx) => {
                            const badges    = infoBadge(sub);
                            const tipo      = sub._tipo || tipoDeSubtarea(sub.nombre, fase.id);
                            const subKey    = `${fase.id}-${idx}`;
                            const bloqueada = esTecnico && sub.locked;
                            return (
                                <div key={idx}
                                    onClick={bloqueada ? undefined : () => onAbrirSubtarea(maquinaId, fase.id, idx)}
                                    onMouseEnter={() => !bloqueada && sub.hecho && setHoveredSubKey(subKey)}
                                    onMouseLeave={() => setHoveredSubKey(null)}
                                    style={{ display:'flex', alignItems:'center', gap:'12px', padding:'13px 14px', cursor: bloqueada ? 'default' : 'pointer', background: sub.hecho?(hoveredSubKey===subKey&&!bloqueada?C.bgHover:C.bgDone):C.bgDark, border:`1px solid ${sub.hecho?C.green:C.border}`, borderLeft:`4px solid ${sub.hecho?C.green:C.orange}`, borderRadius:'8px', transition:'background 0.15s' }}>
                                    <div style={{ width:'20px', height:'20px', flexShrink:0, border:`2px solid ${sub.hecho?C.green:C.borderMid}`, background: modoLecturaCard(sub)?'transparent':sub.hecho?C.green:'transparent', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'3px' }}>
                                        {bloqueada ? <span style={{ fontSize:'11px' }}>🔒</span> : modoLecturaCard(sub) ? <span style={{ fontSize:'11px', color:C.green }}>🔒</span> : sub.hecho ? <span style={{ color:C.bgDark, fontSize:'12px', fontWeight:'bold' }}>✓</span> : null}
                                    </div>
                                    <div style={{ flex:1, minWidth:0 }}>
                                        <div style={{ fontSize:'0.9rem', color: sub.hecho?C.textDim:C.text, textDecoration: sub.hecho?'line-through':'none', fontWeight: sub.hecho?400:600, display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                                            {sub.nombre}
                                            {(tipo===TIPO.COMPONENTE||tipo===TIPO.PALA) && fase.id===1 && <span style={{ fontSize:'10px', background:C.bg, color:C.orange, padding:'1px 5px', border:`1px solid ${C.borderMid}`, fontFamily:"'Barlow Condensed', sans-serif" }}>S/N</span>}
                                            {(tipo===TIPO.COMPONENTE||tipo===TIPO.PALA) && sub.fotos?.length > 0 && <span style={{ fontSize:'10px', background:C.bg, color:'#9333ea', padding:'1px 5px', border:`1px solid ${C.borderMid}`, fontFamily:"'Barlow Condensed', sans-serif" }}>📸 {sub.fotos.length}</span>}
                                            {tipo===TIPO.APRIETE && <span style={{ fontSize:'10px', background:C.bg, color:'#ea580c', padding:'1px 5px', border:`1px solid ${C.borderMid}`, fontFamily:"'Barlow Condensed', sans-serif" }}>⚙️</span>}
                                        </div>
                                        {sub.hecho && badges.length > 0 && (
                                            <div style={{ fontSize:'10px', color:C.textDim, marginTop:'4px', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                                                {badges.map((b, i) => <span key={i}>{b}</span>)}
                                            </div>
                                        )}
                                    </div>
                                    {sub.hecho && (sub.serial || sub.item || sub.tornSerial || sub.bomTipo) && (
                                        <span style={{ fontSize:'9px', background:C.orange, color:C.bgDark, padding:'2px 6px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>VIEW</span>
                                    )}
                                    <ChevronRight size={16} color={C.textDim} style={{ flexShrink:0 }}/>
                                </div>
                            );
                        })}
                        {fase.hecho && (
                            <div style={{ padding:'12px', background:C.bgDone, border:`1px solid ${C.green}`, borderRadius:'8px', fontSize:'12px', color:C.green, fontWeight:700, textAlign:'center', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', marginTop:'4px' }}>
                                ✅ Phase complete
                            </div>
                        )}
                    </div>
                </div>
                {/* Footer */}
                <div style={{ padding:'12px 16px', borderTop:`1px solid ${C.border}`, flexShrink:0, display:'flex', justifyContent:'flex-end' }}>
                    <button onClick={() => proyectoSeleccionado && generarPDFFase(proyectoSeleccionado, maquina, fase)}
                        style={{ background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textDim, cursor:'pointer', padding:'8px 16px', display:'flex', alignItems:'center', gap:'6px', fontFamily:"'Barlow Condensed', sans-serif", fontSize:'11px', fontWeight:700, textTransform:'uppercase', borderRadius:'6px' }}>
                        <FileText size={12}/> Export PDF
                    </button>
                </div>
            </div>
        </div>
    );
}

const SectionTitle = ({icon, title, color, C}) => (
    <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'10px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>
        <span style={{ color:C.orange }}>{icon}</span>
        <span style={{ fontSize:'0.72rem', fontWeight:700, color:color||C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'Barlow Condensed', sans-serif" }}>{title}</span>
    </div>
);
const ReadField = ({label, value, mono, C}) => (
    <div>
        <div style={{ fontSize:'10px', color:C.textDim, fontWeight:700, textTransform:'uppercase', marginBottom:'2px', fontFamily:"'Barlow Condensed', sans-serif", letterSpacing:'0.04em' }}>{label}</div>
        <div style={{ fontSize:'0.85rem', color:C.text, fontFamily: mono?'monospace':'inherit', fontWeight: mono?600:'normal' }}>{value}</div>
    </div>
);

function ModalComponenteExtra({ compNombre, datos, proyectoModelo = '', onClose, onGuardar, esTecnico, maquinaId, C }) {
    const { soloLectura } = useTech();
    const mLS = { display:'flex', alignItems:'center', fontWeight:700, fontSize:'0.68rem', color:C.textDim, marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'Barlow Condensed', sans-serif" };
    const mIS = { width:'100%', padding:'9px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' };

    const [hecho,      setHecho]      = useState(datos.hecho      || false);
    const [item,       setItem]       = useState(datos.item       || '');
    const [serial,     setSerial]     = useState(datos.serial     || '');
    const [fabricante, setFabricante] = useState(datos.fabricante || '');
    const [vui,        setVui]        = useState(datos.vui        || '');
    const [operario,   setOperario]   = useState(datos.operario   || '');
    const [fecha,      setFecha]      = useState(datos.fecha      || '');
    const [notas,      setNotas]      = useState(datos.notas      || '');
    const [fotos,      setFotos]      = useState(datos.fotos      || []);
    const [camposExtra, setCamposExtra] = useState(datos.camposExtra || {});
    const camposFab = getCamposFabricante(proyectoModelo || fabricante);

    const [escaneando,  setEscaneando]  = useState(false);
    const [scanPreview, setScanPreview] = useState(null);
    const [scanResult,  setScanResult]  = useState(null);
    const [scanError,   setScanError]   = useState('');
    const [scanMismatch, setScanMismatch] = useState(null);
    const [aplicados, setAplicados] = useState(new Set());

    const escanearEtiqueta = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setScanError(''); setScanResult(null); setScanMismatch(null); setAplicados(new Set()); setEscaneando(true);
        const previewReader = new FileReader();
        previewReader.onload = ev => setScanPreview(ev.target.result);
        previewReader.readAsDataURL(files[0]);

        const fabKey = detectarFabricante(proyectoModelo || fabricante);
        const esVestas = fabKey === 'vestas';
        const camposEsp = fabKey ? FABRICANTE_CAMPOS[fabKey].campos : [];
        const extraKeys = camposEsp.filter(c => c !== 'VUI');
        const extraSchema = extraKeys.length > 0
            ? `,"_extra":{${extraKeys.map(c => `"${c}":""`).join(',')}}`
            : '';
        const extraInstructions = extraKeys.length > 0
            ? `\n- "_extra": object — extract these specific fields if visible: ${extraKeys.map(c => `"${c}"`).join(', ')}`
            : '';

        const prompt = esVestas
            ? `You are analyzing a wind turbine component label from Vestas.
Extract ONLY these fields:
- "item": look for "Item", "Generic Item", "Article No", "Item No", "Material Number", "VESTAS ITEM NUMBER"
- "serial": look for "Serial", "S/N", "Manu S/N", "Manuf S/N". If format "XXXXX - YYYYYYY - ZZZZZZ" take ONLY the LAST number.
- "fabricante": brand name on the SMALL label. If two brands, return both as "fabricante" and "fabricante_2"
- "modelType": look for "Type", "Model", "Type/Model"
- "vui": look for "VUI", "Traceability", "Traceability No", "Trace No", "Trace ID"${extraInstructions}
IGNORE: dates, GPS, weight, voltage, addresses, QR values.
Return ONLY this JSON: {"item":"","serial":"","fabricante":"","fabricante_2":"","modelType":"","vui":""${extraSchema}}`
            : `You are analyzing a wind turbine component label.
Extract ONLY: "item" (Item/Part No), "serial" (Serial/S/N), "fabricante" (Manufacturer brand), "fabricante_2" (second brand if present), "modelType" (Type/Model).${extraInstructions}
IGNORE: dates, GPS, addresses, technical specs.
Return ONLY this JSON: {"item":"","serial":"","fabricante":"","fabricante_2":"","modelType":""${extraSchema}}`;

        const toStr = v => {
            if (!v || v === null) return '';
            if (typeof v === 'object') return Object.values(v).filter(x => x && typeof x === 'string').join(' ') || '';
            return String(v).trim();
        };
        const normalizarResultado = (parsed) => {
            const r = { ...parsed };
            Object.keys(r).forEach(k => {
                if (k === '_extra') return;
                if (!r[k] || r[k] === 'N/A' || r[k] === 'n/a' || r[k] === '-' || r[k] === null) r[k] = '';
                if (typeof r[k] === 'object') r[k] = toStr(r[k]);
                if (typeof r[k] === 'string') r[k] = r[k].trim();
            });
            if (!r.vui && r._extra?.traceability) { r.vui = r._extra.traceability; delete r._extra.traceability; }
            if (r._extra && typeof r._extra === 'object') {
                r._extra = Object.fromEntries(
                    Object.entries(r._extra)
                        .map(([k, v]) => [k, toStr(v)])
                        .filter(([, v]) => v && v.trim())
                );
            }
            return r;
        };

        try {
            let mergedResult = {};
            const conflictos = [];

            for (const file of files) {
                const { base64, dataUrl: fotoUrl, blob } = await comprimirImagen(file);
                const fotoId = Date.now() + Math.random();
                setFotos(prev => [...prev, { id: fotoId, src: fotoUrl, desc: 'Label scan', esEtiqueta: true }]);
                subirFotoSupabase(blob, maquinaId).then(url => {
                    setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, src: url } : f));
                }).catch(() => {});
                const response = await fetch('/api/scan', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        model: 'claude-haiku-4-5-20251001', max_tokens: 500,
                        messages: [{ role: 'user', content: [
                            { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: base64 } },
                            { type: 'text', text: prompt }
                        ]}]
                    })
                });
                if (!response.ok) { const e2 = await response.json().catch(()=>({})); throw new Error(e2?.error?.message || 'Error ' + response.status); }
                const data = await response.json();
                let parsed;
                try { parsed = JSON.parse(data.content?.[0]?.text.replace(/```json|```/g, '').trim() || '{}'); } catch { parsed = {}; }
                const r = normalizarResultado(parsed);

                Object.entries(r).forEach(([k, v]) => {
                    if (k === '_extra') {
                        if (v && typeof v === 'object') {
                            Object.entries(v).forEach(([ek, ev]) => {
                                if (ev && mergedResult._extra?.[ek] && mergedResult._extra[ek] !== ev)
                                    conflictos.push({ key: `_extra_${ek}`, label: ek, anterior: mergedResult._extra[ek], nuevo: ev });
                                if (ev) mergedResult._extra = { ...(mergedResult._extra || {}), [ek]: ev };
                            });
                        }
                    } else {
                        if (v && mergedResult[k] && mergedResult[k] !== v)
                            conflictos.push({ key: k, label: k, anterior: mergedResult[k], nuevo: v });
                        if (v) mergedResult[k] = v;
                    }
                });
            }

            // Conflictos con datos ya en el formulario
            const formActual = { item, serial, fabricante, vui };
            Object.entries(formActual).forEach(([k, formVal]) => {
                if (formVal && mergedResult[k] && formVal !== mergedResult[k] && !conflictos.find(c => c.key === k))
                    conflictos.push({ key: k, label: k === 'item' ? 'Item No.' : k === 'serial' ? 'Serial' : k === 'fabricante' ? 'Manufacturer' : 'VUI', anterior: formVal, nuevo: mergedResult[k] });
            });

            const tieneData = Object.entries(mergedResult).some(([k, v]) => k !== '_extra' && v) || Object.keys(mergedResult._extra || {}).length > 0;
            if (!tieneData) {
                setScanError('Could not read label.');
            } else {
                setScanResult(mergedResult);
                if (conflictos.length > 0) setScanMismatch({ campos: conflictos });
            }
        } catch (err) {
            setScanError('No se pudo leer la etiqueta.'); console.error(err);
        } finally { setEscaneando(false); e.target.value = ''; }
    };

    const aceptarCampo = (key) => {
        if (!scanResult) return;
        if (key.startsWith('_extra_')) {
            const realKey = key.slice(7);
            const val = scanResult._extra?.[realKey];
            if (!val) return;
            setCamposExtra(prev => ({ ...prev, [realKey]: val }));
            setAplicados(prev => new Set([...prev, key]));
            return;
        }
        const val = scanResult[key];
        if (!val) return;
        const mismatchValoresExtra = { item, serial };
        const mismatchLabelsExtra  = { item: 'Item No.', serial: 'Serial' };
        if (key in mismatchValoresExtra && mismatchValoresExtra[key] && mismatchValoresExtra[key] !== val) {
            if (!window.confirm(`⚠️ MISMATCHED DATA\n\n${mismatchLabelsExtra[key]}:\n  Previously registered: "${mismatchValoresExtra[key]}"\n  New scan: "${val}"\n\nOverwrite with new value?`)) return;
        }
        const setters = { item: setItem, serial: setSerial, fabricante: setFabricante, fabricante_2: setFabricante, modelType: ()=>{}, vui: setVui };
        if (setters[key]) setters[key](val);
        setAplicados(prev => {
            const n = new Set([...prev, key]);
            if (key === 'fabricante' && scanResult.fabricante_2) n.delete('fabricante_2');
            if (key === 'fabricante_2') n.delete('fabricante');
            return n;
        });
    };

    const rechazarCampo = (key) => {
        if (key.startsWith('_extra_')) {
            const realKey = key.slice(7);
            setCamposExtra(prev => ({ ...prev, [realKey]: '' }));
            setAplicados(prev => { const n = new Set(prev); n.delete(key); return n; });
            return;
        }
        const clearers = { item: () => setItem(''), serial: () => setSerial(''), fabricante: () => setFabricante(''), fabricante_2: () => setFabricante(''), vui: () => setVui('') };
        if (clearers[key]) clearers[key]();
        setAplicados(prev => { const n = new Set(prev); n.delete(key); return n; });
    };

    const aplicarScanResult = () => {
        if (!scanResult) return;
        let forzarSobreescribir = false;
        if (scanMismatch && scanMismatch.campos.length > 0) {
            const detalles = scanMismatch.campos.map(c =>
                `• ${c.label}:\n    Registered: "${c.anterior}"\n    New scan:   "${c.nuevo}"`
            ).join('\n\n');
            if (!window.confirm(`⚠️ MISMATCHED DATA DETECTED\n\n${detalles}\n\nDo you want to overwrite with the new scan data?`)) return;
            forzarSobreescribir = true;
        }
        if ((forzarSobreescribir || !item)       && scanResult.item)       setItem(scanResult.item);
        if ((forzarSobreescribir || !serial)     && scanResult.serial)     setSerial(scanResult.serial);
        if ((forzarSobreescribir || !fabricante) && scanResult.fabricante) setFabricante(scanResult.fabricante);
        if ((forzarSobreescribir || !vui)        && scanResult.vui)        setVui(scanResult.vui);
        if (scanResult._extra) {
            Object.entries(scanResult._extra).forEach(([k, v]) => {
                if (v && (forzarSobreescribir || !camposExtra[k])) setCamposExtra(prev => ({ ...prev, [k]: v }));
            });
        }
        setScanResult(null); setScanPreview(null); setScanMismatch(null);
    };

    const modoLectura = datos.hecho === true || !!(datos.serial || datos.item || datos.fecha);

    const handleGuardar = () => {
        const fechaFinal = fecha || formatearFecha(hoyISO());
        if (!fecha) setFecha(fechaFinal);

        const faltantes = [];
        if (!operario) faltantes.push('Operator');
        if (!fechaFinal) faltantes.push('Date');
        if (!item) faltantes.push('Item No.');
        if (!serial) faltantes.push('Serial Number');
        if (faltantes.length > 0) {
            const continuar = window.confirm(`Missing data: ${faltantes.join(', ')}.\nDo you want to continue anyway?`);
            if (!continuar) return;
        }
        onGuardar({ hecho: true, item, serial, fabricante, vui, operario, fecha: fechaFinal, notas, fotos, camposExtra });
        onClose();
    };

    const handleEliminarRegistro = () => {
        const confirmar = window.confirm(
            `Delete record for "${compNombre}"?\n\nAll recorded data will be erased and the task will be set back to pending.`
        );
        if (!confirmar) return;
        onGuardar({ item:'', serial:'', fabricante:'', vui:'', operario:'', fecha:'', notas:'', fotos:[], hecho:false, camposExtra:{} });
        onClose();
    };

    return (
        <div style={{ position:'fixed', inset:0, zIndex:10000, background:C.overlay, backdropFilter:'blur(3px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
            <div style={{ background:C.bgCard, border:`1px solid ${C.borderMid}`, width:'100%', maxWidth:'560px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 64px rgba(0,0,0,0.6)', overflow:'hidden', animation:'slideUp 0.2s ease', borderRadius:'12px' }}>
                {/* Header */}
                <div style={{ background:C.bgDark, padding:'18px 24px', flexShrink:0, borderBottom:`3px solid ${C.orange}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                            <div style={{ fontSize:'10px', color:C.textDim, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'3px', fontFamily:"'Barlow Condensed', sans-serif" }}>Registro de Componentes</div>
                            <h3 style={{ margin:0, color:C.text, fontSize:'1.2rem', fontWeight:900, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>{compNombre}</h3>
                            <span style={{ fontSize:'11px', fontWeight:700, background:'rgba(255,255,255,0.08)', color: hecho?C.green:C.orange, padding:'2px 10px', border:`1px solid ${hecho?C.green:C.orange}`, marginTop:'6px', display:'inline-block', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em' }}>
                                {hecho ? 'Registered' : 'Pending'}
                            </span>
                        </div>
                        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'7px', display:'flex', borderRadius:'8px' }}><X size={18}/></button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ overflowY:'auto', flex:1, padding:'20px 24px', background:C.bgCard }}>

                    {modoLectura && (
                        <div style={{ background:C.bgDark, border:`1px solid ${C.borderMid}`, padding:'10px 14px', marginBottom:'16px', fontSize:'11px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', display:'flex', alignItems:'center', gap:'8px' }}>
                            🔒 RECORD COMPLETED — Press "Delete record" to modify the data
                        </div>
                    )}

                    {/* Operario + Fecha */}
                    <SectionTitle icon={<User size={13}/>} title={'Execution record'} C={C} />
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
                        <Campo label={'Operators (initials)'} icon={<User/>} value={operario} onChange={setOperario} placeholder={'e.g. BLL, JMR, AL'} upper maxLen={30} C={C} />
                        <div>
                            <label style={mLS}><Calendar size={11} style={{marginRight:4}}/>{'Completion date'}</label>
                            <input type="date" value={fechaParaInput(fecha)} onChange={e=>setFecha(formatearFecha(e.target.value))} style={{ ...mIS, colorScheme: C.bg === '#1a1c1e' ? 'dark' : 'light' }}/>
                        </div>
                    </div>

                    {/* Component data + scan */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                        <SectionTitle icon={<Package size={13}/>} title={'Component data'} C={C} />
                        <EscanearBtn onScan={escanearEtiqueta} escaneando={escaneando} C={C}/>
                    </div>
                    <ScanPanel preview={scanPreview} result={scanResult} error={scanError} escaneando={escaneando} tipo={TIPO.COMPONENTE} onAceptar={aceptarCampo} onAplicarTodos={aplicarScanResult} onDescartar={()=>{setScanResult(null);setScanPreview(null);setScanError('');setScanMismatch(null);setAplicados(new Set());}} mismatch={scanMismatch} aplicados={aplicados} onRechazar={rechazarCampo} C={C}/>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom: camposFab.length > 0 ? '12px' : '20px' }}>
                        <Campo label={'Item No.'}       icon={<Hash/>}    value={item}       onChange={setItem}       placeholder="Ej: 10023456" mono C={C} />
                        <Campo label={'Serial Number'}  icon={<Hash/>}    value={serial}     onChange={setSerial}     placeholder="Ej: SN-2024-00847" mono C={C} />
                        <Campo label={'Manufacturer'}   icon={<Package/>} value={fabricante} onChange={setFabricante} placeholder="Ej: Vestas" C={C} />
                    </div>
                    {camposFab.length > 0 && (
                        <>
                            <div style={{ fontSize:'9px', color: C.orange, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                                <span style={{ flex:1, height:'1px', background: C.border }}/>
                                {detectarFabricante(proyectoModelo || fabricante)?.toUpperCase()} SPECIFIC FIELDS
                                <span style={{ flex:1, height:'1px', background: C.border }}/>
                            </div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
                                {camposFab.map(campo => (
                                    <div key={campo}>
                                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color: C.orange, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}># {campo}</label>
                                        <input type="text"
                                            value={campo === 'VUI' ? vui : (camposExtra[campo] || '')}
                                            onChange={e => campo === 'VUI' ? setVui(e.target.value) : setCamposExtra(prev => ({ ...prev, [campo]: e.target.value }))}
                                            readOnly={modoLectura} placeholder={`e.g. ${campo}...`}
                                            style={{ width:'100%', padding:'8px 10px', boxSizing:'border-box', background: modoLectura ? C.bgDark : C.bgCard, border:`1px solid ${C.borderMid}`, color: C.text, fontSize:'13px', fontFamily:'monospace', outline:'none', borderRadius:'6px' }}/>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Notes */}
                    <SectionTitle icon={<FileText size={13}/>} title={'Notes'} C={C} />
                    <textarea value={notas} onChange={e=>setNotas(e.target.value)} placeholder={'Incidents, special conditions, applied torques...'} rows={3}
                        style={{ ...mIS, resize:'vertical', lineHeight:'1.5', width:'100%', boxSizing:'border-box', marginBottom:'20px' }}/>

                    {/* Photos */}
                    <SectionTitle icon={<Camera size={13}/>} title={`Component photos (${fotos.length})`} C={C} />
                    <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', padding:'10px', border:`2px dashed ${C.borderMid}`, cursor:'pointer', background:C.bgDark, marginBottom:'14px', color:C.textDim, fontWeight:600, fontSize:'0.82rem', transition:'all 0.2s' }}>
                        <ImagePlus size={16} color={C.orange}/>
                        <span>{'Add photo'}</span>
                        <input type="file" accept="image/*" multiple style={{ display:'none' }}
                            onChange={async e => {
                                const files = Array.from(e.target.files);
                                for (const file of files) {
                                    const { dataUrl, blob } = await comprimirImagen(file);
                                    const fotoId = Date.now()+Math.random();
                                    setFotos(prev => [...prev, { id: fotoId, src: dataUrl, desc: '' }]);
                                    subirFotoSupabase(blob, maquinaId).then(url => {
                                        setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, src: url } : f));
                                    }).catch(() => {});
                                }
                                e.target.value = '';
                            }}
                        />
                    </label>
                    {fotos.length > 0 && (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))', gap:'10px', marginBottom:'20px' }}>
                            {fotos.map((foto, fi) => (
                                <div key={foto.id} style={{ overflow:'hidden', border:`1px solid ${C.border}`, background:C.bgDark, borderRadius:'4px' }}>
                                    <div style={{ position:'relative', paddingTop:'75%', background:C.bgDark }}>
                                        <img src={foto.src} alt={foto.desc||`Foto ${fi+1}`} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}/>
                                        <button onClick={() => setFotos(prev => prev.filter(f => f.id !== foto.id))} style={{ position:'absolute', top:'5px', right:'5px', background:'rgba(239,68,68,0.9)', border:'none', color:'white', width:'24px', height:'24px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, borderRadius:'50%' }}>
                                            <Trash size={12}/>
                                        </button>
                                    </div>
                                    <div style={{ padding:'6px 8px' }}>
                                        <input type="text" value={foto.desc} onChange={e => setFotos(prev => prev.map(f => f.id===foto.id ? {...f, desc: e.target.value} : f))} placeholder="Description..." style={{ width:'100%', border:'none', background:'transparent', fontSize:'11px', color:C.textDim, outline:'none', padding:0, boxSizing:'border-box' }}/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.border}`, flexShrink:0, background:C.bgDark, display:'flex', gap:'10px' }}>
                    {modoLectura ? (
                        <>
                            {!esTecnico && !soloLectura && (
                            <button onClick={handleEliminarRegistro} style={{ flex:1, padding:'12px', background:'transparent', border:`2px solid #c0392b`, color:'#c0392b', cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.08em', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', borderRadius:'8px' }}>
                                🗑 DELETE RECORD
                            </button>
                            )}
                            <button onClick={onClose} style={{ padding:'12px 20px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                Close
                            </button>
                        </>
                    ) : (
                        <button onClick={handleGuardar} disabled={soloLectura} title={soloLectura ? MSG_TRIAL_EXPIRED : undefined} style={{ opacity: soloLectura ? 0.4 : 1, flex:1, padding:'14px', border:'none', background:C.orange, color:C.bgDark, cursor:'pointer', fontWeight:700, fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                            <ClipboardList size={16}/> {'Save'}
                        </button>
                    )}
                </div>
            </div>
            <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
    );
}

function ModalBoltEntry({ entry, C, proyectoModelo, onSave, onDelete, esTecnico, maquinaId, onClose }) {
    const { soloLectura } = useTech();
    const [item, setItem]               = useState(entry.item || '');
    const [serial, setSerial]           = useState(entry.serial || '');
    const [fabricante, setFabricante]   = useState(entry.fabricante || '');
    const [boltSize, setBoltSize]       = useState(entry.boltSize || '');
    const [batch, setBatch]             = useState(entry.batch || '');
    const [openingTime, setOpeningTime] = useState(entry.openingTime || '');
    const [openingDate, setOpeningDate] = useState(entry.openingDate || '');
    const [notes, setNotes]             = useState(entry.notes || '');
    const [vui, setVui]                 = useState(entry.vui || '');
    const [camposExtra, setCamposExtra] = useState(entry.camposExtra || {});
    const [fotos, setFotos]             = useState(entry.fotos || []);

    const [escaneando, setEscaneando]   = useState(false);
    const [scanPreview, setScanPreview] = useState(null);
    const [scanResult, setScanResult]   = useState(null);
    const [scanError, setScanError]     = useState('');
    const [scanMismatch, setScanMismatch] = useState(null);
    const [aplicadosBolt, setAplicadosBolt] = useState(new Set());

    const modoLectura = entry.registrado;
    const camposFab = getCamposFabricante(proyectoModelo);

    const CAMPOS_LABELS = {
        tornItem: 'Item No.', tornSerial: 'Serial',
        tornFabricante: 'Manufacturer', tornBatch: 'Batch / ID Mark',
        metrica: 'Bolt Size', vui: 'VUI',
    };

    const handleSave = () => {
        if (!item && !serial && !fabricante && !boltSize && !batch) {
            alert('Please fill in at least one field before saving.');
            return;
        }
        onSave({ item, serial, fabricante, boltSize, batch, openingTime, openingDate, notes, vui, camposExtra, fotos });
        onClose();
    };

    const escanearEtiqueta = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setScanError(''); setScanResult(null); setScanMismatch(null); setAplicadosBolt(new Set()); setEscaneando(true);
        const previewReader = new FileReader();
        previewReader.onload = ev => setScanPreview(ev.target.result);
        previewReader.readAsDataURL(files[0]);

        const esVestas = /vestas|v\d{3}/i.test(proyectoModelo);
        const prompt = esVestas
            ? `You are analyzing a Vestas wind turbine BOLTING label (fasteners/bolts/studs).
Extract ONLY these fields:
- "tornItem": look for "Item No", "Item", "Generic Item", "Article No"
- "tornSerial": look for "Manu S/N", "Manuf S/N", "Serial Number", "S/N". IMPORTANT: Copy the COMPLETE value exactly as it appears, including all characters. Example: "HTF 84R = 18 & W12668" → tornSerial is "HTF 84R = 18 & W12668". Do NOT split or take only part of it.
- "tornFabricante": look for "Supplier", "Supplier / Manufacture", "Manufacturer". If it starts with a numeric code (e.g. "509946- Hiten Fasteners Pvt Ltd."), strip the code and return only the company name.
- "tornBatch": look for "Batch", "Batch No", "ID Mark", "Heat No", "Lot No"
- "metrica": look for bolt size pattern MXXxYYY in "Description", "COMPONENT" or part name. Example: "FL.B TOP M30x270 ISO4014" → "M30x270"
- "vui": look for "VUI", "VUI No", "Traceability", "Traceability No"
Return ONLY JSON: {"tornItem":"","tornSerial":"","tornFabricante":"","tornBatch":"","metrica":"","vui":""}`
            : `You are analyzing a wind turbine BOLTING label. Extract ONLY: "tornItem" (Item/Part No), "tornSerial" (Serial/S/N, copy complete value), "tornFabricante" (Manufacturer/brand), "tornBatch" (Batch/ID Mark/Heat No/Lot No), "metrica" (bolt size MXXxYYY from description or part name). Return ONLY JSON: {"tornItem":"","tornSerial":"","tornFabricante":"","tornBatch":"","metrica":""}`;

        const normalizar = (parsed) => {
            const r = { ...parsed };
            Object.keys(r).forEach(k => {
                if (!r[k] || r[k] === 'N/A' || r[k] === 'n/a' || r[k] === '-') r[k] = '';
                if (typeof r[k] === 'string') r[k] = r[k].trim();
            });
            if (r.metrica) {
                const match = r.metrica.match(/M\d+[xX]\d+/i);
                if (match) r.metrica = match[0].replace('X', 'x');
            }
            return r;
        };

        try {
            let acumulado = {};
            const conflictos = [];

            for (const file of files) {
                const { base64, dataUrl: fotoDataUrl, blob } = await comprimirImagen(file);
                const fotoId = Date.now() + Math.random();
                setFotos(prev => [...prev, { id: fotoId, src: fotoDataUrl, desc: 'Label scan', esEtiqueta: true }]);
                subirFotoSupabase(blob, maquinaId).then(url => {
                    setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, src: url } : f));
                }).catch(() => {});
                const response = await fetch('/api/scan', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        model: 'claude-haiku-4-5-20251001', max_tokens: 400,
                        messages: [{ role: 'user', content: [
                            { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: base64 } },
                            { type: 'text', text: prompt }
                        ]}]
                    })
                });
                if (!response.ok) throw new Error('API Error ' + response.status);
                const data = await response.json();
                const text = data.content?.[0]?.text || '';
                const clean = text.replace(/```json|```/g, '').trim();
                let parsed;
                try { parsed = JSON.parse(clean); } catch { parsed = {}; }
                const r = normalizar(parsed);

                Object.entries(r).forEach(([k, v]) => {
                    if (!v) return;
                    if (acumulado[k] && acumulado[k] !== v) {
                        conflictos.push({ key: k, label: CAMPOS_LABELS[k] || k, anterior: acumulado[k], nuevo: v });
                    }
                    if (v) acumulado[k] = v;
                });
            }

            const tieneData = Object.values(acumulado).some(v => v);
            if (!tieneData) {
                setScanError('Could not read label. Please fill in manually.');
            } else {
                // Detectar conflictos con valores ya aplicados en el formulario
                const formActual = { tornItem: item, tornSerial: serial, tornFabricante: fabricante, metrica: boltSize, tornBatch: batch, vui };
                Object.entries(formActual).forEach(([k, formVal]) => {
                    if (formVal && acumulado[k] && formVal !== acumulado[k] && !conflictos.find(c => c.key === k)) {
                        conflictos.push({ key: k, label: CAMPOS_LABELS[k] || k, anterior: formVal, nuevo: acumulado[k] });
                    }
                });
                setScanResult(acumulado);
                if (conflictos.length > 0) setScanMismatch({ campos: conflictos });
            }
        } catch (err) {
            setScanError('Could not read label. Please fill in manually.');
        } finally {
            setEscaneando(false);
            e.target.value = '';
        }
    };

    const aceptarCampo = (key) => {
        if (!scanResult?.[key]) return;
        const setters = {
            tornItem: setItem, tornSerial: setSerial,
            tornFabricante: setFabricante, tornBatch: setBatch,
            metrica: setBoltSize, vui: setVui,
        };
        if (setters[key]) setters[key](scanResult[key]);
        setAplicadosBolt(prev => new Set([...prev, key]));
    };

    const rechazarCampoBolt = (key) => {
        const clearers = {
            tornItem: () => setItem(''), tornSerial: () => setSerial(''),
            tornFabricante: () => setFabricante(''), tornBatch: () => setBatch(''),
            metrica: () => setBoltSize(''), vui: () => setVui(''),
        };
        if (clearers[key]) clearers[key]();
        setAplicadosBolt(prev => { const n = new Set(prev); n.delete(key); return n; });
    };

    const camposConValor = scanResult ? Object.entries(CAMPOS_LABELS).filter(([k]) => scanResult[k]) : [];

    return (
        <div style={{ position:'fixed', inset:0, zIndex:10000, background: C.overlay, backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
            <div style={{ background: C.bgCard, width:'100%', maxWidth:'620px', maxHeight:'90vh', display:'flex', flexDirection:'column', border:`1px solid ${C.borderMid}` }}>

                {/* Header */}
                <div style={{ background: C.bgDark, borderBottom:`3px solid ${C.orange}`, padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
                    <div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'18px', fontWeight:900, color: C.text, textTransform:'uppercase' }}>
                            🔩 {entry.connectionName}
                        </div>
                        {modoLectura && <div style={{ fontSize:'11px', color:'#86efac', marginTop:'2px', fontWeight:700 }}>✓ REGISTERED</div>}
                    </div>
                    <button onClick={onClose} style={{ background:'none', border:'none', color: C.textMuted, cursor:'pointer', fontSize:'20px' }}>✕</button>
                </div>

                {/* Scrollable content */}
                <div style={{ overflowY:'auto', flex:1, padding:'16px 20px' }}>

                    {/* Scan Label */}
                    {!modoLectura && (
                        <div style={{ marginBottom:'14px' }}>
                            <label style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'6px 14px', background: escaneando ? C.bgDark : 'linear-gradient(135deg,#7c3aed,#a855f7)', color:'white', cursor: escaneando ? 'wait' : 'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'12px', textTransform:'uppercase', opacity: escaneando ? 0.7 : 1 }}>
                                {escaneando ? '⏳ Scanning...' : '📷 Scan Label'}
                                <input type="file" accept="image/*" capture="environment" multiple style={{display:'none'}} onChange={escanearEtiqueta} disabled={escaneando}/>
                            </label>
                        </div>
                    )}

                    {/* Scan panel */}
                    {(scanPreview || scanResult || scanError) && (
                        <div style={{ background:'#faf5ff', border:'2px solid #a855f7', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                    {scanPreview && <img src={scanPreview} alt="" style={{ width:'40px', height:'30px', objectFit:'cover', borderRadius:'4px' }}/>}
                                    <span style={{ fontSize:'11px', fontWeight:700, color:'#7c3aed', textTransform:'uppercase' }}>
                                        {escaneando ? 'Analysing...' : scanError ? '⚠️ Error' : '✨ Data detected'}
                                    </span>
                                </div>
                                <button onClick={() => { setScanResult(null); setScanPreview(null); setScanError(''); setScanMismatch(null); }} style={{ background:'none', border:'none', color:'#94a3b8', cursor:'pointer' }}>✕</button>
                            </div>
                            {scanError && <div style={{ color:'#dc2626', fontSize:'12px' }}>{scanError}</div>}
                            {scanMismatch && scanMismatch.campos.length > 0 && !escaneando && (
                                <div style={{ background:'rgba(234,179,8,0.12)', border:'1px solid #ca8a04', borderRadius:'6px', padding:'9px 11px', marginBottom:'10px' }}>
                                    <div style={{ fontSize:'10px', fontWeight:700, color:'#fbbf24', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' }}>⚠️ Conflict between images — last value will be used</div>
                                    {scanMismatch.campos.map(c => (
                                        <div key={c.key} style={{ fontSize:'11px', marginBottom:'3px', fontFamily:'monospace' }}>
                                            <span style={{ color:'#9ca3af' }}>{c.label}: </span>
                                            <span style={{ textDecoration:'line-through', color:'#6b7280' }}>{c.anterior}</span>
                                            <span style={{ color:'#fbbf24' }}> → {c.nuevo}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {scanResult && camposConValor.length > 0 && (
                                <>
                                    {camposConValor.map(([key, label]) => {
                                        const usado = aplicadosBolt.has(key);
                                        return (
                                            <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'white', border:`1px solid ${usado ? '#bbf7d0' : '#e9d5ff'}`, borderRadius:'6px', padding:'7px 10px', marginBottom:'5px' }}>
                                                <div>
                                                    <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>{label}</div>
                                                    <div style={{ fontSize:'12px', color:'#1e293b', fontFamily:'monospace', fontWeight:700 }}>{scanResult[key]}</div>
                                                </div>
                                                <button onClick={() => usado ? rechazarCampoBolt(key) : aceptarCampo(key)} style={{ padding:'4px 10px', background: usado ? '#16a34a' : '#7c3aed', color:'white', border:'none', cursor:'pointer', fontWeight:700, fontSize:'11px', borderRadius:'4px' }}>
                                                    {usado ? '✓ USED' : '✓ USE'}
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <button onClick={() => { camposConValor.forEach(([k]) => aceptarCampo(k)); setScanResult(null); setScanPreview(null); setScanMismatch(null); setAplicadosBolt(new Set()); }} style={{ width:'100%', padding:'6px', background:'#f5f3ff', color:'#7c3aed', border:'1px solid #ddd6fe', cursor:'pointer', fontWeight:700, fontSize:'11px', borderRadius:'4px' }}>✓ USE ALL</button>
                                </>
                            )}
                        </div>
                    )}

                    {/* Read-only banner */}
                    {modoLectura && (
                        <div style={{ background: C.bgDark, border:`1px solid ${C.borderMid}`, padding:'8px 12px', marginBottom:'12px', fontSize:'11px', color: C.textDim, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                            🔒 RECORD COMPLETED — Press "Delete Record" to modify
                        </div>
                    )}

                    {/* Fixed fields */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                        {[
                            ['Item No.', item, setItem, 'e.g. 29204123', true],
                            ['Serial', serial, setSerial, 'e.g. 105022', true],
                            ['Manufacturer', fabricante, setFabricante, 'e.g. Cooper & Turner', false],
                            ['Bolt Size', boltSize, setBoltSize, 'e.g. M64x525', true],
                            ['Batch / ID Mark', batch, setBatch, 'e.g. GN1', true],
                        ].map(([label, val, setter, placeholder, mono]) => (
                            <div key={label}>
                                <label style={{ display:'block', fontSize:'10px', fontWeight:700, color: C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}># {label}</label>
                                <input type="text" value={val} onChange={e => setter(e.target.value)} readOnly={modoLectura} placeholder={modoLectura ? '' : placeholder} style={{ width:'100%', padding:'7px 8px', boxSizing:'border-box', background: modoLectura ? C.bgDark : C.bgCard, border:`1px solid ${C.borderMid}`, color: C.text, fontSize:'12px', fontFamily: mono ? 'monospace' : 'inherit' }}/>
                            </div>
                        ))}
                    </div>
                    {/* Opening Date & Time */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                        <div>
                            <label style={{ display:'block', fontSize:'10px', fontWeight:700, color: C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}># Opening Date</label>
                            <input type="date" value={openingDate} onChange={e => setOpeningDate(e.target.value)} readOnly={modoLectura} style={{ width:'100%', padding:'7px 8px', boxSizing:'border-box', background: modoLectura ? C.bgDark : C.bgCard, border:`1px solid ${C.borderMid}`, color: C.text, fontSize:'12px', colorScheme: C.bg === '#1a1c1e' ? 'dark' : 'light' }}/>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:'10px', fontWeight:700, color: C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}># Opening Time</label>
                            <div style={{ display:'flex', gap:'6px' }}>
                                <input type="time" value={openingTime} onChange={e => setOpeningTime(e.target.value)} readOnly={modoLectura} style={{ flex:1, padding:'7px 8px', background: modoLectura ? C.bgDark : C.bgCard, border:`1px solid ${C.borderMid}`, color: C.text, fontSize:'12px', colorScheme: C.bg === '#1a1c1e' ? 'dark' : 'light' }}/>
                                {!modoLectura && <button type="button" onClick={() => { const n = new Date(); setOpeningTime(n.toTimeString().slice(0,5)); const y = n.getFullYear(), mo = String(n.getMonth()+1).padStart(2,'0'), d = String(n.getDate()).padStart(2,'0'); setOpeningDate(`${y}-${mo}-${d}`); }} style={{ padding:'0 10px', background: C.orange, color: C.bgDark, border:'none', cursor:'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'11px' }}>NOW</button>}
                            </div>
                        </div>
                    </div>

                    {/* Manufacturer-specific fields */}
                    {camposFab.length > 0 && (
                        <>
                            <div style={{ fontSize:'9px', color: C.orange, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginTop:'4px', marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                                <span style={{ flex:1, height:'1px', background: C.border }}/>
                                {detectarFabricante(proyectoModelo)?.toUpperCase()} SPECIFIC FIELDS
                                <span style={{ flex:1, height:'1px', background: C.border }}/>
                            </div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                                {camposFab.map(campo => (
                                    <div key={campo}>
                                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color: C.orange, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}># {campo}</label>
                                        <input
                                            type="text"
                                            value={campo === 'VUI' ? vui : (camposExtra[campo] || '')}
                                            onChange={e => campo === 'VUI' ? setVui(e.target.value) : setCamposExtra(prev => ({ ...prev, [campo]: e.target.value }))}
                                            readOnly={modoLectura}
                                            placeholder={modoLectura ? '' : `e.g. ${campo}...`}
                                            style={{ width:'100%', padding:'7px 8px', boxSizing:'border-box', background: modoLectura ? C.bgDark : C.bgCard, border:`1px solid ${C.borderMid}`, color: C.text, fontSize:'12px', fontFamily:'monospace' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Notes */}
                    <div style={{ marginBottom:'12px' }}>
                        <label style={{ display:'block', fontSize:'10px', fontWeight:700, color: C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>Notes</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} readOnly={modoLectura} placeholder={modoLectura ? '' : 'Observations, special conditions...'} rows={2} style={{ width:'100%', padding:'7px 8px', boxSizing:'border-box', background: modoLectura ? C.bgDark : C.bgCard, border:`1px solid ${C.borderMid}`, color: C.text, fontSize:'12px', resize:'vertical' }}/>
                    </div>

                    {/* Photos */}
                    <div style={{ marginBottom:'12px' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
                            <label style={{ fontSize:'10px', fontWeight:700, color: C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', display:'flex', alignItems:'center', gap:'5px' }}>
                                <Camera size={11}/> Photos ({fotos.length})
                            </label>
                            {!modoLectura && (
                                <label style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'4px 10px', cursor:'pointer', background: C.bgDark, border:`1px solid ${C.borderMid}`, color: C.textMuted, fontSize:'10px', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase' }}>
                                    <ImagePlus size={11}/> Add photo
                                    <input type="file" accept="image/*" multiple style={{ display:'none' }} onChange={async e => { const files = Array.from(e.target.files); for (const file of files) { const { dataUrl, blob } = await comprimirImagen(file); const fotoId = Date.now() + Math.random(); setFotos(prev => [...prev, { id: fotoId, src: dataUrl, desc: '' }]); subirFotoSupabase(blob, maquinaId).then(url => { setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, src: url } : f)); }).catch(() => {}); } e.target.value = ''; }}/>
                                </label>
                            )}
                        </div>
                        {fotos.length > 0 && (
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:'8px' }}>
                                {fotos.map((foto) => (
                                    <div key={foto.id} style={{ position:'relative', background: C.bgDark, border:`1px solid ${C.borderMid}` }}>
                                        <img src={foto.src} alt="" style={{ width:'100%', height:'80px', objectFit:'cover', display:'block' }}/>
                                        {!modoLectura && (
                                            <button onClick={() => setFotos(prev => prev.filter(f => f.id !== foto.id))} style={{ position:'absolute', top:'3px', right:'3px', background:'rgba(220,38,38,0.9)', border:'none', color:'white', width:'20px', height:'20px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, fontSize:'11px' }}>✕</button>
                                        )}
                                        <input type="text" value={foto.desc} onChange={e => setFotos(prev => prev.map(f => f.id===foto.id ? {...f, desc: e.target.value} : f))} readOnly={modoLectura} placeholder="Caption..." style={{ width:'100%', border:'none', borderTop:`1px solid ${C.borderMid}`, background: C.bgDark, color: C.textDim, fontSize:'10px', padding:'3px 5px', boxSizing:'border-box', outline:'none' }}/>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:'8px', justifyContent:'flex-end', flexShrink:0 }}>
                    {!modoLectura ? (
                        <button type="button" onClick={handleSave} disabled={soloLectura} title={soloLectura ? MSG_TRIAL_EXPIRED : undefined} style={{ opacity: soloLectura ? 0.4 : 1, padding:'10px 24px', background: C.orange, color: C.bgDark, border:'none', cursor:'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'14px', textTransform:'uppercase' }}>
                            ✓ SAVE RECORD
                        </button>
                    ) : (
                        !esTecnico && !soloLectura && (
                            <button onClick={onDelete} style={{ padding:'10px 24px', background:'transparent', border:`2px solid #c0392b`, color:'#c0392b', cursor:'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'14px', textTransform:'uppercase' }}>
                                🗑 DELETE RECORD
                            </button>
                        )
                    )}
                    <button onClick={onClose} style={{ padding:'10px 24px', background: C.bgDark, border:`1px solid ${C.borderMid}`, color: C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'13px', textTransform:'uppercase' }}>CANCEL</button>
                </div>
            </div>
        </div>
    );
}

function BoltRegisterCard({ entry, C, onOpen }) {
    const modoLectura = entry.registrado;
    return (
        <div onClick={onOpen} style={{ background: modoLectura ? '#f0fdf4' : C.bgCard, border:`1px solid ${modoLectura ? '#86efac' : C.border}`, borderLeft:`4px solid ${modoLectura ? '#5a9e45' : C.orange}`, marginBottom:'10px', cursor:'pointer' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'15px', fontWeight:700, color: C.text, textTransform:'uppercase' }}>{entry.connectionName}</span>
                    {modoLectura && <span style={{ fontSize:'9px', background:'#5a9e45', color:'white', padding:'2px 7px', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase' }}>REGISTERED</span>}
                    {!modoLectura && entry.boltSize && <span style={{ fontSize:'10px', color: C.orange, fontFamily:'monospace', fontWeight:700 }}>{entry.boltSize}</span>}
                    {entry.fotos?.length > 0 && <span style={{ fontSize:'9px', background:'#9333ea', color:'white', padding:'2px 7px', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase' }}>📸 {entry.fotos.length}</span>}
                </div>
                <span style={{ color: C.textDim, fontSize:'14px' }}>▶</span>
            </div>
            {modoLectura && (
                <div style={{ padding:'0 16px 10px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'4px 12px' }}>
                    {[['Item', entry.item],['Serial', entry.serial],['Manufacturer', entry.fabricante],
                      ['Bolt Size', entry.boltSize],['Batch', entry.batch],['Opening', (() => { if (!entry.openingTime) return ''; if (!entry.openingDate) return entry.openingTime; const p = entry.openingDate.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]} ${entry.openingTime}` : `${entry.openingDate} ${entry.openingTime}`; })()],['VUI', entry.vui]
                    ].filter(([,v]) => v).map(([l,v]) => (
                        <div key={l}>
                            <div style={{ fontSize:'9px', color: C.textDim, fontWeight:700, textTransform:'uppercase' }}>{l}</div>
                            <div style={{ fontSize:'11px', color: C.text, fontFamily:'monospace', fontWeight:600 }}>{v}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function App({ esTecnico = false, proyectoTech = null }) {
    const _fontC = "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif";
    const [theme, setTheme]                               = useState(() => localStorage.getItem('windmanager_theme') || 'dark');
    const C = THEMES[theme];
    const [view, setView] = useState(esTecnico ? 'map' : 'home');
    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject]               = useState(null);
    useEffect(() => {
    if (esTecnico && proyectoTech) {
        const proyectoMapeado = {
            ...proyectoTech,
            nombre_parque: proyectoTech.nombre || proyectoTech.nombre_parque || '',
            componentesPersonalizados: proyectoTech.componentes_personalizados || [],
            herramientas: { ...herramientasDefault(), ...(proyectoTech.herramientas || {}) },
            maquinas: (proyectoTech.maquinas || []).map(m => ({
                ...m,
                tareas: m.tareas || [],
                herramientas: { ...herramientasDefault(), ...(m.herramientas || {}) },
                componentesExtra: m.componentes_extra || componentesExtraDefault(),
                boltRegister: m.bolt_register || [],
            }))
        };
        setProjects([proyectoMapeado]);
        setActiveProject(proyectoMapeado);
    } else {
        cargarProyectosSupabase().then(data => setProjects(data));
    }
}, []);
    const [nombre, setNombre]                             = useState('');
    const [modeloGeneral, setModeloGeneral]               = useState('');
    const [numMaquinasTotal, setNumMaquinasTotal]         = useState(1);
    const [numTramosTorre, setNumTramosTorre]             = useState(4);
    const [dtAparte, setDtAparte] = useState(false);
 
    const [modalEnlace, setModalEnlace] = useState(null);
    const [direccionInput, setDireccionInput]             = useState('');
    const [sugerencias, setSugerencias]                   = useState([]);
    const [direccionCompleta, setDireccionCompleta]       = useState('');
    const [buscandoDireccion, setBuscandoDireccion]       = useState(false);
    const [maquinasUbicadas, setMaquinasUbicadas]         = useState([]);
    const [centroMapa, setCentroMapa]                     = useState([46.18, 0.40]);
    const [maquinaSeleccionada, setMaquinaSeleccionada]   = useState(null);
    const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
    const [guardadoMsg, setGuardadoMsg]                   = useState('');
    const [modalData, setModalData]                       = useState(null);
    const [scrollPositionAntes, setScrollPositionAntes]   = useState(0);
    const [viewAntes, setViewAntes]                       = useState(null);
    const [modalHerramientas, setModalHerramientas]       = useState(null);
    const [proyectoRegistro, setProyectoRegistro]         = useState(null);
    const [maquinaRegistroSel, setMaquinaRegistroSel]     = useState(null);
    const [modalCompExtra, setModalCompExtra]             = useState(null); // {maquinaId, compNombre}
    const [nuevoCompForm, setNuevoCompForm]               = useState(false);
    const [nuevoCompNombre, setNuevoCompNombre]           = useState('');
    const [tooltipMaquina, setTooltipMaquina]             = useState(false);
    const [hoveredSubKey, setHoveredSubKey]               = useState(null);
    const [modalOrigen, setModalOrigen]                   = useState(null); // null | 'registro'
    const [modalBoltRegister, setModalBoltRegister]       = useState(false);
    const [boltRegisterData, setBoltRegisterData]         = useState([]);
    const [boltEntryModal, setBoltEntryModal]             = useState(null); // { idx, entry }
    const [mapBottomSheetExpanded, setMapBottomSheetExpanded] = useState(false);
    const [esMobil, setEsMobil] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setEsMobil(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    const [herramientaSeleccionada, setHerramientaSeleccionada] = useState(null); // { herramienta, tipo: 'bomba'|'hidraulica'|'tensionador' }
    const [modoEdicion, setModoEdicion]                         = useState(false);
    const ignorarProximoClick                                    = useRef(false);
    const [parqueEditando, setParqueEditando]                   = useState(null);
    const [faseModalAbiertaId, setFaseModalAbiertaId]           = useState(null);
    const [fitBoundsCoords, setFitBoundsCoords]                 = useState(null);
    const [coordsTemp, setCoordsTemp]                           = useState({});
    const [maquinaExpandidaId, setMaquinaExpandidaId]           = useState(null);

    // ── Suscripción / trial ──
    const [suscripcion, setSuscripcion]                         = useState(null); // { status, trial_ends_at }
    const [subCargada, setSubCargada]                           = useState(esTecnico);
    const [bloqueadoPorServidor, setBloqueadoPorServidor]       = useState(false);
    const [avisoEscritura, setAvisoEscritura]                   = useState('');
    const cargarSuscripcion = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) { setSubCargada(true); return; }
        const { data, error } = await supabase
            .from('user_subscriptions')
            .select('status, trial_ends_at')
            .eq('user_id', session.user.id)
            .maybeSingle();
        if (error) console.error('Error cargando suscripción:', error);
        else setSuscripcion(data || null);
        setSubCargada(true);
    };
    useEffect(() => {
        if (esTecnico) return;
        cargarSuscripcion();
    }, []); // eslint-disable-line

    // Checkout de Stripe: type = 'subscription' | 'park'
    const iniciarCheckout = async (type, parkId) => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            if (!token) { setAvisoEscritura('You must be logged in to continue.'); return; }
            const resp = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ type, parkId }),
            });
            const json = await resp.json().catch(() => ({}));
            if (json?.url) { window.location.href = json.url; return; }
            setAvisoEscritura(json?.error || json?.message || 'Could not start checkout. Please try again.');
        } catch (err) {
            setAvisoEscritura(err?.message || 'Could not start checkout. Please try again.');
        }
    };

    // Retorno de Stripe: ?pago=ok / ?pago=cancelado
    useEffect(() => {
        if (esTecnico) return;
        const params = new URLSearchParams(window.location.search);
        const pago = params.get('pago');
        if (!pago) return;
        params.delete('pago');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
        if (pago !== 'ok') return;
        setGuardadoMsg('');
        setAvisoEscritura('Payment received. It may take a few seconds to update.');
        const recargar = () => { cargarSuscripcion(); cargarProyectosSupabase().then(data => setProjects(data)); };
        const t1 = setTimeout(recargar, 4000);
        const t2 = setTimeout(recargar, 10000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []); // eslint-disable-line
    const trialFin = suscripcion?.trial_ends_at ? new Date(suscripcion.trial_ends_at) : null;
    const suscripcionActiva = suscripcion?.status === 'active';
    const trialVigente = !!trialFin && trialFin.getTime() > Date.now();
    const trialDiasRestantes = trialVigente ? Math.ceil((trialFin.getTime() - Date.now()) / 86400000) : 0;
    const trialCaducado = !!suscripcion && !suscripcionActiva && !trialVigente;
    const soloLectura = trialCaducado || bloqueadoPorServidor;
    const mostrarTrialRestante = !esTecnico && !!suscripcion && !suscripcionActiva && trialVigente;
    useEffect(() => {
        const handler = (e) => {
            const { codigo, msg } = e.detail || {};
            if (codigo === 'TRIAL_EXPIRED') setBloqueadoPorServidor(true);
            setGuardadoMsg('');
            setAvisoEscritura(esTecnico && codigo === 'TRIAL_EXPIRED'
                ? "This wind farm is in read-only mode (the owner's trial has ended). Your changes were not saved."
                : msg);
        };
        window.addEventListener('windmanager:write-error', handler);
        return () => window.removeEventListener('windmanager:write-error', handler);
    }, [esTecnico]);
    // Parque sin pagar (suscripción activa pero pagado=false): bloqueado para el manager
    const parqueSinPagar = (p) => !esTecnico && suscripcionActiva && !!p && p.pagado === false;
    const parqueAbierto = view === 'map' ? activeProject : (proyectoSeleccionado || activeProject);
    const parqueAbiertoSinPagar = parqueSinPagar(parqueAbierto);
    const lecturaParqueAbierto = soloLectura || parqueAbiertoSinPagar;
    // Devuelve true (y avisa) si estamos en solo lectura: usar al inicio de cada handler de escritura.
    const bloqueadoSoloLectura = () => {
        if (!soloLectura && parqueAbiertoSinPagar) { setAvisoEscritura(MSG_ACTIVATE_PARK); return true; }
        if (!soloLectura) return false;
        setAvisoEscritura(esTecnico
            ? 'This wind farm is in read-only mode. Your changes were not saved.'
            : MSG_TRIAL_EXPIRED);
        return true;
    };

    useEffect(() => { localStorage.setItem('windmanager_theme', theme); }, [theme]);
    useEffect(() => {
      document.body.style.background = C.bg;
      document.body.style.color = C.text;
      document.body.style.transition = 'background 0.2s ease, color 0.2s ease';
      document.body.style.fontFamily = "'Barlow', sans-serif";
      document.body.style.margin = '0';
    }, [C]);

    const getHerramientas = (nombreParque) => {
        const p = projects.find(pr => pr.nombre_parque === nombreParque);
        return { ...herramientasDefault(), ...(p?.herramientas || {}) };
    };
    const addHerramienta = (nombreParque, tipo, modelo, serial, fabricante, certificado, gaugeSerial, pressureGaugeModel, calibrationValid) => {
        if (bloqueadoSoloLectura()) return;
        const herramientasActuales = { ...herramientasDefault(), ...(getHerramientas(nombreParque) || {}) };
        const nuevasHerramientas = { ...herramientasActuales, [tipo]: [...(herramientasActuales[tipo] || []), { id: Date.now(), modelo: modelo||'', serial: serial||'', fabricante: fabricante||'', certificado: certificado||null, gaugeSerial: gaugeSerial||'', pressureGaugeModel: pressureGaugeModel||'', calibrationValid: calibrationValid||'' }] };
        setProjects(prev => prev.map(p => p.nombre_parque !== nombreParque ? p : { ...p, herramientas: nuevasHerramientas }));
        const proyecto = projects.find(p => p.nombre_parque === nombreParque);
        if (proyecto) {
            guardarProyectoSupabase({ ...proyecto, herramientas: nuevasHerramientas });
            proyecto.maquinas?.forEach(m => guardarMaquinaSupabase(m, proyecto.id));
        }
    };
    const deleteHerramienta = (nombreParque, tipo, id) => {
        if (bloqueadoSoloLectura()) return;
        const herramientasActuales = { ...herramientasDefault(), ...(getHerramientas(nombreParque) || {}) };
        const nuevasHerramientas = { ...herramientasActuales, [tipo]: (herramientasActuales[tipo] || []).filter(h => h.id !== id) };
        setProjects(prev => prev.map(p => p.nombre_parque !== nombreParque ? p : { ...p, herramientas: nuevasHerramientas }));
        const proyecto = projects.find(p => p.nombre_parque === nombreParque);
        if (proyecto) {
            guardarProyectoSupabase({ ...proyecto, herramientas: nuevasHerramientas });
            proyecto.maquinas?.forEach(m => guardarMaquinaSupabase(m, proyecto.id));
        }
    };
    const inicializarBoltRegister = (maquina) => {
        if (maquina.boltRegister?.length > 0) return maquina.boltRegister;
        const faseAprietes = maquina.tareas?.find(f =>
            f.nombre === 'Torque' || f.nombre === 'Aprietes'
        );
        if (!faseAprietes) return [];
        return faseAprietes.subtareas
            .filter(s => s._tipo === 'APRIETE' || s.nombre.toLowerCase().includes('flange') ||
                         s.nombre.toLowerCase().includes('hub') ||
                         s.nombre.toLowerCase().includes('blade') ||
                         s.nombre.toLowerCase().includes('anchor') ||
                         s.nombre.toLowerCase().includes('nacelle'))
            .map(s => ({
                id: Date.now() + Math.random(),
                connectionName: s.nombre,
                item: '', serial: '', fabricante: '',
                boltSize: '', batch: '', openingTime: '', openingDate: '', notes: '', vui: '', fotos: [],
                registrado: false,
            }));
    };

    useEffect(() => {
        if (modalBoltRegister && maquinaSeleccionada) {
            setBoltRegisterData(inicializarBoltRegister(maquinaSeleccionada));
        }
    }, [modalBoltRegister]); // eslint-disable-line

    const guardarBoltRegister = (nuevosData) => {
        if (bloqueadoSoloLectura()) return;
        const nuevosProjects = projects.map(p => {
            if (p.nombre_parque !== activeProject?.nombre_parque) return p;
            return {
                ...p,
                maquinas: p.maquinas.map(m => {
                    if (m.id !== maquinaSeleccionada?.id) return m;
                    return { ...m, boltRegister: nuevosData };
                })
            };
        });
        setProjects(nuevosProjects);
        const p = nuevosProjects.find(p => p.id === activeProject?.id);
        if (p) guardarProyectoSupabase(p);
        const m = p?.maquinas?.find(m => m.id === maquinaSeleccionada?.id);
        if (m) guardarMaquinaSupabase(m, p.id);
        setBoltRegisterData(nuevosData);
        setMaquinaSeleccionada(prev => ({ ...prev, boltRegister: nuevosData }));
    };

    const generarPDFBoltRegister = () => {
        const pt = PDF_T.en;
        const doc = new jsPDF();
        const hoy = new Date().toLocaleDateString('en-GB');
        const maq = maquinaSeleccionada;
        const parque = activeProject?.nombre_parque || '';
        const data = boltRegisterData;
        const registradas = data.filter(e => e.registrado).length;

        pdfPortada(doc,
            maq?.nombre?.toUpperCase() || 'WTG',
            'Bolt Register — Bolting record',
            [
                { valor: data.length,    label: 'Connections' },
                { valor: registradas,    label: 'Registered' },
                { valor: data.length - registradas, label: 'Pending' },
                { valor: data.length > 0 ? Math.round(registradas / data.length * 100) + '%' : '—', label: 'Progress' },
            ],
            data.length > 0 ? Math.round(registradas / data.length * 100) : null,
            parque,
            pt,
            activeProject
        );

        doc.addPage();
        pdfHeader(doc, `${parque.toUpperCase()} — ${maq?.nombre?.toUpperCase() || ''}`, 'BOLT REGISTER');

        const head = [['CONNECTION', 'ITEM No.', 'SERIAL', 'MANUFACTURER', 'BOLT SIZE', 'BATCH / ID MARK', 'OPENING', 'STATUS']];
        const body = data.map(e => [
            e.connectionName || '—',
            e.item || '—',
            e.serial || '—',
            e.fabricante || '—',
            e.boltSize || '—',
            e.batch || '—',
            e.openingTime || '—',
            e.registrado ? '✔ DONE' : 'PENDING',
        ]);

        autoTable(doc, {
            startY: 28,
            head,
            body,
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 2.5, font: 'helvetica', textColor: PDF.text },
            headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 7 },
            alternateRowStyles: { fillColor: PDF.light },
            columnStyles: {
                0: { cellWidth: 36, fontStyle: 'bold' },
                1: { cellWidth: 22, font: 'courier' },
                2: { cellWidth: 22, font: 'courier' },
                3: { cellWidth: 28 },
                4: { cellWidth: 18, font: 'courier' },
                5: { cellWidth: 22, font: 'courier' },
                6: { cellWidth: 16 },
                7: { cellWidth: 18, fontStyle: 'bold',
                     textColor: (cell) => cell?.raw === '✔ DONE' ? PDF.green : PDF.muted },
            },
            didParseCell: (data) => {
                if (data.column.index === 7) {
                    data.cell.styles.textColor = data.cell.raw === '✔ DONE' ? PDF.green : [180,130,0];
                }
            },
        });

        const conNotas = data.filter(e => e.registrado && e.notes);
        if (conNotas.length > 0) {
            doc.addPage();
            pdfHeader(doc, `${parque.toUpperCase()} — ${maq?.nombre?.toUpperCase() || ''}`, 'BOLT REGISTER — NOTES');
            let ny = 28;
            conNotas.forEach(e => {
                if (ny > 270) { doc.addPage(); pdfHeader(doc, `${parque.toUpperCase()} — ${maq?.nombre?.toUpperCase() || ''}`, 'BOLT REGISTER — NOTES'); ny = 28; }
                doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...PDF.dark);
                doc.text(e.connectionName.toUpperCase(), 14, ny); ny += 5;
                doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.muted);
                const lineas = doc.splitTextToSize(e.notes, 176);
                doc.text(lineas, 14, ny); ny += lineas.length * 4 + 6;
                doc.setDrawColor(...PDF.border); doc.setLineWidth(0.2); doc.line(14, ny - 2, 196, ny - 2);
            });
        }

        const conFotos = data.filter(e => e.fotos?.length > 0);
        if (conFotos.length > 0) {
            doc.addPage();
            pdfHeader(doc, `${parque.toUpperCase()} — ${maq?.nombre?.toUpperCase() || ''}`, 'BOLT REGISTER — PHOTOS');
            let fy = 28; let col = 0; let fx = 14;
            const fw = 85; const fh = 60;
            conFotos.forEach(entry => {
                entry.fotos.forEach(foto => {
                    if (!foto?.src) return;
                    if (col === 2) { col = 0; fx = 14; fy += fh + 18; }
                    if (fy + fh + 18 > 278) {
                        doc.addPage();
                        pdfHeader(doc, `${parque.toUpperCase()} — ${maq?.nombre?.toUpperCase() || ''}`, 'BOLT REGISTER — PHOTOS');
                        fy = 28; col = 0; fx = 14;
                    }
                    try { doc.addImage(foto.src, 'JPEG', fx, fy, fw, fh); } catch {}
                    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...PDF.orange);
                    doc.text(entry.connectionName.toUpperCase(), fx, fy + fh + 4);
                    doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...PDF.muted);
                    if (foto.desc) doc.text(foto.desc, fx, fy + fh + 8);
                    col++; fx += fw + 12;
                });
            });
        }

        pdfFooter(doc, `${parque} — ${maq?.nombre || ''}`, hoy);
        doc.save(`BOLT_REGISTER_${parque.replace(/\s+/g,'_')}_${(maq?.nombre||'WTG').replace(/\s+/g,'_')}_${hoy.replace(/\//g,'')}.pdf`);
    };

    const generarPDFHerramientas = (nombreParque) => {
        const pt = PDF_T.en;
        const doc = new jsPDF();
        const hoy = new Date().toLocaleDateString('en-GB');
        const _herr = getHerramientas(nombreParque);
        const herr = { bombas: _herr.bombas || [], hidraulicas: _herr.hidraulicas || [], tensionadores: _herr.tensionadores || [] };
        const proyectoObj = projects.find(p => p.nombre_parque === nombreParque);

        pdfPortada(doc,
            nombreParque.toUpperCase(),
            pt.subtitle_herramientas,
            [
                { valor: herr.bombas.length,        label: 'Pumps' },
                { valor: herr.hidraulicas.length,   label: 'Hyd. Wrenches' },
                { valor: herr.tensionadores.length, label: 'Tensioners' },
                { valor: herr.bombas.length + herr.hidraulicas.length + herr.tensionadores.length, label: 'Total' },
            ],
            null,
            nombreParque,
            pt,
            proyectoObj
        );

        doc.addPage();
        pdfHeader(doc, nombreParque.toUpperCase(), pt.herramientasTitulo);

        let startY = 28;

        if (herr.bombas.length > 0) {
            doc.setFillColor(...PDF.dark);
            doc.rect(14, startY, 182, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(...PDF.white);
            doc.text(pt.bombasSec, 18, startY + 5.5);
            doc.setTextColor(...PDF.orange);
            doc.text(`${herr.bombas.length} ${pt.unidades}`, 196, startY + 5.5, { align: 'right' });
            startY += 10;
            autoTable(doc, {
                startY,
                head: [['#', pt.modelo, pt.numSerie, 'PRESSURE GAUGE S/N', 'CALIBRATION VALID']],
                body: (herr.bombas || []).map((h, i) => {
                    const calExp = h?.calibrationValid && new Date(h.calibrationValid) < new Date();
                    return [i + 1, h?.modelo || '—', h?.serial || '—', h?.gaugeSerial || '—', h?.calibrationValid ? `${h.calibrationValid}${calExp?' ⚠️':''}` : '—'];
                }),
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 4, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
                headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 7 },
                alternateRowStyles: { fillColor: PDF.light },
                columnStyles: { 0: { cellWidth: 10, halign: 'center', textColor: PDF.muted }, 1: { cellWidth: 55, fontStyle: 'bold' }, 2: { cellWidth: 40, font: 'courier', fontSize: 8 }, 3: { cellWidth: 40, font: 'courier', fontSize: 8 }, 4: { cellWidth: 37, fontSize: 8 } },
            });
            startY = (doc.lastAutoTable?.finalY || 100) + 10;
        }

        if (herr.hidraulicas.length > 0) {
            if (startY > 240) { doc.addPage(); pdfHeader(doc, nombreParque.toUpperCase(), pt.herramientasTitulo); startY = 28; }
            doc.setFillColor(...PDF.dark);
            doc.rect(14, startY, 182, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(...PDF.white);
            doc.text(pt.hidraulicasSec, 18, startY + 5.5);
            doc.setTextColor(...PDF.orange);
            doc.text(`${herr.hidraulicas.length} ${pt.unidades}`, 196, startY + 5.5, { align: 'right' });
            startY += 10;
            autoTable(doc, {
                startY,
                head: [['#', pt.modelo, pt.numSerie]],
                body: (herr.hidraulicas || []).map((h, i) => [i + 1, h?.modelo || '—', h?.serial || '—']),
                theme: 'grid',
                styles: { fontSize: 9, cellPadding: 4, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
                headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 8 },
                alternateRowStyles: { fillColor: PDF.light },
                columnStyles: { 0: { cellWidth: 12, halign: 'center', textColor: PDF.muted }, 1: { cellWidth: 100, fontStyle: 'bold' }, 2: { cellWidth: 70, font: 'courier', fontSize: 9 } },
            });
            startY = (doc.lastAutoTable?.finalY || 100) + 10;
        }

        if (herr.tensionadores.length > 0) {
            if (startY > 240) { doc.addPage(); pdfHeader(doc, nombreParque.toUpperCase(), pt.herramientasTitulo); startY = 28; }
            doc.setFillColor(...PDF.dark);
            doc.rect(14, startY, 182, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(...PDF.white);
            doc.text(pt.tensionadoresSec, 18, startY + 5.5);
            doc.setTextColor(...PDF.orange);
            doc.text(`${herr.tensionadores.length} ${pt.unidades}`, 196, startY + 5.5, { align: 'right' });
            startY += 10;
            autoTable(doc, {
                startY,
                head: [['#', pt.modelo, pt.numSerie]],
                body: (herr.tensionadores || []).map((h, i) => [i + 1, h?.modelo || '—', h?.serial || '—']),
                theme: 'grid',
                styles: { fontSize: 9, cellPadding: 4, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
                headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 8 },
                alternateRowStyles: { fillColor: PDF.light },
                columnStyles: { 0: { cellWidth: 12, halign: 'center', textColor: PDF.muted }, 1: { cellWidth: 100, fontStyle: 'bold' }, 2: { cellWidth: 70, font: 'courier', fontSize: 9 } },
            });
        }

        if (herr.bombas.length === 0 && herr.hidraulicas.length === 0 && herr.tensionadores.length === 0) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...PDF.muted);
            doc.text(pt.noHerramientas, 105, 120, { align: 'center' });
        }

        pdfFooter(doc, nombreParque, hoy);
        doc.save(`WINDMANAGER_PRO_${nombreParque.replace(/\s+/g, '_')}_HERRAMIENTAS_${hoy.replace(/\//g, '')}.pdf`);
    };

    const generarPDFHerramientaIndividual = (nombreParque, herramienta, tipo) => {
        const pt = PDF_T.en;
        const doc = new jsPDF();
        const hoy = new Date().toLocaleDateString('en-GB');
        const tipoLabel = tipo === 'bomba' ? 'Hydraulic Pump' : tipo === 'hidraulica' ? 'Hydraulic Wrench' : 'Tensioner';

        const calExpPdf = herramienta.calibrationValid && new Date(herramienta.calibrationValid) < new Date();
        pdfPortada(doc,
            (herramienta.modelo || 'No model').toUpperCase(),
            tipoLabel,
            [
                { valor: herramienta.serial || '—', label: pt.numSerie },
                ...(tipo === 'bomba' && herramienta.gaugeSerial ? [{ valor: herramienta.gaugeSerial, label: 'Pressure Gauge S/N' }] : []),
                { valor: herramienta.fabricante || '—', label: 'Manufacturer' },
                { valor: herramienta.certificado ? 'Yes' : 'No', label: 'Certificate' },
                ...(herramienta.calibrationValid ? [{ valor: `${herramienta.calibrationValid}${calExpPdf ? ' (EXPIRED)' : ''}`, label: 'Calibration Valid' }] : []),
            ],
            null,
            nombreParque,
            pt
        );

        doc.addPage();
        pdfHeader(doc, nombreParque.toUpperCase(), tipoLabel);

        let y = 32;

        // Bloque de datos
        const calExpired = herramienta.calibrationValid && new Date(herramienta.calibrationValid) < new Date();
        const filas = [
            ['Model',           herramienta.modelo    || '—'],
            ['Serial Number',   herramienta.serial    || '—'],
            ...(tipo === 'bomba' && herramienta.gaugeSerial     ? [['Pressure Gauge S/N',    herramienta.gaugeSerial]] : []),
            ...(tipo === 'bomba' && herramienta.pressureGaugeModel ? [['Pressure Gauge Model', herramienta.pressureGaugeModel]] : []),
            ['Manufacturer',    herramienta.fabricante|| '—'],
            ['Type',            tipoLabel],
            ['Certificate',     herramienta.certificado ? 'Yes' : 'No'],
            ...(herramienta.calibrationValid ? [['Calibration Valid Until', `${herramienta.calibrationValid}${calExpired ? ' — EXPIRED' : ''}`]] : []),
        ];

        autoTable(doc, {
            startY: y,
            head: [['Campo', 'Valor']],
            body: filas,
            theme: 'grid',
            styles: { fontSize: 10, cellPadding: 5, textColor: PDF.text, lineColor: PDF.border, lineWidth: 0.2 },
            headStyles: { fillColor: PDF.dark, textColor: PDF.white, fontStyle: 'bold', fontSize: 9 },
            alternateRowStyles: { fillColor: PDF.light },
            columnStyles: { 0: { cellWidth: 60, fontStyle: 'bold', textColor: PDF.muted }, 1: { cellWidth: 122, font: 'courier' } },
        });

        pdfFooter(doc, nombreParque, hoy);
        const nombreArchivo = `WINDMANAGER_PRO_${nombreParque.replace(/\s+/g,'_')}_${(herramienta.modelo||'herramienta').replace(/\s+/g,'_')}_${hoy.replace(/\//g,'')}.pdf`;
        doc.save(nombreArchivo);
    };

    const añadirComponentePersonalizado = (nombreParque, nombreComp) => {
        if (bloqueadoSoloLectura()) return;
        const nombre = nombreComp.trim();
        if (!nombre) return;
        const proyecto = projects.find(p => p.nombre_parque === nombreParque);
        if (!proyecto) return;
        const proyectoActualizado = {
            ...proyecto,
            componentesPersonalizados: [...(proyecto.componentesPersonalizados || []), nombre],
            maquinas: proyecto.maquinas.map(m => ({
                ...m,
                componentesExtra: {
                    ...(m.componentesExtra || componentesExtraDefault()),
                    [nombre]: componenteExtraVacio(),
                }
            }))
        };
        setProjects(prev => prev.map(p => p.nombre_parque !== nombreParque ? p : proyectoActualizado));
        guardarProyectoSupabase(proyectoActualizado);
        proyectoActualizado.maquinas.forEach(m => guardarMaquinaSupabase(m, proyectoActualizado.id));
    };

    const guardarComponenteExtra = (nombreParque, maquinaId, compNombre, datos) => {
        if (bloqueadoSoloLectura()) return;
        const proyecto = projects.find(p => p.nombre_parque === nombreParque);
        const maquinaActual = proyecto?.maquinas?.find(m => m.id === maquinaId);
        const maquinaActualizada = maquinaActual ? {
            ...maquinaActual,
            componentesExtra: {
                ...(maquinaActual.componentesExtra || componentesExtraDefault()),
                [compNombre]: { ...(maquinaActual.componentesExtra?.[compNombre] || componenteExtraVacio()), ...datos }
            }
        } : null;
        setProjects(prev => prev.map(p => p.nombre_parque !== nombreParque ? p : {
            ...p, maquinas: p.maquinas.map(m => m.id !== maquinaId ? m : maquinaActualizada || m)
        }));
        if (maquinaActualizada && proyecto?.id) {
            guardarMaquinaSupabase(maquinaActualizada, proyecto.id);
        }
        mostrarMsgGuardado();
    };

    useEffect(() => {
        if (subCargada && !soloLectura) projects.forEach(p => { if (!parqueSinPagar(p)) guardarProyectoSupabase(p); });
        // Mantener activeProject sincronizado con projects (fuente de verdad)
        if (activeProject) {
            const updated = projects.find(p => p.nombre_parque === activeProject.nombre_parque);
            if (updated) setActiveProject(updated);
        }
        if (proyectoRegistro) {
            const updated = projects.find(p => p.nombre_parque === proyectoRegistro.nombre_parque);
            if (updated) {
                setProyectoRegistro(updated);
                if (maquinaRegistroSel) {
                    const updMaq = updated.maquinas.find(m => m.id === maquinaRegistroSel.id);
                    if (updMaq) setMaquinaRegistroSel(updMaq);
                }
            }
        }
    }, [projects]);
    const mostrarMsgGuardado = () => { setGuardadoMsg('✅ Saved'); setTimeout(()=>setGuardadoMsg(''),2000); };

    const calcularProgreso = (tareas) => {
        if (!tareas?.length) return 0;
        return Math.round(tareas.reduce((acc, fase) => {
            const h = fase.subtareas.filter(s=>s.hecho).length;
            return acc + (fase.peso * (fase.subtareas.length > 0 ? h/fase.subtareas.length : 0));
        }, 0));
    };

    const abrirModal = (maquinaId, faseId, subIdx) => {
        setScrollPositionAntes(window.scrollY);
        setViewAntes(view);
        setModalData({ maquinaId, faseId, subIdx });
    };

    const cerrarModal = () => {
        setModalData(null);
        if (viewAntes && viewAntes !== view) setView(viewAntes);
        setTimeout(() => { window.scrollTo({ top: scrollPositionAntes, behavior: 'instant' }); }, 50);
    };

    const abrirModalCompExtra = (compNombre) => {
        setScrollPositionAntes(window.scrollY);
        setViewAntes(view);
        setModalCompExtra({ compNombre });
    };

    const cerrarModalCompExtra = () => {
        setModalCompExtra(null);
        if (viewAntes && viewAntes !== view) setView(viewAntes);
        setTimeout(() => { window.scrollTo({ top: scrollPositionAntes, behavior: 'instant' }); }, 50);
    };

    const guardarSubtarea = (maquinaId, faseId, subIdx, datos) => {
        if (bloqueadoSoloLectura()) return;
        const datosFinales = (esTecnico && datos.hecho) ? { ...datos, locked: true } : datos;
        const p = JSON.parse(JSON.stringify(activeProject));
        const maq  = p.maquinas.find(m => m.id === maquinaId);
        const fase = maq.tareas.find(t => t.id === faseId);
        fase.subtareas[subIdx] = { ...fase.subtareas[subIdx], ...datosFinales };
        fase.hecho = fase.subtareas.every(s => s.hecho);
        const nuevos = projects.map(pr => pr.nombre_parque === p.nombre_parque ? p : pr);
        setActiveProject(p);
        setMaquinaSeleccionada(maq);
        setProjects(nuevos);
        guardarMaquinaSupabase(maq, p.id);
        mostrarMsgGuardado();
        if (modalOrigen === 'registro') {
            setView('registro-componentes');
            setModalOrigen(null);
        }
    };

    const buscarDireccion = async (texto) => {
        if (texto.length < 3) { setSugerencias([]); return; }
        setBuscandoDireccion(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(texto)}&limit=5&addressdetails=1`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await res.json();
            setSugerencias(data);
        } catch {
            setSugerencias([]);
        } finally {
            setBuscandoDireccion(false);
        }
    };

    const debounceRef = React.useRef(null);
    const handleDireccionChange = (valor) => {
        setDireccionInput(valor);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => buscarDireccion(valor), 400);
    };

    const seleccionarSugerencia = (item) => {
        const dirCompleta = item.display_name;
        setDireccionInput(dirCompleta);
        setDireccionCompleta(dirCompleta);
        setSugerencias([]);
        if (item.lat && item.lon) {
            setCentroMapa([parseFloat(item.lat), parseFloat(item.lon)]);
        }
    };

    const añadirMaquina = (coords) => {
        if (bloqueadoSoloLectura()) return;
        if (modoEdicion) {
            const numeros = maquinasUbicadas
                .map(m => parseInt(m.nombre.replace(/\D/g, '')))
                .filter(n => !isNaN(n));
            const siguiente = numeros.length > 0 ? Math.max(...numeros) + 1 : 1;
            const nuevoNombre = `WTG-${String(siguiente).padStart(2, '0')}`;
            ignorarProximoClick.current = true;
            if (!window.confirm(`Add WTG "${nuevoNombre}" at this location?`)) {
                ignorarProximoClick.current = false;
                return;
            }
            const parque = projects.find(p => p.nombre_parque === parqueEditando);
            const tramos = parque?.num_tramos || 4;
            setMaquinasUbicadas(prev => [...prev, {
                id: crypto.randomUUID(),
                nombre: nuevoNombre,
                idMaquina: '',
                lat: coords[0], lng: coords[1],
                modelo_especifico: modeloGeneral, tramos,
                tareas: generarTareas(tramos, parque?.dt_aparte || false),
                componentesExtra: componentesExtraDefault(),
            }]);
            setTooltipMaquina(true);
            setTimeout(() => setTooltipMaquina(false), 3000);
            return;
        }
        if (maquinasUbicadas.length >= numMaquinasTotal) return;
        ignorarProximoClick.current = true;
        if (!window.confirm(`Place WTG ${maquinasUbicadas.length + 1} at this position?`)) {
            ignorarProximoClick.current = false;
            return;
        }
        const tramos = Math.min(Math.max(parseInt(numTramosTorre)||4, 3), 8);
        setMaquinasUbicadas([...maquinasUbicadas, {
            id: crypto.randomUUID(),
            nombre: '',
            idMaquina: '',
            lat: coords[0], lng: coords[1],
            modelo_especifico: modeloGeneral, tramos,
            tareas: generarTareas(tramos, dtAparte),
            componentesExtra: componentesExtraDefault(),
            boltRegister: [],
        }]);
        setTooltipMaquina(true);
        setTimeout(() => setTooltipMaquina(false), 4000);
    };

    const actualizarDatoMaquina = (id, campo, valor) => setMaquinasUbicadas(prev => prev.map(m =>
        m.id===id ? {...m, [campo]: ['lat','lng','tramos'].includes(campo) ? parseFloat(valor)||0 : valor} : m
    ));

    const guardarParque = () => {
        if (bloqueadoSoloLectura()) return;
        if (!nombre || maquinasUbicadas.length===0) return alert('Missing information');
        const existe = projects.find(p => p.nombre_parque === nombre);
        if (existe && !window.confirm(`"${nombre}" already exists. Overwrite?`)) return;
        const maquinasConNombre = maquinasUbicadas.map((m,i) => m.nombre.trim() ? m : { ...m, nombre: `WTG-${String(i+1).padStart(2,'0')}` });
        const nuevoSlug = Math.random().toString(36).substring(2, 10);
        const pinGenerado = generarPin(nombre);
        const payload = { id:crypto.randomUUID(), slug:nuevoSlug, pin:pinGenerado, nombre_parque:nombre, modelo_maquina:modeloGeneral, num_tramos:parseInt(numTramosTorre), dt_aparte:dtAparte, direccion: direccionCompleta || direccionInput, maquinas:maquinasConNombre, fecha_creacion:new Date().toLocaleDateString('en-GB'), herramientas:herramientasDefault() };
        setProjects(existe ? projects.map(p=>p.nombre_parque===nombre?payload:p) : [...projects,payload]);
        guardarProyectoSupabase(payload);
        payload.maquinas.forEach(m => guardarMaquinaSupabase({
            ...m,
            id: typeof m.id === 'number' ? crypto.randomUUID() : m.id
         }, payload.id));
         const enlace = `${window.location.origin}/parque/${nuevoSlug}`;
      setModalEnlace({ enlace, pin: pinGenerado });
         setMaquinasUbicadas([]); setNombre(''); setModeloGeneral(''); setDireccionInput(''); setDireccionCompleta(''); setSugerencias([]); setView('map');
    };

    const guardarEdicion = () => {
        if (bloqueadoSoloLectura()) return;
        if (!modoEdicion || !parqueEditando) return;
        const maquinasSinUbicar = maquinasUbicadas.filter(m => !m.lat || !m.lng);
        const maquinasValidas   = maquinasUbicadas.filter(m => m.lat && m.lng);

        if (maquinasSinUbicar.length > 0) {
            const continuar = window.confirm(
                `⚠️ The following WTGs have no map location and will be removed automatically:\n\n· ${maquinasSinUbicar.map(m => m.nombre).join('\n· ')}\n\nContinue? Only WTGs with a valid location will be saved.`
            );
            if (!continuar) return;
        }

        const nuevosProjects = projects.map(p => {
            if (p.nombre_parque !== parqueEditando) return p;
            return { ...p, maquinas: maquinasValidas };
        });
        setProjects(nuevosProjects);
        setMaquinasUbicadas(maquinasValidas);
        const p = nuevosProjects.find(p => p.nombre_parque === parqueEditando);
        if (p) guardarProyectoSupabase(p);
        if (p) p.maquinas?.forEach(m => guardarMaquinaSupabase({
            ...m,
            id: typeof m.id === 'number' ? crypto.randomUUID() : m.id
        }, p.id));
        setModoEdicion(false);
        setParqueEditando(null);
        setView('map');
        alert('✅ Wind farm updated');
    };

    const eliminarProyecto = async (np) => {
        if (bloqueadoSoloLectura()) return;
        const confirmar = window.confirm(
            `Delete "${np}"?\n\n⚠️ ALL park data will be permanently deleted:\n· Machines and locations\n· Component records\n· Photos and torque data\n· Project tools\n\nThis action CANNOT be undone.`
        );
        if (!confirmar) return;
        const proyecto = projects.find(p => p.nombre_parque === np);
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) { console.error('No user logged in'); return; }
        setProjects(projects.filter(p=>p.nombre_parque!==np));
        if (activeProject?.nombre_parque===np) setActiveProject(null);
        if (proyecto?.id) supabase.from('proyectos').delete().eq('id', proyecto.id).eq('owner_id', userId).then(({ error }) => {
            if (error && !avisarErrorEscritura(error)) console.error('Error eliminando proyecto:', error);
        });
    };

    const exportarExcel = (proyecto) => {
        const wb = XLSX.utils.book_new();
        // Resumen
        const resumenData = [
            ['RESUMEN DEL PARQUE','','',''],
            ['Parque', proyecto.nombre_parque,'',''],
            ['Modelo', proyecto.modelo_maquina,'',''],
            ['Tramos', proyecto.num_tramos,'',''],
            ['Con DT', proyecto.dt_aparte?'Yes':'No','',''],
            ['Creation date', proyecto.fecha_creacion||'-','',''],
            ['Total WTGs', proyecto.maquinas?.length,'',''],
            ['','','',''],
            ['WTG','Modelo','Progreso (%)','Subtareas completadas'],
            ...proyecto.maquinas.map(m => {
                const tot = m.tareas.reduce((a,f)=>a+f.subtareas.length,0);
                const hec = m.tareas.reduce((a,f)=>a+f.subtareas.filter(s=>s.hecho).length,0);
                return [m.nombre, m.modelo_especifico||proyecto.modelo_maquina, calcularProgreso(m.tareas), `${hec}/${tot}`];
            }),
        ];
        const wsR = XLSX.utils.aoa_to_sheet(resumenData);
        wsR['!cols'] = [{wch:20},{wch:25},{wch:14},{wch:20}];
        XLSX.utils.book_append_sheet(wb, wsR, 'Resumen');

        proyecto.maquinas.forEach(m => {
            const filas = [
                ['WTG: '+m.nombre,'','','','','','','','','','',''],
                ['Modelo', m.modelo_especifico||proyecto.modelo_maquina,'','','','','','','','','',''],
                ['Progreso', calcularProgreso(m.tareas)+'%','','','','','','','','','',''],
                [''],
                ['PHASE','SUBTASK','STATUS','DATE','OPERATOR','ITEM No.','SERIAL','MANUFACTURER','VUI','SET No.','SET / BATCH','BOLT SIZE','NOTES','TORQUE VALUE'],
            ];
            m.tareas.forEach(fase => {
                fase.subtareas.forEach((sub,idx) => {
                    const tipo = sub._tipo || tipoDeSubtarea(sub.nombre, fase.id);
                    filas.push([
                        idx===0 ? fase.nombre : '',
                        sub.nombre,
                        sub.hecho ? '✅ Completado' : '⬜ Pendiente',
                        sub.fecha     || '',
                        sub.operario  || '',
                        tipo===TIPO.APRIETE ? (sub.tornItem||'')    : (sub.item||''),
                        tipo===TIPO.APRIETE ? (sub.tornSerial||'')  : (sub.serial||''),
                        tipo===TIPO.APRIETE ? (sub.tornFabricante||'') : (sub.fabricante||''),
                        sub.vui       || (tipo===TIPO.APRIETE ? `Bomba:${sub.bomTipo||''} S/N:${sub.bomSerial||''}` : ''),
                        sub.numSet    || '',
                        tipo===TIPO.APRIETE ? (sub.tornBatch||'')   : (sub.set||''),
                        tipo===TIPO.APRIETE ? (sub.tornMetrica||'') : '',
                        sub.notas     || '',
                        (sub._tipo===TIPO.APRIETE||tipoDeSubtarea(sub.nombre,fase.id)===TIPO.APRIETE) && sub.valorApriete ? sub.valorApriete+' '+(sub.unidadApriete||'Nm') : '',
                    ]);
                });
                filas.push(['']);
            });
            const ws = XLSX.utils.aoa_to_sheet(filas);
            ws['!cols'] = [{wch:18},{wch:20},{wch:14},{wch:12},{wch:10},{wch:12},{wch:16},{wch:14},{wch:14},{wch:12},{wch:14},{wch:10},{wch:28},{wch:14}];
            XLSX.utils.book_append_sheet(wb, ws, m.nombre.substring(0,31));
        });

        XLSX.writeFile(wb, proyecto.nombre_parque.replace(/\s+/g,'_')+'.xlsx');
    };

    const getSubActiva  = () => { if (!modalData||!maquinaSeleccionada) return null; const f=maquinaSeleccionada.tareas.find(f=>f.id===modalData.faseId); return f?.subtareas[modalData.subIdx]||null; };
    const getFaseActiva = () => { if (!modalData||!maquinaSeleccionada) return null; return maquinaSeleccionada.tareas.find(f=>f.id===modalData.faseId)||null; };

    const modoLecturaCard = (sub) => sub.hecho || !!(sub.serial || sub.item || sub.tornSerial || sub.fecha);
    const componenteCompletado = (comp) => comp.estado === 'completado' || comp.hecho === true || !!(comp.serial || comp.item || comp.fecha);

    const infoBadge = (sub) => {
        const tipo = sub._tipo;
        const items = [];
        if (sub.fecha)    items.push(`📅 ${sub.fecha}`);
        if (sub.operario) items.push(`👤 ${sub.operario}`);
        if (sub.serial)   items.push(`🔢 ${sub.serial}`);
        if (sub.numSet)   items.push(`📦 Set: ${sub.numSet}`);
        if (tipo===TIPO.APRIETE && sub.tornMetrica) items.push(`🔩 ${sub.tornMetrica}`);
        if (tipo===TIPO.APRIETE && sub.bomTipo) items.push(`🔴 ${sub.bomTipo}${sub.bomBar?' · '+sub.bomBar+' bar':''}`);
        if (tipo===TIPO.APRIETE && sub.valorApriete) items.push(`⚡ ${sub.valorApriete} ${sub.unidadApriete||'Nm'}`);
        if ((tipo===TIPO.COMPONENTE||tipo===TIPO.PALA) && sub.fotos?.length > 0) items.push(`📸 ${sub.fotos.length} foto${sub.fotos.length>1?'s':''}`);
        return items;
    };

    const headerStyle          = { background:C.bgDark, color:C.text, padding:'0 24px', display:'flex', justifyContent:'space-between', alignItems:'center', height:'56px', borderBottom:`3px solid ${C.orange}`, fontFamily:"'Barlow Condensed', sans-serif" };
    const bannerSuscripcion = (
        <>
            {soloLectura && (
                <div style={{ background:'#7a2e0e', color:'#fff', padding:'8px 16px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'13px', letterSpacing:'0.04em', textAlign:'center', flexShrink:0 }}>
                    {esTecnico ? 'This wind farm is in read-only mode. Your changes were not saved.' : MSG_TRIAL_EXPIRED}
                    {!esTecnico && (
                        <button onClick={()=>iniciarCheckout('subscription')}
                            style={{ marginLeft:'12px', padding:'3px 12px', background:C.orange, color:C.bgDark, border:'none', cursor:'pointer', borderRadius:'4px', fontWeight:700, fontSize:'12px', letterSpacing:'0.06em', textTransform:'uppercase', fontFamily:"'Barlow Condensed', sans-serif", display:'inline-block' }}>
                            Subscribe
                        </button>
                    )}
                </div>
            )}
            {mostrarTrialRestante && (
                <div style={{ background:C.bgDark, color:C.orange, padding:'6px 16px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'12px', letterSpacing:'0.06em', textAlign:'center', textTransform:'uppercase', flexShrink:0 }}>
                    {`Trial: ${trialDiasRestantes} ${trialDiasRestantes === 1 ? 'day' : 'days'} left`}
                </div>
            )}
            {avisoEscritura && (
                <div role="alert" style={{ background:'#c0392b', color:'#fff', padding:'10px 16px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'13px', display:'flex', alignItems:'center', justifyContent:'center', gap:'12px', flexShrink:0 }}>
                    <span>{avisoEscritura}</span>
                    <button onClick={()=>setAvisoEscritura('')} style={{ background:'transparent', border:'1px solid #fff', color:'#fff', cursor:'pointer', borderRadius:'4px', padding:'0 8px' }}>×</button>
                </div>
            )}
        </>
    );

    const navBtnStyle          = { background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, padding:'6px 14px', cursor:'pointer', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:'5px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', borderRadius:'8px' };
    const labelStyle           = { display:'block', fontWeight:700, fontSize:'0.7rem', color:C.textDim, marginTop:'12px', marginBottom:'4px', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em' };
    const inputStyle           = { width:'100%', padding:'10px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', fontFamily:"'Barlow', sans-serif", boxSizing:'border-box', borderRadius:'6px' };
    const saveBtnStyle         = { width:'100%', padding:'14px', background:C.orange, color:C.bgDark, border:'none', fontWeight:700, cursor:'pointer', marginTop:'25px', fontFamily:"'Barlow Condensed', sans-serif", fontSize:'1rem', letterSpacing:'0.06em', textTransform:'uppercase', borderRadius:'8px' };
    const maquinasStatus       = { marginTop:'20px', padding:'12px', background:C.bgDark, border:`1px dashed ${C.borderMid}`, display:'flex', justifyContent:'space-between', alignItems:'center', color:C.textMuted };
    const popupLabel           = { fontSize:'10px', color:C.textDim, display:'block', marginTop:'5px', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' };
    const popupInput           = { width:'100%', padding:'5px 7px', fontSize:'12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, outline:'none', fontFamily:"'Barlow', sans-serif", borderRadius:'4px' };
    const mapOverlay           = { position:'absolute', top:'15px', left:'50%', transform:'translateX(-50%)', zIndex:1000, background:`rgba(${theme==='dark'?'17,19,20':'232,229,222'},0.95)`, color:C.textMuted, padding:'10px 20px', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:'10px', border:`1px solid ${C.borderMid}`, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em' };

    if (view === 'home') {
        const _fontC = "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif";
        const _totalWTGs = projects.reduce((acc, p) => acc + (p.maquinas?.length || 0), 0);
        const _avgProgreso = projects.length > 0
            ? Math.round(projects.reduce((acc, p) => {
                const avg = p.maquinas?.length > 0
                    ? p.maquinas.reduce((a, m) => a + calcularProgreso(m.tareas), 0) / p.maquinas?.length
                    : 0;
                return acc + avg;
            }, 0) / projects.length)
            : 0;
        return (
            <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', background:C.bg, paddingBottom:'48px' }}>
                {/* ── HEADER ── */}
                <header style={{ background:C.bgDark, borderBottom:`3px solid ${C.orange}`, height:'56px', padding: esMobil ? '0 12px' : '0 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                        <img src={iconoApp} alt="WindManager icon" style={{ height:'40px', width:'40px', objectFit:'contain' }} />
                        <div>
                            <div style={{ display:'flex', alignItems:'baseline', gap:0, lineHeight:1 }}>
                                <span style={{ fontFamily:"'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize:'18px', fontWeight:900, color:C.text, letterSpacing:'0.02em' }}>Wind</span>
                                <span style={{ fontFamily:"'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize:'18px', fontWeight:900, color:C.orange, letterSpacing:'0.02em' }}>Manager</span>
                                <span style={{ fontFamily:"'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize:'18px', fontWeight:900, color:C.text, letterSpacing:'0.02em' }}>Pro</span>
                            </div>
                            <div style={{ fontSize:'10px', color:C.textDim, letterSpacing:'0.15em', textTransform:'uppercase' }}>Field Operations</div>
                        </div>
                    </div>
                    <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                        {!esTecnico && !soloLectura && view !== 'home' && (
                            <div style={{ display:'flex', gap:'6px' }}>
                                <button onClick={()=>setView('map')} style={{ fontFamily:_fontC, fontWeight:700, fontSize:'12px', letterSpacing:'0.08em', textTransform:'uppercase', padding:'10px 20px', background:C.orange, color:C.bgDark, border:'none', borderRadius:'8px', cursor:'pointer' }}>{'PROJECTS'}</button>
                                <button onClick={()=>{ setModoEdicion(false); setParqueEditando(null); setNombre(''); setModeloGeneral(''); setNumMaquinasTotal(1); setNumTramosTorre(4); setDtAparte(false); setMaquinasUbicadas([]); setView('admin'); }} style={{ fontFamily:_fontC, fontWeight:700, fontSize:'12px', letterSpacing:'0.08em', textTransform:'uppercase', padding:'10px 20px', background:'transparent', color:C.textMuted, border:`2px solid ${C.borderMid}`, borderRadius:'8px', cursor:'pointer' }}>{'+ NEW'}</button>
                            </div>
                        )}
                        {/* Theme toggle */}
                        <button onClick={()=>setTheme(theme==='dark'?'light':'dark')} style={{ background:C.bgCard, border:`1px solid ${C.borderMid}`, color:C.textMuted, padding:'6px 14px', fontSize:'11px', fontFamily:_fontC, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', borderRadius:'8px' }}>
                            {theme==='dark' ? '☀️ LIGHT' : <><svg width="14" height="14" viewBox="0 0 24 24" fill="#f0e6cc" stroke="#f0e6cc" strokeWidth="1"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg> DARK</>}
                        </button>

                    </div>
                </header>

                {bannerSuscripcion}
                {/* ── BODY ── */}
                <main style={{ flex:1, background:C.bg, padding: esMobil ? '20px 16px 16px' : '48px 40px 32px' }}>
                    {/* Top row */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        {/* Title */}
                        <div>
                            <div style={{ fontFamily:_fontC, fontSize: esMobil ? '28px' : '52px', fontWeight:900, color:C.text, textTransform:'uppercase', lineHeight:1 }}>{'WIND FARM'}</div>
                            <div style={{ fontFamily:_fontC, fontSize: esMobil ? '28px' : '52px', fontWeight:900, color:C.orange, textTransform:'uppercase', lineHeight:1 }}>{'MANAGEMENT'}</div>
                            <div style={{ fontFamily:_fontC, fontSize: esMobil ? '10px' : '12px', color:C.textDim, letterSpacing:'0.12em', textTransform:'uppercase', marginTop:'12px' }}>{'INSTALLATION · TIGHTENING · COMPONENT REGISTRY'}</div>
                        </div>
                        {/* Stats */}
                        <div style={{ display:'flex', gap: esMobil ? '4px' : '2px', borderLeft:`1px solid ${C.border}`, flexDirection: esMobil ? 'column' : 'row' }}>
                            {[
                                { label: 'FARMS',  value: projects.length, pct: false },
                                { label: 'WTGS',     value: _totalWTGs,       pct: false },
                                { label: 'PROGRESS', value: _avgProgreso,      pct: true  },
                            ].map(s => (
                                <div key={s.label} style={{ background:C.bg, padding: esMobil ? '10px 12px' : '16px 24px', borderLeft: esMobil ? 'none' : `3px solid ${C.orange}`, borderBottom: esMobil ? `3px solid ${C.orange}` : 'none' }}>
                                    <div style={{ fontFamily:_fontC, fontWeight:700, fontSize: esMobil ? '22px' : '36px', color:C.text, lineHeight:1 }}>
                                        {s.value}{s.pct && <span style={{ fontSize: esMobil ? '14px' : '20px' }}>%</span>}
                                    </div>
                                    <div style={{ fontFamily:_fontC, fontSize: esMobil ? '8px' : '10px', color:C.textDim, letterSpacing:'0.14em', textTransform:'uppercase', marginTop:'4px' }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display:'flex', gap:'12px', marginTop: esMobil ? '20px' : '36px', marginBottom: esMobil ? '20px' : '40px', flexDirection: esMobil ? 'column' : 'row' }}>
                        {!esTecnico && !soloLectura && (
                            <button onClick={()=>{ setModoEdicion(false); setParqueEditando(null); setNombre(''); setModeloGeneral(''); setNumMaquinasTotal(1); setNumTramosTorre(4); setDtAparte(false); setMaquinasUbicadas([]); setView('admin'); }} style={{ fontFamily:_fontC, fontWeight:700, fontSize: esMobil ? '12px' : '16px', letterSpacing:'0.08em', textTransform:'uppercase', padding: esMobil ? '12px 20px' : '16px 32px', background:C.orange, color:C.bgDark, border:'none', borderRadius:'10px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                                {'+ CREATE PROJECT'}
                            </button>
                        )}
                        <button onClick={()=>setView('map')}
                             onMouseEnter={e=>{ e.currentTarget.style.borderColor=C.orange; e.currentTarget.style.color=C.orange; }}
                             onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.borderMid; e.currentTarget.style.color=C.textMuted; }}
                             style={{ fontFamily:_fontC, fontWeight:700, fontSize: esMobil ? '12px' : '16px', letterSpacing:'0.08em', textTransform:'uppercase', padding: esMobil ? '12px 20px' : '16px 32px', background:'transparent', color:C.textMuted, border:`2px solid ${C.borderMid}`, borderRadius:'10px', cursor:'pointer' }}>
                             {'VIEW ON MAP'}
                        </button>
                    </div>

                    {/* Projects list */}
                    {projects.length > 0 && (
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px' }}>
                                <div style={{ fontFamily:_fontC, fontWeight:600, fontSize:'11px', color:C.textDim, letterSpacing:'0.14em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{'ACTIVE PROJECTS'}</div>
                                <div style={{ flex:1, height:'1px', background:C.border }}/>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                                {projects.map(p => {
                                    const _prog = p.maquinas?.length > 0
                                        ? Math.round(p.maquinas.reduce((a, m) => a + calcularProgreso(m.tareas), 0) / p.maquinas?.length)
                                        : 0;
                                    const _done = _prog === 100;
                                    return (
                                        <div key={p.nombre_parque}
                                            onClick={()=>{ setActiveProject(p); if(p.maquinas?.length>0 && p.maquinas[0].lat) setCentroMapa([p.maquinas[0].lat,p.maquinas[0].lng]); setView('map'); }}
                                            style={{ background:C.bgCard, padding: esMobil ? '12px 14px' : '16px 20px', display:'flex', alignItems:'center', gap: esMobil ? '10px' : '16px', borderLeft:`3px solid ${C.orange}`, borderRadius:'8px', cursor:'pointer', flexWrap: esMobil ? 'wrap' : 'nowrap' }}>
                                            <div style={{ width:'8px', height:'8px', background: _done ? C.green : C.orange, flexShrink:0 }}/>
                                            <div style={{ flex:'0 0 auto' }}>
                                                <div style={{ fontFamily:_fontC, fontWeight:700, fontSize: esMobil ? '12px' : '14px', textTransform:'uppercase', color:C.text, letterSpacing:'0.05em' }}>{p.nombre_parque}</div>
                                                <div style={{ fontFamily:_fontC, fontSize: esMobil ? '10px' : '11px', color:C.textDim, marginTop:'2px' }}>
                                                    {p.maquinas?.length || 0} WTGs · {p.modelo_maquina} · {p.num_tramos} {'sections'}{p.dt_aparte ? ' · DT' : ''}
                                                </div>
                                            </div>
                                            <div style={{ flex:1, height:'4px', background:C.bgDark, margin: esMobil ? '0 8px' : '0 16px', minWidth: esMobil ? '40px' : '100px' }}>
                                                <div style={{ width:_prog+'%', height:'100%', background: _done ? C.green : C.orange }}/>
                                            </div>
                                            <div style={{ fontFamily:_fontC, fontWeight:700, fontSize: esMobil ? '18px' : '28px', color: _done ? C.green : C.orange, minWidth: esMobil ? '40px' : '70px', textAlign:'right' }}>
                                                {_prog}%
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </main>

                {/* ── FOOTER ── */}
                {!esTecnico && (
                <footer style={{ background:C.bgDark, borderTop:`1px solid ${C.border}`, padding:'12px 40px', display:'flex', alignItems:'center', gap:'8px', position:'fixed', bottom:0, width:'100%', boxSizing:'border-box' }}>
                    <div style={{ width:'6px', height:'6px', background:C.green, flexShrink:0 }}/>
                    <span style={{ fontFamily:_fontC, fontSize:'11px', color:C.textDim, textTransform:'uppercase', letterSpacing:'0.1em' }}>
                        {`DATA SAVED LOCALLY · ${projects.length} FARMS · ${_totalWTGs} WTGS`}
                    </span>
                </footer>
                )}
            </div>
        );
    }

    return (
        <TechContext.Provider value={{ esTecnico, proyectoTech, soloLectura: lecturaParqueAbierto }}>
        <div style={{ height:'100vh', display:'flex', flexDirection:'column', background:C.bg }}>
            {bannerSuscripcion}

            {/* Modal enlace técnico */}
            {modalEnlace && (
                 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
                    <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'32px', width:'100%', maxWidth:'460px', margin:'16px' }}>
                        <div style={{ fontFamily:_fontC, fontWeight:900, fontSize:'1.1rem', color:C.text, marginBottom:'8px', textTransform:'uppercase' }}>
                            {modalEnlace.compartir ? '🔗 Technician access link' : '✅ Farm saved!'}
                        </div>
                        <div style={{ fontFamily:_fontC, fontSize:'0.8rem', color:C.textDim, marginBottom:'20px' }}>
                            Share this link and PIN with your technicians
                        </div>
                        <div style={{ background:C.bgDark, borderRadius:'8px', padding:'12px 16px', marginBottom:'12px' }}>
                            <div style={{ fontSize:'0.7rem', color:C.textDim, marginBottom:'4px', textTransform:'uppercase', fontFamily:_fontC }}>Access link</div>
                            <div style={{ fontSize:'0.85rem', color:C.orange, wordBreak:'break-all', fontFamily:'monospace', userSelect:'text', WebkitUserSelect:'text', cursor:'text' }}>{modalEnlace.enlace}</div>
                        </div>
                        <div style={{ background:C.bgDark, borderRadius:'8px', padding:'12px 16px', marginBottom:'20px' }}>
                            <div style={{ fontSize:'0.7rem', color:C.textDim, marginBottom:'4px', textTransform:'uppercase', fontFamily:_fontC }}>PIN</div>
                            <div style={{ fontSize:'1.5rem', fontWeight:900, color:C.orange, fontFamily:'monospace', letterSpacing:'0.3em', userSelect:'text', WebkitUserSelect:'text', cursor:'text' }}>{modalEnlace.pin}</div>
                        </div>
                        <div style={{ display:'flex', gap:'10px' }}>
                            <button onClick={() => {
                                navigator.clipboard.writeText(modalEnlace.enlace);
                                alert('Link copied!');
                            }} style={{ flex:1, padding:'10px', background:C.orange, border:'none', borderRadius:'8px', color:'#fff', fontFamily:_fontC, fontWeight:700, fontSize:'0.85rem', cursor:'pointer', textTransform:'uppercase' }}>
                                📋 Copy link
                            </button>
                            <button onClick={() => setModalEnlace(null)}
                                 style={{ flex:1, padding:'10px', background:'transparent', border:`1px solid ${C.borderMid}`, borderRadius:'8px', color:C.textMuted, fontFamily:_fontC, fontWeight:700, fontSize:'0.85rem', cursor:'pointer', textTransform:'uppercase' }}>
                                 Close
                            </button>
                        </div>
                    </div>
                 </div>
            )}
            {/* ── Modal Herramientas del Proyecto ── */}
            {modalHerramientas && (
                <div style={{ position:'fixed', inset:0, zIndex:9998, background:C.overlay, backdropFilter:'blur(3px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                    <div style={{ background:C.bgCard, border:`1px solid ${C.borderMid}`, width:'100%', maxWidth:'640px', maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 64px rgba(0,0,0,0.6)', overflow:'hidden', position:'relative', borderRadius:'12px' }}>
                        {/* Cabecera */}
                        <div style={{ background:C.bgDark, padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, borderBottom:`3px solid ${C.orange}` }}>
                            <div>
                                <div style={{ fontSize:'10px', color:C.textDim, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'3px', fontFamily:"'Barlow Condensed', sans-serif" }}>
                                    {modalHerramientas}
                                </div>
                                <h3 style={{ margin:0, color:C.text, fontSize:'1.2rem', fontWeight:900, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', display:'flex', alignItems:'center', gap:'8px' }}><Wrench size={20}/> {'Project Tools'}</h3>
                                <div style={{ fontSize:'11px', color:C.textDim, marginTop:'4px', fontFamily:"'Barlow Condensed', sans-serif" }}>{'Pumps and wrenches available for tightening in this farm'}</div>
                            </div>
                            <button onClick={()=>{ setModalHerramientas(null); setHerramientaSeleccionada(null); }} style={{ background:'rgba(255,255,255,0.08)', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'7px', display:'flex', borderRadius:'8px' }}><X size={18}/></button>
                        </div>
                        {/* Cuerpo scrollable */}
                        <div style={{ overflowY:'auto', flex:1, padding:'20px 24px', background:C.bgCard }}>
                            <HerramientaSeccion
                                titulo={'Torque Pumps'}
                                icono={'🔴'}
                                descripcion={'Hydraulic pumps for bolt tightening'}
                                items={getHerramientas(modalHerramientas).bombas || []}
                                showGauge={true}
                                tipoHerramienta={'bomba'}
                                onAdd={(m,s,f,cert,gauge,gaugeModel,calValid)=>addHerramienta(modalHerramientas,'bombas',m,s,f,cert,gauge,gaugeModel,calValid)}
                                onSelect={(h)=>setHerramientaSeleccionada({ herramienta: h, tipo: 'bomba' })}
                                C={C}
                            />
                            <HerramientaSeccion
                                titulo={'Hydraulic Wrenches'}
                                icono={'🔧'}
                                descripcion={'Hydraulic torque wrenches'}
                                items={getHerramientas(modalHerramientas).hidraulicas || []}
                                tipoHerramienta={'hidraulica'}
                                onAdd={(m,s,f,cert,gauge,gaugeModel,calValid)=>addHerramienta(modalHerramientas,'hidraulicas',m,s,f,cert,'','',calValid)}
                                onSelect={(h)=>setHerramientaSeleccionada({ herramienta: h, tipo: 'hidraulica' })}
                                C={C}
                            />
                            <HerramientaSeccion
                                titulo={'Tensioners (Pullers)'}
                                icono={'⚙️'}
                                descripcion={'Hydraulic tensioners for elongation tightening'}
                                items={getHerramientas(modalHerramientas).tensionadores || []}
                                tipoHerramienta={'tensionador'}
                                onAdd={(m,s,f,cert,gauge,gaugeModel,calValid)=>addHerramienta(modalHerramientas,'tensionadores',m,s,f,cert,'','',calValid)}
                                onSelect={(h)=>setHerramientaSeleccionada({ herramienta: h, tipo: 'tensionador' })}
                                C={C}
                            />
                        </div>
                        {/* Footer */}
                        <div style={{ padding:'14px 24px', borderTop:`1px solid ${C.border}`, background:C.bgDark, flexShrink:0, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <span style={{ fontSize:'11px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>{'💾 Saved automatically'}</span>
                            <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                                <button onClick={()=>generarPDFHerramientas(modalHerramientas)} style={{ padding:'10px 20px', background:'transparent', border:`2px solid ${C.orange}`, color:C.orange, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'14px', textTransform:'uppercase', letterSpacing:'0.08em', display:'flex', alignItems:'center', gap:'6px', borderRadius:'8px' }}>
                                    <FileText size={14}/> PDF
                                </button>
                                <button onClick={()=>{ setModalHerramientas(null); setHerramientaSeleccionada(null); }} style={{ padding:'10px 24px', border:'none', background:C.orange, color:C.bgDark, cursor:'pointer', fontWeight:700, fontSize:'0.875rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                    {'Close'}
                                </button>
                            </div>
                        </div>

                        {/* Panel detalle herramienta */}
                        {herramientaSeleccionada && (
                            <div style={{ position:'absolute', inset:0, zIndex:10, background:C.bgCard, display:'flex', flexDirection:'column', borderRadius:'inherit', overflow:'hidden' }}>
                                {/* Cabecera */}
                                <div style={{ background:C.bgDark, borderBottom:`3px solid ${C.orange}`, padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                    <div>
                                        <div style={{ fontSize:'10px', color:C.textDim, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>
                                            {herramientaSeleccionada.tipo === 'bomba' ? 'Hydraulic Pump' :
                                             herramientaSeleccionada.tipo === 'hidraulica' ? 'Hydraulic Wrench' : 'Tensioner'}
                                        </div>
                                        <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:'22px', fontWeight:900, color:C.text, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                                            {herramientaSeleccionada.herramienta.modelo || '—'}
                                        </div>
                                    </div>
                                    <button onClick={() => setHerramientaSeleccionada(null)} style={{ background:'none', border:'none', color:C.textMuted, cursor:'pointer', fontSize:'20px' }}>✕</button>
                                </div>

                                {/* Datos */}
                                <div style={{ padding:'24px 20px', flex:1, overflowY:'auto' }}>
                                    <div style={{ marginBottom:'16px' }}>
                                        <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Modelo</div>
                                        <div style={{ fontSize:'16px', color:C.text, fontWeight:700, fontFamily:'monospace' }}>{herramientaSeleccionada.herramienta.modelo || '—'}</div>
                                    </div>
                                    <div style={{ marginBottom:'16px' }}>
                                        <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Serial Number</div>
                                        <div style={{ fontSize:'16px', color:C.text, fontWeight:700, fontFamily:'monospace' }}>{herramientaSeleccionada.herramienta.serial || '—'}</div>
                                    </div>
                                    {herramientaSeleccionada.tipo === 'bomba' && (
                                        <>
                                            <div style={{ marginBottom:'16px' }}>
                                                <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Pressure Gauge S/N</div>
                                                <div style={{ fontSize:'16px', color:C.text, fontWeight:700, fontFamily:'monospace' }}>{herramientaSeleccionada.herramienta.gaugeSerial || '—'}</div>
                                            </div>
                                            {herramientaSeleccionada.herramienta.pressureGaugeModel && (
                                                <div style={{ marginBottom:'16px' }}>
                                                    <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Pressure Gauge Model</div>
                                                    <div style={{ fontSize:'14px', color:C.text, fontWeight:600 }}>{herramientaSeleccionada.herramienta.pressureGaugeModel}</div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {herramientaSeleccionada.herramienta.calibrationValid && (() => {
                                        const expired = new Date(herramientaSeleccionada.herramienta.calibrationValid) < new Date();
                                        return (
                                            <div style={{ marginBottom:'16px' }}>
                                                <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Calibration Valid Until</div>
                                                <div style={{ fontSize:'16px', fontWeight:700, color: expired ? '#c0392b' : '#5a9e45' }}>
                                                    {expired ? `⚠️ EXPIRED — ${herramientaSeleccionada.herramienta.calibrationValid}` : `✓ ${herramientaSeleccionada.herramienta.calibrationValid}`}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {herramientaSeleccionada.herramienta.fabricante && (
                                        <div style={{ marginBottom:'16px' }}>
                                            <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Manufacturer</div>
                                            <div style={{ fontSize:'16px', color:C.text, fontWeight:700 }}>{herramientaSeleccionada.herramienta.fabricante}</div>
                                        </div>
                                    )}
                                    <div style={{ marginBottom:'16px' }}>
                                        <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Type</div>
                                        <div style={{ display:'inline-block', background:C.bgDark, border:`1px solid ${C.borderMid}`, padding:'4px 10px', fontSize:'11px', color:C.orange, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                                            {herramientaSeleccionada.tipo === 'bomba' ? '🔴 Hydraulic Pump' :
                                             herramientaSeleccionada.tipo === 'hidraulica' ? '🔧 Hydraulic Wrench' : '⚙️ Tensioner'}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom:'16px' }}>
                                        <div style={{ fontSize:'9px', color:C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'4px' }}>Certificate</div>
                                        <div style={{ fontSize:'16px', fontWeight:700, color: herramientaSeleccionada.herramienta.certificado ? C.green : C.textDim }}>
                                            {herramientaSeleccionada.herramienta.certificado ? 'Yes' : 'No'}
                                        </div>
                                    </div>
                                </div>

                                {/* Footer con botones */}
                                <div style={{ padding:'16px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:'10px' }}>
                                    <button
                                        onClick={() => generarPDFHerramientaIndividual(modalHerramientas, herramientaSeleccionada.herramienta, herramientaSeleccionada.tipo)}
                                        style={{ padding:'10px 16px', background:'transparent', border:`2px solid ${C.orange}`, color:C.orange, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.06em', display:'flex', alignItems:'center', gap:'6px' }}
                                    >
                                        📄 PDF
                                    </button>
                                    {!esTecnico && (
                                    <button
                                        onClick={() => {
                                            const confirmar = window.confirm(
                                                `Delete "${herramientaSeleccionada.herramienta.modelo || 'this tool'}"?\n\nIt will be removed from the project catalogue.`
                                            );
                                            if (!confirmar) return;
                                            const tipoKey = herramientaSeleccionada.tipo === 'bomba' ? 'bombas' :
                                                            herramientaSeleccionada.tipo === 'hidraulica' ? 'hidraulicas' : 'tensionadores';
                                            deleteHerramienta(modalHerramientas, tipoKey, herramientaSeleccionada.herramienta.id);
                                            setHerramientaSeleccionada(null);
                                        }}
                                        style={{ flex:1, padding:'10px', background:'transparent', border:`2px solid #c0392b`, color:'#c0392b', cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'14px', textTransform:'uppercase', letterSpacing:'0.08em' }}
                                    >
                                        🗑 ELIMINAR
                                    </button>
                                    )}
                                    <button
                                        onClick={() => setHerramientaSeleccionada(null)}
                                        style={{ padding:'10px 20px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'14px', textTransform:'uppercase', letterSpacing:'0.08em' }}
                                    >
                                        CLOSE
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }`}</style>
                </div>
            )}

            {/* Modal */}
            {modalData && getSubActiva() && (
                <ModalSubtarea
                    sub={getSubActiva()}
                    faseId={modalData.faseId}
                    faseNombre={getFaseActiva()?.nombre}
                    maquina={maquinaSeleccionada}
                    maquinaNombre={maquinaSeleccionada?.nombre}
                    herramientas={projects.find(p=>p.nombre_parque===activeProject?.nombre_parque)?.herramientas || herramientasDefault()}
                    proyecto={activeProject}
                    proyectoModelo={maquinaSeleccionada?.modelo_especifico || activeProject?.modelo_maquina || ''}
                    onClose={cerrarModal}
                    onGuardar={(datos)=>guardarSubtarea(modalData.maquinaId, modalData.faseId, modalData.subIdx, datos)}
                    esTecnico={esTecnico}
                    C={C}
                />
            )}
            {modalCompExtra && maquinaRegistroSel && proyectoRegistro && (()=>{
                const ce = (maquinaRegistroSel.componentesExtra || {})[modalCompExtra.compNombre] || componenteExtraVacio();
                return (
                    <ModalComponenteExtra
                        compNombre={modalCompExtra.compNombre}
                        datos={ce}
                        proyectoModelo={maquinaRegistroSel.modelo_especifico || proyectoRegistro.modelo_maquina || ''}
                        onClose={cerrarModalCompExtra}
                        onGuardar={(datos)=>guardarComponenteExtra(proyectoRegistro.nombre_parque, maquinaRegistroSel.id, modalCompExtra.compNombre, datos)}
                        esTecnico={esTecnico}
                        maquinaId={maquinaRegistroSel.id}
                        C={C}
                    />
                );
            })()}

            {modalBoltRegister && (
                <div style={{
                    position:'fixed', inset:0, zIndex:9999,
                    background: C.overlay,
                    backdropFilter:'blur(4px)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    padding:'16px',
                }}>
                    <div style={{
                        background: C.bgCard,
                        width:'100%', maxWidth:'680px',
                        maxHeight:'88vh',
                        display:'flex', flexDirection:'column',
                        border:`1px solid ${C.borderMid}`,
                    }}>
                        {/* Cabecera */}
                        <div style={{
                            background: C.bgDark,
                            borderBottom:`3px solid ${C.orange}`,
                            padding:'16px 20px',
                            display:'flex', justifyContent:'space-between', alignItems:'center',
                            flexShrink:0,
                        }}>
                            <div>
                                <div style={{
                                    fontFamily:"'Barlow Condensed',sans-serif",
                                    fontSize:'20px', fontWeight:900,
                                    color: C.text, textTransform:'uppercase',
                                }}>
                                    🔩 Bolt Register — {maquinaSeleccionada?.nombre}
                                </div>
                                <div style={{ fontSize:'11px', color: C.textDim, marginTop:'2px' }}>
                                    Register bolting before torque operations
                                </div>
                            </div>
                            <button
                                onClick={() => setModalBoltRegister(false)}
                                style={{ background:'none', border:'none', color: C.textMuted, cursor:'pointer', fontSize:'20px' }}
                            >✕</button>
                        </div>

                        {/* Contenido scrollable */}
                        <div style={{ overflowY:'auto', flex:1, padding:'16px 20px' }}>
                            {boltRegisterData.length === 0 && (
                                <div style={{ color: C.textDim, fontSize:'13px', textAlign:'center', padding:'32px' }}>
                                    No torque connections found for this WTG.
                                </div>
                            )}

                            {boltRegisterData.map((entry, idx) => (
                                <BoltRegisterCard
                                    key={entry.id}
                                    entry={entry}
                                    C={C}
                                    onOpen={() => setBoltEntryModal({ idx, entry })}
                                />
                            ))}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding:'12px 20px',
                            borderTop:`1px solid ${C.border}`,
                            display:'flex', justifyContent:'space-between', alignItems:'center',
                            flexShrink:0,
                        }}>
                            <div style={{ fontSize:'11px', color: C.textDim }}>
                                {boltRegisterData.filter(e => e.registrado).length} / {boltRegisterData.length} connections registered
                            </div>
                            <div style={{ display:'flex', gap:'8px' }}>
                            <button
                                onClick={generarPDFBoltRegister}
                                disabled={boltRegisterData.length === 0}
                                style={{
                                    padding:'8px 18px',
                                    background:'transparent', border:`2px solid ${C.orange}`,
                                    color: C.orange, cursor: boltRegisterData.length === 0 ? 'not-allowed' : 'pointer',
                                    fontFamily:"'Barlow Condensed',sans-serif",
                                    fontWeight:700, fontSize:'13px', textTransform:'uppercase',
                                    display:'flex', alignItems:'center', gap:'6px',
                                    opacity: boltRegisterData.length === 0 ? 0.4 : 1,
                                }}
                            ><FileSpreadsheet size={14}/> PDF</button>
                            <button
                                onClick={() => setModalBoltRegister(false)}
                                style={{
                                    padding:'8px 24px',
                                    background: C.bgDark, border:`1px solid ${C.borderMid}`,
                                    color: C.textMuted, cursor:'pointer',
                                    fontFamily:"'Barlow Condensed',sans-serif",
                                    fontWeight:700, fontSize:'13px', textTransform:'uppercase',
                                }}
                            >CLOSE</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {boltEntryModal && (
                <ModalBoltEntry
                    entry={boltEntryModal.entry}
                    C={C}
                    proyectoModelo={maquinaSeleccionada?.modelo_especifico || activeProject?.modelo_maquina || ''}
                    esTecnico={esTecnico}
                    maquinaId={maquinaSeleccionada?.id}
                    onClose={() => setBoltEntryModal(null)}
                    onSave={(datosActualizados) => {
                        const nuevos = boltRegisterData.map((e, i) =>
                            i === boltEntryModal.idx ? { ...e, ...datosActualizados, registrado: true } : e
                        );
                        guardarBoltRegister(nuevos);
                        setBoltEntryModal(null);
                    }}
                    onDelete={() => {
                        const confirmar = window.confirm(
                            `Delete bolt record for "${boltEntryModal.entry.connectionName}"?\n\nAll registered data will be cleared.`
                        );
                        if (!confirmar) return;
                        const nuevos = boltRegisterData.map((e, i) =>
                            i === boltEntryModal.idx ? {
                                ...e,
                                item:'', serial:'', fabricante:'', boltSize:'',
                                batch:'', openingTime:'', notes:'', vui:'', camposExtra:{}, fotos:[], registrado:false
                            } : e
                        );
                        guardarBoltRegister(nuevos);
                        setBoltEntryModal(null);
                    }}
                />
            )}

            <header style={{ ...headerStyle, borderBottom: view==='detalle-maquina' ? 'none' : `3px solid ${C.orange}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div onClick={()=>setView(view==='detalle-maquina'?'map':'home')} style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:'10px' }}>
                        <img src={iconoApp} alt="WindManager icon" style={{ height:'38px', width:'38px', objectFit:'contain' }} />
                        <div>
                            <div style={{ display:'flex', alignItems:'baseline', gap:0, lineHeight:1 }}>
                                <span style={{ fontFamily:"'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize:'18px', fontWeight:900, color:C.text, letterSpacing:'0.02em' }}>Wind</span>
                                <span style={{ fontFamily:"'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize:'18px', fontWeight:900, color:C.orange, letterSpacing:'0.02em' }}>Manager</span>
                                <span style={{ fontFamily:"'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize:'18px', fontWeight:900, color:C.text, letterSpacing:'0.02em' }}>Pro</span>
                            </div>
                            <div style={{ fontSize:'10px', color:C.textDim, letterSpacing:'0.15em', textTransform:'uppercase' }}>Field Operations</div>
                        </div>
                    </div>
                    {view==='detalle-maquina' && (
                        <button onClick={()=>setView('map')} style={{ ...navBtnStyle, marginLeft:'12px' }}>{'← Back to Map'}</button>
                    )}
                </div>
                <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                    {view==='detalle-maquina' && maquinaSeleccionada ? (
                        <>
                            {guardadoMsg && <span style={{ fontSize:'0.72rem', color:C.green, fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em' }}>{guardadoMsg}</span>}
                            <div style={{ textAlign:'right' }}>
                                <div style={{ color:C.text, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:900, textTransform:'uppercase', fontSize:'1.5rem', letterSpacing:'0.04em', lineHeight:1 }}>{maquinaSeleccionada.nombre}</div>
                                <div style={{ color:C.textDim, fontSize:'0.75rem', fontFamily:"'Barlow Condensed', sans-serif" }}>{proyectoSeleccionado?.nombre_parque} — {maquinaSeleccionada.modelo_especifico} — {maquinaSeleccionada.tramos} {'sections'}{proyectoSeleccionado?.dt_aparte?` · ${'With DT'}`:''}</div>
                            </div>
                        </>
                    ) : (
                        <>
                            {guardadoMsg && <span style={{ fontSize:'0.72rem', color:C.green, fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em' }}>{guardadoMsg}</span>}
                            {view!=='map' && <button onClick={()=>setView('map')} style={navBtnStyle}><LayoutGrid size={13}/> {'Map'}</button>}
                            {!esTecnico && !soloLectura && (
                                <button onClick={()=>{ setModoEdicion(false); setParqueEditando(null); setNombre(''); setModeloGeneral(''); setNumMaquinasTotal(1); setNumTramosTorre(4); setDtAparte(false); setMaquinasUbicadas([]); setView('admin'); }} style={{...navBtnStyle, background:C.orange, color:C.bgDark, border:`1px solid ${C.orange}`}}><PlusCircle size={13}/> {'Create'}</button>
                            )}
                        </>
                    )}
                    <button onClick={()=>setTheme(theme==='dark'?'light':'dark')} style={navBtnStyle}>
                        {theme==='dark' ? '☀️' : '🌙'}
                    </button>
                </div>
            </header>

            <main style={{ flex:1, display:'flex', overflow:'hidden' }}>

                {/* ── MAPA ── */}
                {view==='map' && (<>
                    {esTecnico ? (
                        /* Tech: lista de WTGs (sin mapa ni aside) */
                        <div style={{ flex:1, overflowY:'auto', background:C.bg, padding:'16px' }}>
                            <div style={{ maxWidth:'600px', margin:'0 auto' }}>
                                {(activeProject?.maquinas || []).length === 0 && (
                                    <div style={{ color:C.textDim, padding:'48px 0', textAlign:'center', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'0.9rem', textTransform:'uppercase', letterSpacing:'0.08em' }}>
                                        No WTGs found in this project.
                                    </div>
                                )}
                                {(activeProject?.maquinas || []).map(maquina => {
                                    const progreso = calcularProgreso(maquina.tareas);
                                    let estado = 'In progress'; let estadoColor = C.orange;
                                    if (progreso >= 100) { estado = 'Complete'; estadoColor = C.green; }
                                    else if (progreso === 0) { estado = 'Not started'; estadoColor = C.textDim; }
                                    return (
                                        <div key={maquina.id}
                                            onClick={() => { setMaquinaSeleccionada(maquina); setProyectoSeleccionado(activeProject); setView('detalle-maquina'); }}
                                            style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', marginBottom:'10px', padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:'12px', transition:'border-color 0.15s' }}
                                            onMouseEnter={e => e.currentTarget.style.borderColor = C.orange}
                                            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                                        >
                                            <img src={wtgIcon} alt="" style={{ width:'32px', height:'32px', objectFit:'contain', flexShrink:0, opacity: progreso >= 100 ? 1 : 0.65 }} />
                                            <div style={{ flex:1, minWidth:0 }}>
                                                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:'1rem', color:C.text, textTransform:'uppercase', letterSpacing:'0.04em' }}>
                                                    {maquina.nombre}
                                                </div>
                                                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'5px' }}>
                                                    <div style={{ flex:1, height:'4px', background:C.bgDark, borderRadius:'2px', overflow:'hidden' }}>
                                                        <div style={{ width:`${progreso}%`, height:'100%', background: progreso >= 100 ? C.green : C.orange, borderRadius:'2px' }} />
                                                    </div>
                                                    <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'0.75rem', color:C.textMuted, whiteSpace:'nowrap' }}>
                                                        {progreso}%
                                                    </span>
                                                </div>
                                            </div>
                                            <span style={{ padding:'3px 9px', borderRadius:'10px', fontSize:'0.65rem', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', background:estadoColor+'1a', color:estadoColor, border:`1px solid ${estadoColor}50`, flexShrink:0 }}>
                                                {estado}
                                            </span>
                                            <ChevronRight size={16} style={{ color:C.textDim, flexShrink:0 }} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (<>
                    {/* Desktop: aside lateral */}
                    {!esMobil && (
                        <aside style={{ width:'300px', background:C.bgDark, borderRight:`1px solid ${C.border}`, padding:'16px', overflowY:'auto' }}>
                            <h3 style={{ fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:C.textDim, marginBottom:'14px', borderBottom:`1px solid ${C.border}`, paddingBottom:'8px' }}>{'Active Projects'}</h3>
                            {projects.length===0 && <p style={{ color:C.textDim, fontSize:'0.82rem' }}>{'No projects. Create one first.'}</p>}
                            {projects.map(p => (
                                <div key={p.nombre_parque} onClick={()=>{ setActiveProject(p); if(p.maquinas?.length>0) setCentroMapa([p.maquinas[0].lat,p.maquinas[0].lng]); }}
                                    style={{ position:'relative', padding:'12px', background: activeProject?.nombre_parque===p.nombre_parque?C.bgHover:C.bgCard, borderLeft:`3px solid ${activeProject?.nombre_parque===p.nombre_parque?C.orange:'transparent'}`, border:`1px solid ${C.border}`, cursor:'pointer', marginBottom:'8px', borderRadius:'8px' }}>
                                    {!esTecnico && !soloLectura && !parqueSinPagar(p) && (
                                        <button onClick={(e)=>{ e.stopPropagation(); eliminarProyecto(p.nombre_parque); }} title={'Delete'}
                                            style={{ position:'absolute', top:'10px', right:'10px', background:'transparent', border:'none', color:'#c0392b', cursor:'pointer', padding:'4px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                            <Trash2 size={15}/>
                                        </button>
                                    )}
                                    {!esTecnico && (
                                        <button onClick={(e)=>{ e.stopPropagation(); const enlace=`${window.location.origin}/parque/${p.slug}`; setModalEnlace({ enlace, pin: p.pin, compartir: true }); }} title={'Share access link'}
                                            style={{ position:'absolute', top:'10px', right:'58px', background:'transparent', border:'none', color:C.orange, cursor:'pointer', padding:'4px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                            🔗
                                        </button>
                                    )}
                                    {!esTecnico && !soloLectura && !parqueSinPagar(p) && (
                                        <button onClick={(e)=>{
                                            e.stopPropagation();
                                            const proj = projects.find(proj=>proj.nombre_parque===p.nombre_parque);
                                            if (!proj) return;
                                            setNombre(proj.nombre_parque); setModeloGeneral(proj.modelo_maquina||''); setNumMaquinasTotal(proj.maquinas?.length||1); setNumTramosTorre(proj.num_tramos||4); setDtAparte(proj.dt_aparte||false);
                                            const maqsConCoords = proj.maquinas?.filter(m=>m.lat&&m.lng)||[];
                                            setMaquinasUbicadas(maqsConCoords); setDireccionInput(proj.direccion||''); setDireccionCompleta(proj.direccion||'');
                                            if (maqsConCoords.length>0) { const avgLat=maqsConCoords.reduce((s,m)=>s+m.lat,0)/maqsConCoords.length; const avgLng=maqsConCoords.reduce((s,m)=>s+m.lng,0)/maqsConCoords.length; setCentroMapa([avgLat,avgLng]); setFitBoundsCoords(maqsConCoords.map(m=>[m.lat,m.lng])); } else { setFitBoundsCoords(null); }
                                            setModoEdicion(true); setParqueEditando(proj.nombre_parque); setView('admin');
                                        }} title="Editar parque"
                                            style={{ position:'absolute', top:'10px', right:'36px', background:'transparent', border:'none', color:C.textMuted, cursor:'pointer', padding:'4px', display:'flex', alignItems:'center' }}>
                                            <Pencil size={15}/>
                                        </button>
                                    )}
                                    <div style={{ fontWeight:700, marginBottom:'2px', fontFamily:"'Barlow Condensed', sans-serif", fontSize:'0.85rem', textTransform:'uppercase', letterSpacing:'0.04em', color:C.text }}>{p.nombre_parque}</div>
                                    <small style={{ color:C.textDim, fontSize:'10px' }}>{p.maquinas?.length} WTGs · {p.modelo_maquina} · {p.num_tramos} {'sections'}{p.dt_aparte?` · ${'With DT'}`:''}</small>
                                    {p.fecha_creacion && <div style={{ fontSize:'10px', color:C.textDim, marginTop:'2px' }}>{'Created:'} {p.fecha_creacion}</div>}
                                    {p.direccion && <div style={{ fontSize:'10px', color:C.textDim, marginTop:'2px' }}>📍 {p.direccion}</div>}
                                    {parqueSinPagar(p) && (
                                        <div style={{ marginTop:'6px' }}>
                                            <div style={{ fontSize:'11px', color:C.orange, fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif" }}>🔒 {MSG_ACTIVATE_PARK}</div>
                                            <button onClick={(e)=>{e.stopPropagation();iniciarCheckout('park', p.id);}} style={{ marginTop:'6px', padding:'4px 12px', background:C.orange, color:C.bgDark, border:'none', cursor:'pointer', borderRadius:'6px', fontWeight:700, fontSize:'11px', textTransform:'uppercase', fontFamily:"'Barlow Condensed', sans-serif" }}>ACTIVATE (€270)</button>
                                        </div>
                                    )}
                                    <div style={{ display:'flex', gap:'6px', marginTop:'8px' }}>
                                        {!esTecnico && (
                                        <button onClick={(e)=>{e.stopPropagation();exportarExcel(p);}} title={'Export Excel'}
                                            style={{ display:'flex', alignItems:'center', gap:'4px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'4px 9px', fontSize:'11px', fontWeight:600, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                            <FileSpreadsheet size={11}/> {'Export'}
                                        </button>
                                        )}
                                        {!esTecnico && (
                                        <button onClick={(e)=>{e.stopPropagation();generarPDFParque(p);}} title={'PDF report'}
                                            style={{ display:'flex', alignItems:'center', gap:'4px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'4px 9px', fontSize:'11px', fontWeight:600, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                            📄 PDF
                                        </button>
                                        )}
                                        <button disabled={soloLectura || parqueSinPagar(p)} onClick={(e)=>{e.stopPropagation();setModalHerramientas(p.nombre_parque);}} title={'Project tools'}
                                            style={{ opacity: (soloLectura || parqueSinPagar(p)) ? 0.4 : 1, display:'flex', alignItems:'center', gap:'4px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'4px 9px', fontSize:'11px', fontWeight:600, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                            <Settings size={11}/> {'Tools'}
                                        </button>
                                    </div>
                                    {(()=>{ const pct=p.maquinas?.length>0?Math.round(p.maquinas.reduce((a,m)=>a+calcularProgreso(m.tareas),0)/p.maquinas?.length):0; return (<>
                                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'8px', marginBottom:'3px' }}>
                                            <span style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>{'Farm progress'}</span>
                                            <span style={{ fontSize:'13px', fontWeight:700, color: pct===100?C.green:C.orange, fontFamily:"'Barlow Condensed', sans-serif" }}>{pct}%</span>
                                        </div>
                                        <div style={{ height:'4px', background:C.bgDark, overflow:'hidden', marginTop:'4px' }}>
                                            <div style={{ width:pct+'%', height:'100%', background: pct===100?C.green:C.orange, transition:'width 0.3s' }}></div>
                                        </div>
                                    </>); })()}
                                </div>
                            ))}
                        </aside>
                    )}

                    {/* Mapa (siempre visible) + bottom sheet en móvil */}
                    <div style={{ flex:1, position:'relative', overflow:'hidden' }}>
                        <MapContainer center={centroMapa} zoom={6} style={{ height:'100%', width:'100%' }}>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                            <RecenterMap coords={centroMapa}/>
                            {activeProject?.maquinas?.filter(m => m.lat !== null && m.lng !== null && m.lat !== undefined && m.lng !== undefined).map(m => (
                                <Marker key={m.id} position={[m.lat,m.lng]} icon={crearTurbineIcon(calcularProgreso(m.tareas))}>
                                    <Popup minWidth={250}>
                                        <div style={{ padding:'5px', fontFamily:"'Barlow', sans-serif", background:C.bgCard, color:C.text }}>
                                            <div style={{ display:'flex', justifyContent:'space-between', borderBottom:`1px solid ${C.border}`, paddingBottom:'5px', marginBottom:'8px' }}>
                                                <strong style={{ color:C.text, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, textTransform:'uppercase' }}>{m.nombre}</strong><span style={{ fontSize:'10px', color:C.textDim }}>{m.lat && m.lng ? `📍 ${m.lat.toFixed(4)}, ${m.lng.toFixed(4)}` : '📍 Sin ubicar'}</span>
                                            </div>
                                            {m.idMaquina && <p style={{ margin:'0 0 4px', fontSize:'12px', color:C.textDim }}>ID: <strong style={{ fontFamily:'monospace' }}>{m.idMaquina}</strong></p>}
                                            <p style={{ margin:'0 0 4px', fontSize:'12px', color:C.textDim }}>{'Model:'} <strong>{m.modelo_especifico||'N/A'}</strong></p>
                                            <p style={{ margin:'0 0 10px', fontSize:'12px', color:C.textDim }}>{m.tramos} {'sections'} · {activeProject.dt_aparte?'With DT':'No DT'}</p>
                                            <div style={{ marginBottom:'12px' }}>
                                                <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', marginBottom:'4px' }}><span style={{ color:C.textDim }}>{'Progress'}</span><strong style={{ color:C.orange, fontFamily:"'Barlow Condensed', sans-serif" }}>{calcularProgreso(m.tareas)}%</strong></div>
                                                <div style={{ width:'100%', height:'8px', background:C.bgDark, overflow:'hidden' }}>
                                                    <div style={{ width:calcularProgreso(m.tareas)+'%', height:'100%', background: calcularProgreso(m.tareas)===100?C.green:C.orange, transition:'width 0.5s' }}></div>
                                                </div>
                                            </div>
                                            <button onClick={()=>{ setMaquinaSeleccionada(m); setProyectoSeleccionado(activeProject); setView('detalle-maquina'); }}
                                                style={{ width:'100%', padding:'10px', background:C.orange, color:C.bgDark, border:'none', cursor:'pointer', fontWeight:700, fontSize:'13px', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'8px' }}>
                                                ⚙️ Manage Subtasks
                                            </button>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>

                        {/* Móvil: bottom sheet flotante */}
                        {esMobil && (
                        <div style={{
                            position:'absolute', bottom:0, left:0, right:0, zIndex:1000,
                            background: C.bgSheet,
                            borderTop:`2px solid ${C.orange}`,
                            borderRadius:'12px 12px 0 0',
                            transition:'height 0.3s ease',
                            height: mapBottomSheetExpanded ? '50vh' : '60px',
                            display:'flex', flexDirection:'column', overflow:'hidden',
                        }}>
                            {/* Handle */}
                            <div onClick={()=>setMapBottomSheetExpanded(v=>!v)}
                                style={{ flexShrink:0, height:'28px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', userSelect:'none' }}>
                                <div style={{ width:'36px', height:'4px', borderRadius:'2px', background:C.borderMid }}/>
                            </div>

                            {/* Colapsado: nombre + progreso del parque activo */}
                            {!mapBottomSheetExpanded && (
                                <div onClick={()=>setMapBottomSheetExpanded(true)}
                                    style={{ padding:'0 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer', flex:1 }}>
                                    {activeProject ? (()=>{
                                        const pct = activeProject.maquinas?.length>0 ? Math.round(activeProject.maquinas.reduce((a,m)=>a+calcularProgreso(m.tareas),0)/activeProject.maquinas.length) : 0;
                                        return (<>
                                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'0.85rem', textTransform:'uppercase', letterSpacing:'0.04em', color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'200px' }}>{activeProject.nombre_parque}</span>
                                            <div style={{ flex:1, height:'4px', background:C.bgCard, overflow:'hidden', borderRadius:'2px' }}>
                                                <div style={{ width:pct+'%', height:'100%', background: pct===100?C.green:C.orange, transition:'width 0.3s' }}/>
                                            </div>
                                            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'0.85rem', color: pct===100?C.green:C.orange, whiteSpace:'nowrap' }}>{pct}%</span>
                                        </>);
                                    })() : (
                                        <span style={{ color:C.textDim, fontSize:'0.8rem', fontFamily:"'Barlow Condensed',sans-serif" }}>{'No active project — tap to select'}</span>
                                    )}
                                </div>
                            )}

                            {/* Expandido: lista completa */}
                            {mapBottomSheetExpanded && (
                                <div style={{ flex:1, overflowY:'auto', padding:'0 16px 16px' }}>
                                    <h3 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:C.textDim, marginBottom:'14px', borderBottom:`1px solid ${C.border}`, paddingBottom:'8px' }}>{'Active Projects'}</h3>
                                    {projects.length===0 && <p style={{ color:C.textDim, fontSize:'0.82rem' }}>{'No projects. Create one first.'}</p>}
                                    {projects.map(p => (
                                        <div key={p.nombre_parque} onClick={()=>{ setActiveProject(p); if(p.maquinas?.length>0) setCentroMapa([p.maquinas[0].lat,p.maquinas[0].lng]); setMapBottomSheetExpanded(false); }}
                                            style={{ position:'relative', padding:'12px', background: activeProject?.nombre_parque===p.nombre_parque?C.bgHover:C.bgCard, borderLeft:`3px solid ${activeProject?.nombre_parque===p.nombre_parque?C.orange:'transparent'}`, border:`1px solid ${C.border}`, cursor:'pointer', marginBottom:'8px', borderRadius:'8px' }}>
                                            {!esTecnico && !soloLectura && !parqueSinPagar(p) && (
                                                <button onClick={(e)=>{ e.stopPropagation(); eliminarProyecto(p.nombre_parque); }} title={'Delete'}
                                                    style={{ position:'absolute', top:'10px', right:'10px', background:'transparent', border:'none', color:'#c0392b', cursor:'pointer', padding:'4px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <Trash2 size={15}/>
                                                </button>
                                            )}
                                            {!esTecnico && (
                                                <button onClick={(e)=>{ e.stopPropagation(); const enlace=`${window.location.origin}/parque/${p.slug}`; setModalEnlace({ enlace, pin: p.pin, compartir: true }); }} title={'Share access link'}
                                                    style={{ position:'absolute', top:'10px', right:'58px', background:'transparent', border:'none', color:C.orange, cursor:'pointer', padding:'4px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    🔗
                                                </button>
                                            )}
                                            {!esTecnico && !soloLectura && !parqueSinPagar(p) && (
                                                <button onClick={(e)=>{
                                                    e.stopPropagation();
                                                    const proj = projects.find(proj=>proj.nombre_parque===p.nombre_parque);
                                                    if (!proj) return;
                                                    setNombre(proj.nombre_parque); setModeloGeneral(proj.modelo_maquina||''); setNumMaquinasTotal(proj.maquinas?.length||1); setNumTramosTorre(proj.num_tramos||4); setDtAparte(proj.dt_aparte||false);
                                                    const maqsConCoords = proj.maquinas?.filter(m=>m.lat&&m.lng)||[];
                                                    setMaquinasUbicadas(maqsConCoords); setDireccionInput(proj.direccion||''); setDireccionCompleta(proj.direccion||'');
                                                    if (maqsConCoords.length>0) { const avgLat=maqsConCoords.reduce((s,m)=>s+m.lat,0)/maqsConCoords.length; const avgLng=maqsConCoords.reduce((s,m)=>s+m.lng,0)/maqsConCoords.length; setCentroMapa([avgLat,avgLng]); setFitBoundsCoords(maqsConCoords.map(m=>[m.lat,m.lng])); } else { setFitBoundsCoords(null); }
                                                    setModoEdicion(true); setParqueEditando(proj.nombre_parque); setView('admin');
                                                }} title="Editar parque"
                                                    style={{ position:'absolute', top:'10px', right:'36px', background:'transparent', border:'none', color:C.textMuted, cursor:'pointer', padding:'4px', display:'flex', alignItems:'center' }}>
                                                    <Pencil size={15}/>
                                                </button>
                                            )}
                                            <div style={{ fontWeight:700, marginBottom:'2px', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'0.85rem', textTransform:'uppercase', letterSpacing:'0.04em', color:C.text }}>{p.nombre_parque}</div>
                                            <small style={{ color:C.textDim, fontSize:'10px' }}>{p.maquinas?.length} WTGs · {p.modelo_maquina} · {p.num_tramos} {'sections'}{p.dt_aparte?` · ${'With DT'}`:''}</small>
                                            {p.fecha_creacion && <div style={{ fontSize:'10px', color:C.textDim, marginTop:'2px' }}>{'Created:'} {p.fecha_creacion}</div>}
                                            {p.direccion && <div style={{ fontSize:'10px', color:C.textDim, marginTop:'2px' }}>📍 {p.direccion}</div>}
                                            {parqueSinPagar(p) && (
                                                <div style={{ marginTop:'6px' }}>
                                                    <div style={{ fontSize:'11px', color:C.orange, fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif" }}>🔒 {MSG_ACTIVATE_PARK}</div>
                                                    <button onClick={(e)=>{e.stopPropagation();iniciarCheckout('park', p.id);}} style={{ marginTop:'6px', padding:'4px 12px', background:C.orange, color:C.bgDark, border:'none', cursor:'pointer', borderRadius:'6px', fontWeight:700, fontSize:'11px', textTransform:'uppercase', fontFamily:"'Barlow Condensed', sans-serif" }}>ACTIVATE (€270)</button>
                                                </div>
                                            )}
                                            <div style={{ display:'flex', gap:'6px', marginTop:'8px' }}>
                                                {!esTecnico && (
                                                <button onClick={(e)=>{e.stopPropagation();exportarExcel(p);}} title={'Export Excel'}
                                                    style={{ display:'flex', alignItems:'center', gap:'4px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'4px 9px', fontSize:'11px', fontWeight:600, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                                    <FileSpreadsheet size={11}/> {'Export'}
                                                </button>
                                                )}
                                                {!esTecnico && (
                                                <button onClick={(e)=>{e.stopPropagation();generarPDFParque(p);}} title={'PDF report'}
                                                    style={{ display:'flex', alignItems:'center', gap:'4px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'4px 9px', fontSize:'11px', fontWeight:600, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                                    📄 PDF
                                                </button>
                                                )}
                                                <button disabled={soloLectura || parqueSinPagar(p)} onClick={(e)=>{e.stopPropagation();setModalHerramientas(p.nombre_parque);}} title={'Project tools'}
                                                    style={{ opacity: (soloLectura || parqueSinPagar(p)) ? 0.4 : 1, display:'flex', alignItems:'center', gap:'4px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', padding:'4px 9px', fontSize:'11px', fontWeight:600, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                                    <Settings size={11}/> {'Tools'}
                                                </button>
                                            </div>
                                            {(()=>{ const pct=p.maquinas?.length>0?Math.round(p.maquinas.reduce((a,m)=>a+calcularProgreso(m.tareas),0)/p.maquinas?.length):0; return (<>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'8px', marginBottom:'3px' }}>
                                                    <span style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase' }}>{'Farm progress'}</span>
                                                    <span style={{ fontSize:'13px', fontWeight:700, color: pct===100?C.green:C.orange, fontFamily:"'Barlow Condensed',sans-serif" }}>{pct}%</span>
                                                </div>
                                                <div style={{ height:'4px', background:C.bgDark, overflow:'hidden', marginTop:'4px' }}>
                                                    <div style={{ width:pct+'%', height:'100%', background: pct===100?C.green:C.orange, transition:'width 0.3s' }}/>
                                                </div>
                                            </>); })()}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                    </>)}
                </>)}

                {/* ── ADMIN ── */}
                {view==='admin' && (
                    <div style={{ flex:1, display:'flex', flexDirection: esMobil ? 'column' : 'row', overflowY: esMobil ? 'auto' : undefined }}>
                        <div style={{ width: esMobil ? '100%' : '350px', padding: esMobil ? '16px' : '20px', background:C.bgDark, borderRight: esMobil ? 'none' : `1px solid ${C.border}`, borderBottom: esMobil ? `1px solid ${C.border}` : 'none', overflowY: esMobil ? 'visible' : 'auto', display:'flex', flexDirection:'column' }}>
                            <h3 style={{ marginBottom: esMobil ? '14px' : '20px', color:C.text, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, fontSize: esMobil ? '0.85rem' : '1rem', textTransform:'uppercase', letterSpacing:'0.06em' }}>{modoEdicion ? `✏️ Editando: ${parqueEditando}` : 'Wind Farm Configuration'}</h3>
                            {modoEdicion && (
                                <div style={{ background:C.bgDark, border:`1px solid ${C.orange}`, padding:'10px 14px', marginBottom:'16px', fontSize:'11px', color:C.orange, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', borderRadius:'8px' }}>
                                    ✏️ Edit mode — Manage the wind farm turbines on the map
                                </div>
                            )}
                            {!modoEdicion && (<>
                            <div style={{ position:'relative', marginBottom:'16px' }}>
                                <label style={labelStyle}>{'Address'}</label>
                                <div style={{ display:'flex', gap:'8px' }}>
                                    <input
                                        type="text"
                                        value={direccionInput}
                                        onChange={e => handleDireccionChange(e.target.value)}
                                        placeholder={'Search wind farm address...'}
                                        style={{ ...inputStyle, flex:1 }}
                                    />
                                    {buscandoDireccion && (
                                        <div style={{ display:'flex', alignItems:'center', padding:'0 10px', color: C.textDim, fontSize:'12px' }}>
                                            ...
                                        </div>
                                    )}
                                </div>
                                {sugerencias.length > 0 && (
                                    <div style={{
                                        position:'absolute', top:'100%', left:0, right:0,
                                        background: C.bgCard, border:`1px solid ${C.borderMid}`,
                                        zIndex:9999, maxHeight:'200px', overflowY:'auto',
                                        boxShadow:'0 4px 12px rgba(0,0,0,0.15)', borderRadius:'8px',
                                    }}>
                                        {sugerencias.map((item, i) => (
                                            <div
                                                key={i}
                                                onClick={() => seleccionarSugerencia(item)}
                                                style={{
                                                    padding:'10px 14px', cursor:'pointer',
                                                    fontSize:'12px', color: C.text,
                                                    borderBottom:`1px solid ${C.border}`,
                                                    background:'transparent',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = C.bgHover}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <div style={{ fontWeight:600, fontSize:'13px' }}>
                                                    {item.address?.city || item.address?.town || item.address?.village || item.address?.county || ''}
                                                    {item.address?.country ? `, ${item.address.country}` : ''}
                                                </div>
                                                <div style={{ color: C.textDim, fontSize:'11px', marginTop:'2px' }}>
                                                    {item.display_name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            </>)}
                            <label style={labelStyle}>{'Project Name'}</label>
                            <input style={inputStyle} value={nombre} onChange={e=>setNombre(e.target.value)} placeholder={'Wind farm name...'} disabled={modoEdicion}/>
                            <label style={labelStyle}>{'Machine model'}</label>
                            <input style={inputStyle} value={modeloGeneral} onChange={e=>setModeloGeneral(e.target.value)} placeholder={'E.g: SG 5.0-145'} disabled={modoEdicion}/>
                            <div style={{ display:'grid', gridTemplateColumns: esMobil ? '1fr' : '1fr 1fr', gap:'10px', marginTop:'10px' }}>
                                <div>
                                    <label style={labelStyle}>{'No. WTGs'}</label>
                                    <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                                        <input
                                            type="number"
                                            min="1"
                                            style={{
                                                ...inputStyle,
                                                flex: esMobil ? 1 : undefined,
                                                background: modoEdicion ? C.bgDark : inputStyle.background,
                                                color: modoEdicion ? C.orange : C.text,
                                                cursor: modoEdicion ? 'default' : 'text',
                                                fontWeight: modoEdicion ? 700 : 400,
                                            }}
                                            value={modoEdicion ? maquinasUbicadas.length : numMaquinasTotal}
                                            onChange={e => !modoEdicion && setNumMaquinasTotal(parseInt(e.target.value)||1)}
                                            readOnly={modoEdicion}
                                        />
                                        {esMobil && !modoEdicion && (
                                            <>
                                                <button onClick={() => setNumMaquinasTotal(Math.max(1, numMaquinasTotal-1))} style={{ padding:'8px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, cursor:'pointer', borderRadius:'6px', fontSize:'1.2rem', fontWeight:'bold', height:'40px', width:'40px', display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                                                <button onClick={() => setNumMaquinasTotal(numMaquinasTotal+1)} style={{ padding:'8px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, cursor:'pointer', borderRadius:'6px', fontSize:'1.2rem', fontWeight:'bold', height:'40px', width:'40px', display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                                            </>
                                        )}
                                    </div>
                                    {modoEdicion && (
                                        <div style={{ fontSize:'10px', color:C.textDim, marginTop:'4px', fontStyle:'italic' }}>
                                            Updated automatically
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label style={labelStyle}>{'Sections (3–8)'}</label>
                                    <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                                        <input
                                            type="number"
                                            min="3"
                                            max="8"
                                            style={{
                                                ...inputStyle,
                                                flex: esMobil ? 1 : undefined,
                                                background: modoEdicion ? C.bgDark : inputStyle.background,
                                                color: modoEdicion ? C.orange : C.text,
                                                cursor: modoEdicion ? 'default' : 'text',
                                                fontWeight: modoEdicion ? 700 : 400,
                                            }}
                                            value={numTramosTorre}
                                            onChange={e => !modoEdicion && setNumTramosTorre(Math.min(Math.max(parseInt(e.target.value)||3,3),8))}
                                            readOnly={modoEdicion}
                                        />
                                        {esMobil && !modoEdicion && (
                                            <>
                                                <button onClick={() => setNumTramosTorre(Math.max(3, numTramosTorre-1))} style={{ padding:'8px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, cursor:'pointer', borderRadius:'6px', fontSize:'1.2rem', fontWeight:'bold', height:'40px', width:'40px', display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                                                <button onClick={() => setNumTramosTorre(Math.min(8, numTramosTorre+1))} style={{ padding:'8px 12px', background:C.bgDark, border:`1px solid ${C.borderMid}`, color:C.text, cursor:'pointer', borderRadius:'6px', fontSize:'1.2rem', fontWeight:'bold', height:'40px', width:'40px', display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <label style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'15px', fontSize:'0.85rem', cursor: modoEdicion ? 'not-allowed' : 'pointer', color:C.textMuted }}>
                                <input type="checkbox" checked={dtAparte} onChange={e=>setDtAparte(e.target.checked)} disabled={modoEdicion}/>
                                <strong style={{ color:C.text }}>{'With DT'}</strong>
                                <span style={{ color:C.textDim, fontSize:'0.75rem' }}>{'(adds DT subtask in each phase)'}</span>
                            </label>
                            <div style={maquinasStatus}>
                                <strong>{'Located:'} {maquinasUbicadas.length} / {numMaquinasTotal}</strong>
                                {!modoEdicion && <button onClick={()=>setMaquinasUbicadas([])} style={{ border:'none', background:'none', color:'red', cursor:'pointer' }}><Trash2 size={16}/></button>}
                            </div>
                            {maquinasUbicadas.length>0 && (
                                <div style={{ marginTop:'8px', maxHeight:'120px', overflowY:'auto' }}>
                                    {maquinasUbicadas.map((m,i)=>(
                                        <div key={m.id} style={{ fontSize:'12px', color:C.textDim, padding:'3px 0', borderBottom:`1px solid ${C.border}` }}>
                                            📍 {m.nombre || <em style={{ color:C.textDim }}>{`WTG-${String(i+1).padStart(2,"0")} (auto)`}</em>} — {m.lat != null ? m.lat.toFixed(4) : '—'}, {m.lng != null ? m.lng.toFixed(4) : '—'}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <button onClick={modoEdicion ? guardarEdicion : guardarParque} style={saveBtnStyle}>{modoEdicion ? 'Save Changes' : 'Save Wind Farm'}</button>
                            {modoEdicion && (
                                <button onClick={() => { setModoEdicion(false); setParqueEditando(null); setMaquinasUbicadas([]); setNombre(''); setModeloGeneral(''); setView('map'); }}
                                    style={{ marginTop:'8px', padding:'10px', background:'transparent', border:`1px solid ${C.borderMid}`, color:C.textMuted, cursor:'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'12px', textTransform:'uppercase', letterSpacing:'0.06em', borderRadius:'8px' }}>
                                    ← Volver al Mapa
                                </button>
                            )}
                            <div style={{ marginTop:'10px', padding:'10px', background:C.bgDark, border:`1px solid ${C.border}`, fontSize:'11px', color:C.green, textAlign:'center', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'8px' }}>
                                {'💾 Data is saved in your browser automatically'}
                            </div>
                        </div>
                        <div style={{ flex: esMobil ? 'none' : 1, width: esMobil ? '100%' : undefined, height: esMobil ? '300px' : undefined, minHeight: esMobil ? '300px' : undefined, position:'relative' }}>
                            <div style={mapOverlay}>
                                <Crosshair size={14}/>
                                {modoEdicion
                                    ? '✏️ Click on the map to add a new WTG · Click on a marker to remove it'
                                    : 'Double-click on the map to place turbines'
                                }
                            </div>
                            {tooltipMaquina && (
                                <div style={{
                                    position:'absolute', bottom:'20px', left:'50%',
                                    transform:'translateX(-50%)', zIndex:1000,
                                    background: C.orange, color: C.bgDark,
                                    padding:'10px 20px', fontSize:'12px',
                                    fontFamily:"'Barlow Condensed', sans-serif",
                                    fontWeight:700, textTransform:'uppercase',
                                    letterSpacing:'0.08em', whiteSpace:'nowrap',
                                    boxShadow:'0 4px 12px rgba(0,0,0,0.3)', borderRadius:'8px',
                                }}>
                                    {'✓ Machine placed — click the marker to edit it'}
                                </div>
                            )}
                            <MapContainer center={centroMapa} zoom={13} style={{ height:'100%' }}>
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                                <ClickToAddMachine onAdd={añadirMaquina} disabled={!modoEdicion && maquinasUbicadas.length>=numMaquinasTotal} ignoreNextRef={ignorarProximoClick}/>
                                <RecenterMap coords={centroMapa}/>
                                <FitBoundsMap bounds={fitBoundsCoords}/>
                                {maquinasUbicadas.filter(m => m.lat !== null && m.lng !== null && m.lat !== undefined && m.lng !== undefined).map(m=>(
                                    <Marker key={m.id} position={[m.lat,m.lng]} icon={crearTurbineIcon(calcularProgreso(m.tareas))}>
                                        <Popup minWidth={220}>
                                            <div style={{ padding:'5px' }}>
                                                <strong style={{ display:'block', marginBottom:'8px' }}>{'Adjust turbine'}</strong>
                                                <label style={popupLabel}>{'Name'}</label>
                                                <input style={popupInput} value={m.nombre} onChange={e=>actualizarDatoMaquina(m.id,'nombre',e.target.value)} placeholder={`WTG-${String(maquinasUbicadas.indexOf(m)+1).padStart(2,"0")} (auto)`}/>
                                                <label style={popupLabel}>{'Machine ID'}</label>
                                                <input style={popupInput} value={m.idMaquina||''} onChange={e=>actualizarDatoMaquina(m.id,'idMaquina',e.target.value)} placeholder={'E.g: WTG-001-A'}/>
                                                <label style={popupLabel}>{'Lat / Lng'}</label>
                                                <div style={{ display:'flex', gap:'5px' }}>
                                                    <input style={popupInput} type="number" step="0.0001"
                                                        value={coordsTemp[m.id]?.lat ?? m.lat}
                                                        onChange={e=>setCoordsTemp(prev=>({...prev,[m.id]:{...prev[m.id],lat:e.target.value}}))}
                                                        onBlur={e=>{actualizarDatoMaquina(m.id,'lat',e.target.value);setCoordsTemp(prev=>{const n={...prev};if(n[m.id])delete n[m.id].lat;return n;});}}/>
                                                    <input style={popupInput} type="number" step="0.0001"
                                                        value={coordsTemp[m.id]?.lng ?? m.lng}
                                                        onChange={e=>setCoordsTemp(prev=>({...prev,[m.id]:{...prev[m.id],lng:e.target.value}}))}
                                                        onBlur={e=>{actualizarDatoMaquina(m.id,'lng',e.target.value);setCoordsTemp(prev=>{const n={...prev};if(n[m.id])delete n[m.id].lng;return n;});}}/>
                                                </div>
                                                <label style={popupLabel}>{'Model'}</label>
                                                <input style={popupInput} value={m.modelo_especifico} onChange={e=>actualizarDatoMaquina(m.id,'modelo_especifico',e.target.value)}/>
                                                {modoEdicion && (
                                                    <button
                                                        onClick={(e) => {
                                                            L.DomEvent.stopPropagation(e);
                                                            ignorarProximoClick.current = true;
                                                            const tieneDatos = m.tareas?.some(f => f.subtareas?.some(s => s.hecho || s.serial || s.item || s.fecha));
                                                            const msg = tieneDatos
                                                                ? `⚠️ "${m.nombre}" has registered data.\n\nRemoving it will erase all its records.\n\nRemove anyway?`
                                                                : `Remove "${m.nombre}" from the wind farm?`;
                                                            if (!window.confirm(msg)) { ignorarProximoClick.current = false; return; }
                                                            setMaquinasUbicadas(prev => prev.filter(maq => maq.id !== m.id));
                                                        }}
                                                        style={{ width:'100%', marginTop:'6px', padding:'5px', background:'transparent', border:'1px solid #c0392b', color:'#c0392b', cursor:'pointer', fontSize:'11px', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, textTransform:'uppercase', borderRadius:'6px' }}
                                                    >
                                                        🗑 Remove this WTG
                                                    </button>
                                                )}
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </div>
                    </div>
                )}

                {view==='detalle-maquina' && maquinaSeleccionada && (
                    <div style={{ flex:1, display:'flex', flexDirection:'column', overflowY:'auto' }}>
                        {/* Progress strip */}
                        <div style={{ background:C.bgDark, padding:'10px 24px 12px', borderBottom:`3px solid ${C.orange}`, flexShrink:0 }}>
                            <div style={{ maxWidth:'860px', margin:'0 auto' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                                    <span style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em' }}>{'Electromechanical Assembly Progress'}</span>
                                    <span style={{ color:C.orange, fontWeight:700, fontSize:'1.2rem', fontFamily:"'Barlow Condensed', sans-serif" }}>{calcularProgreso(maquinaSeleccionada.tareas)}%</span>
                                </div>
                                <div style={{ width:'100%', height:'6px', background:C.border, overflow:'hidden' }}>
                                    <div style={{ width:calcularProgreso(maquinaSeleccionada.tareas)+'%', height:'100%', background: calcularProgreso(maquinaSeleccionada.tareas)===100?C.green:C.orange, transition:'width 0.5s' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Content area */}
                        <div style={{ flex:1, background:C.bg, padding:'24px', overflowY:'auto' }}>
                            <div style={{ maxWidth:'860px', margin:'0 auto' }}>
                                {/* Section 1 — Tools & Registry */}
                                <div style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.1em', fontWeight:700, marginBottom:'12px' }}>{'TOOLS & REGISTRY'}</div>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'24px' }}>
                                    <button disabled={lecturaParqueAbierto} onClick={()=>setModalHerramientas(proyectoSeleccionado?.nombre_parque)}
                                        style={{ opacity: lecturaParqueAbierto ? 0.4 : 1, background:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'10px', padding:'14px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
                                        <Wrench size={22} color={C.textMuted}/>
                                        <span style={{ fontSize:'11px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', color:C.text }}>{'TOOLS'}</span>
                                    </button>
                                    <button onClick={()=>setModalBoltRegister(true)}
                                        style={{ background:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'6px', padding:'14px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px', position:'relative' }}>
                                        <Bolt size={22} color={C.textMuted}/>
                                        <span style={{ fontSize:'11px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', color:C.text }}>{'BOLTS'}</span>
                                        {maquinaSeleccionada?.boltRegister?.filter(b=>b.registrado).length > 0 && (
                                            <span style={{ position:'absolute', top:'8px', right:'8px', background:C.orange, color:C.bgDark, borderRadius:'10px', padding:'0 5px', fontSize:'9px', fontWeight:700 }}>
                                                {maquinaSeleccionada.boltRegister.filter(b=>b.registrado).length}
                                            </span>
                                        )}
                                    </button>
                                    <button onClick={()=>{ setProyectoRegistro(proyectoSeleccionado); setMaquinaRegistroSel(maquinaSeleccionada); setView('registro-componentes'); }}
                                       style={{ background:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'10px', padding:'14px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
                                       <ClipboardList size={22} color={C.textMuted}/>
                                       <span style={{ fontSize:'11px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', color:C.text }}>{'COMPONENT REGISTRY'}</span>
                                    </button>
                                    {!esTecnico && (
                                        <button onClick={()=>proyectoSeleccionado && generarPDFWTG(proyectoSeleccionado, maquinaSeleccionada)}
                                            style={{ background:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'10px', padding:'14px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
                                            <FileText size={22} color={C.textMuted}/>
                                            <span style={{ fontSize:'11px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.06em', color:C.text }}>{'PDF WTG'}</span>
                                        </button>
                                    )}
                                </div>

                                {/* Divider */}
                                <div style={{ height:'1px', background:C.border, marginBottom:'24px' }}></div>

                                {/* Section 2 — Assembly Phases */}
                                <div style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.1em', fontWeight:700, marginBottom:'12px' }}>{'ASSEMBLY PHASES'}</div>
                                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                                    {maquinaSeleccionada.tareas.map(fase => {
                                        const hechas = fase.subtareas.filter(s=>s.hecho).length;
                                        const total  = fase.subtareas.length;
                                        const pct    = total>0 ? Math.round((hechas/total)*100) : 0;
                                        return (
                                            <div key={fase.id} onClick={() => setFaseModalAbiertaId(fase.id)}
                                                style={{ background:C.bgCard, border:`1px solid ${fase.hecho?C.green:C.border}`, borderRadius:'10px', overflow:'hidden', cursor:'pointer' }}>
                                                <div style={{ padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                                                    <div style={{ width:'22px', height:'22px', flexShrink:0, background: fase.hecho?C.green:C.orange, color: fase.hecho?'#fff':C.bgDark, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:700, fontFamily:"'Barlow Condensed', sans-serif", borderRadius:'2px' }}>
                                                        {fase.hecho?'✓':fase.id}
                                                    </div>
                                                    <span style={{ flex:1, fontSize:'14px', fontWeight:500, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', color:C.text }}>{fase.nombre}</span>
                                                    <span style={{ fontSize:'11px', fontWeight:700, color:C.orange, background:'rgba(224,123,26,0.12)', padding:'2px 10px', fontFamily:"'Barlow Condensed', sans-serif" }}>{fase.peso}%</span>
                                                    <ChevronRight size={16} color={C.textDim}/>
                                                </div>
                                                <div style={{ height:'4px', background:C.bgDark, overflow:'hidden' }}>
                                                    <div style={{ width:pct+'%', height:'100%', background: fase.hecho?C.green:C.orange, transition:'width 0.4s' }}></div>
                                                </div>
                                                <div style={{ padding:'6px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                                    <span style={{ fontSize:'10px', color:C.textDim, fontFamily:"'Barlow Condensed', sans-serif" }}>{`${hechas}/${total} completed`}</span>
                                                    <span style={{ fontSize:'11px', fontWeight:700, color: pct>0?C.orange:C.textDim, fontFamily:"'Barlow Condensed', sans-serif" }}>{pct}%</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {faseModalAbiertaId && (() => {
                                    const faseActual = maquinaSeleccionada.tareas.find(f => f.id === faseModalAbiertaId);
                                    return faseActual ? (
                                        <ModalFase
                                            fase={faseActual}
                                            maquinaId={maquinaSeleccionada.id}
                                            onClose={() => setFaseModalAbiertaId(null)}
                                            onAbrirSubtarea={(mId, fId, idx) => { setModalOrigen(null); abrirModal(mId, fId, idx); }}
                                            infoBadge={infoBadge}
                                            tipoDeSubtarea={tipoDeSubtarea}
                                            modoLecturaCard={modoLecturaCard}
                                            hoveredSubKey={hoveredSubKey}
                                            setHoveredSubKey={setHoveredSubKey}
                                            proyectoSeleccionado={proyectoSeleccionado}
                                            maquina={maquinaSeleccionada}
                                            generarPDFFase={generarPDFFase}
                                            esTecnico={esTecnico}
                                            C={C}
                                        />
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── REGISTRO DE COMPONENTES ── */}
                {view==='registro-componentes' && proyectoRegistro && maquinaRegistroSel && (
                    <div style={{ flex:1, padding:'24px', overflowY:'auto', background:C.bg }}>
                        <div style={{ maxWidth:'900px', margin:'0 auto' }}>
                            {/* Header */}
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
                                <button onClick={()=>{ setMaquinaSeleccionada(maquinaRegistroSel); setProyectoSeleccionado(proyectoRegistro); setView('detalle-maquina'); }} style={{ background:C.bgCard, border:`1px solid ${C.borderMid}`, color:C.textMuted, padding:'8px 14px', cursor:'pointer', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', fontSize:'0.8rem', borderRadius:'8px' }}>{'← Back to WTG'}</button>
                                <div style={{ textAlign:'right' }}>
                                    <h2 style={{ margin:0, color:C.text, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:900, textTransform:'uppercase', fontSize:'1.5rem', letterSpacing:'0.04em' }}>{maquinaRegistroSel.nombre}</h2>
                                    <span style={{ color:C.textDim, fontSize:'0.82rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase' }}>{'Component Registry'} · {proyectoRegistro.nombre_parque}</span>
                                </div>
                            </div>

                            {/* SECTION 1: Components from Tasks */}
                            <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, padding:'18px', marginBottom:'20px', borderRadius:'8px' }}>
                                <SectionTitle icon={<Package size={13}/>} title={'Components registered in Tasks'} C={C} />
                                {(()=>{
                                    const descarga = maquinaRegistroSel.tareas?.find(f => f.id === 1);
                                    const comps = (descarga?.subtareas.map((s, i) => ({ sub: s, originalIdx: i })).filter(({ sub }) => sub._tipo===TIPO.COMPONENTE||sub._tipo===TIPO.PALA)) || [];
                                    if (comps.length === 0) return <p style={{ color:C.textDim, fontSize:'0.85rem' }}>{'No components with serial registered yet in Unloading.'}</p>;
                                    return (
                                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:'10px' }}>
                                            {comps.map(({ sub, originalIdx }) => {
                                                const hKey = `reg-task-${originalIdx}`;
                                                const isHov = hoveredSubKey === hKey;
                                                return (
                                                    <div key={originalIdx}
                                                        onClick={()=>{ setMaquinaSeleccionada(maquinaRegistroSel); setProyectoSeleccionado(proyectoRegistro); setModalOrigen('registro'); abrirModal(maquinaRegistroSel.id, 1, originalIdx); setView('detalle-maquina'); }}
                                                        onMouseEnter={()=>setHoveredSubKey(hKey)}
                                                        onMouseLeave={()=>setHoveredSubKey(null)}
                                                        style={{ background: sub.hecho?C.bgDone:C.bgDark, border:`1px solid ${isHov?C.orange:(sub.hecho?C.green:C.border)}`, borderLeft:`3px solid ${isHov?C.orange:(sub.hecho?C.green:'transparent')}`, padding:'12px 14px', cursor:'pointer', borderRadius:'8px' }}>
                                                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                                                            <span style={{ fontWeight:700, fontSize:'0.85rem', color:C.text, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.02em' }}>{sub.nombre}</span>
                                                            <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', background:C.bgDark, color: sub.hecho?C.green:C.textDim, border:`1px solid ${sub.hecho?C.green:C.borderMid}`, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', borderRadius:'4px' }}>
                                                                {sub.hecho ? 'Registered' : 'Pending'}
                                                            </span>
                                                        </div>
                                                        {sub.serial && <ReadField label={'Serial Number'} value={sub.serial} mono C={C} />}
                                                        {sub.item && <ReadField label={'Item No.'} value={sub.item} mono C={C} />}
                                                        {sub.fabricante && <ReadField label={'Manufacturer'} value={sub.fabricante} C={C} />}
                                                        {(sub.vui || sub?.camposExtra?.['VUI']) && <ReadField label={'VUI'} value={sub.vui || sub?.camposExtra?.['VUI']} mono C={C} />}
                                                        {sub.numSet && <ReadField label={'Set No.'} value={sub.numSet} mono C={C} />}
                                                        {Object.entries(sub.camposExtra || {}).filter(([k,v]) => v).map(([key, val]) => (
                                                            <div key={key} style={{ marginTop:'4px' }}>
                                                                <div style={{ fontSize:'9px', color: C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>{key}</div>
                                                                <div style={{ fontSize:'13px', color: C.text, fontFamily:'monospace', fontWeight:700 }}>{val}</div>
                                                            </div>
                                                        ))}
                                                        {sub.operario && <div style={{ marginTop:'6px', fontSize:'10px', color:C.textDim }}>👤 {sub.operario}{sub.fecha ? ` · 📅 ${sub.fecha}` : ''}</div>}
                                                        {sub.fotos?.length > 0 && <div style={{ marginTop:'4px', fontSize:'10px', color:'#9333ea' }}>📸 {`${sub.fotos.length} photo${sub.fotos.length!==1?"s":""}`}</div>}
                                                        <div style={{ marginTop:'10px', display:'flex', justifyContent:'flex-end', gap:'6px' }}>
                                                            {sub.hecho && <span onClick={(e)=>{e.stopPropagation(); generarPDFComponente(proyectoRegistro, maquinaRegistroSel, descarga?.nombre || 'Descargas', sub);}} style={{ fontSize:'9px', background:'transparent', color:C.textMuted, border:`1px solid ${C.borderMid}`, padding:'3px 8px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', cursor:'pointer', borderRadius:'4px' }}>📄</span>}
                                                            <span style={{ fontSize:'9px', background:C.orange, color:C.bgDark, padding:'3px 8px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', cursor:'pointer', borderRadius:'4px' }}>👁 VIEW</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* SECTION 2: Extra components */}
                            <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, padding:'18px', marginBottom:'20px', borderRadius:'8px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                                        <span style={{ color:C.orange }}><Wrench size={13}/></span>
                                        <span style={{ fontSize:'0.72rem', fontWeight:700, color:C.textDim, textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'Barlow Condensed', sans-serif" }}>{'Additional components'}</span>
                                    </div>
                                    {!nuevoCompForm && !esTecnico && !lecturaParqueAbierto && (
                                        <button onClick={()=>{ setNuevoCompForm(true); setNuevoCompNombre(''); }}
                                            style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 12px', background:C.orange, color:C.bgDark, border:'none', cursor:'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.04em', borderRadius:'6px' }}>
                                            <PlusCircle size={12}/> New component
                                        </button>
                                    )}
                                </div>
                                {nuevoCompForm && (
                                    <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'14px', padding:'10px 14px', background:C.bgDark, border:`2px dashed ${C.orange}`, borderRadius:'8px' }}>
                                        <input
                                            autoFocus
                                            type="text"
                                            value={nuevoCompNombre}
                                            onChange={e=>setNuevoCompNombre(e.target.value)}
                                            onKeyDown={e=>{ if(e.key==='Enter'&&nuevoCompNombre.trim()){añadirComponentePersonalizado(proyectoRegistro.nombre_parque,nuevoCompNombre);setNuevoCompForm(false);setNuevoCompNombre('');} if(e.key==='Escape'){setNuevoCompForm(false);setNuevoCompNombre('');} }}
                                            placeholder="Component name..."
                                            style={{ flex:1, padding:'8px 10px', background:C.bg, border:`1px solid ${C.borderMid}`, color:C.text, fontSize:'0.875rem', outline:'none', fontFamily:"'Barlow', sans-serif", borderRadius:'6px' }}
                                        />
                                        <button
                                            onClick={()=>{ if(nuevoCompNombre.trim()){añadirComponentePersonalizado(proyectoRegistro.nombre_parque,nuevoCompNombre);} setNuevoCompForm(false); setNuevoCompNombre(''); }}
                                            disabled={!nuevoCompNombre.trim()}
                                            style={{ padding:'8px 14px', background: nuevoCompNombre.trim()?C.green:C.borderMid, color: nuevoCompNombre.trim()?'white':C.textDim, border:'none', cursor: nuevoCompNombre.trim()?'pointer':'not-allowed', fontWeight:700, fontSize:'0.8rem', fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', borderRadius:'6px' }}>
                                            Create
                                        </button>
                                        <button onClick={()=>{setNuevoCompForm(false);setNuevoCompNombre('');}} style={{ padding:'8px 12px', background:'transparent', border:`1px solid ${C.borderMid}`, cursor:'pointer', color:C.textMuted, fontSize:'0.8rem', fontFamily:"'Barlow Condensed', sans-serif", display:'flex', borderRadius:'8px' }}><X size={14}/></button>
                                    </div>
                                )}
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:'10px' }}>
                                    {[...EXTRA_COMPONENTES, ...(proyectoRegistro.componentesPersonalizados || [])].map(nombre => {
                                        const ce = (maquinaRegistroSel.componentesExtra || {})[nombre] || componenteExtraVacio();
                                        const hKey = `reg-extra-${nombre}`;
                                        const isHov = hoveredSubKey === hKey;
                                        return (
                                            <div key={nombre}
                                                onClick={()=>abrirModalCompExtra(nombre)}
                                                onMouseEnter={()=>setHoveredSubKey(hKey)}
                                                onMouseLeave={()=>setHoveredSubKey(null)}
                                                style={{ background: ce.hecho?C.bgDone:C.bgDark, border:`1px solid ${isHov?C.orange:(ce.hecho?C.green:C.border)}`, borderLeft:`3px solid ${isHov?C.orange:(ce.hecho?C.green:'transparent')}`, padding:'12px 14px', borderRadius:'8px', cursor:'pointer' }}>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                                                    <span style={{ fontWeight:700, fontSize:'0.85rem', color:C.text, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', letterSpacing:'0.02em' }}>{nombre}</span>
                                                    <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', background:C.bgDark, color: ce.hecho?C.green:C.textDim, border:`1px solid ${ce.hecho?C.green:C.borderMid}`, fontFamily:"'Barlow Condensed', sans-serif", textTransform:'uppercase', borderRadius:'4px' }}>
                                                        {ce.hecho ? 'Registered' : 'Pending'}
                                                    </span>
                                                </div>
                                                {ce.serial && <ReadField label={'Serial Number'} value={ce.serial} mono C={C} />}
                                                {ce.item && <ReadField label={'Item No.'} value={ce.item} mono C={C} />}
                                                {ce.fabricante && <ReadField label={'Manufacturer'} value={ce.fabricante} C={C} />}
                                                {(ce.vui || ce?.camposExtra?.['VUI']) && <ReadField label={'VUI'} value={ce.vui || ce?.camposExtra?.['VUI']} mono C={C} />}
                                                {Object.entries(ce.camposExtra || {}).filter(([k,v]) => v && k !== 'VUI').map(([key, val]) => (
                                                    <div key={key} style={{ marginTop:'4px' }}>
                                                        <div style={{ fontSize:'9px', color: C.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>{key}</div>
                                                        <div style={{ fontSize:'13px', color: C.text, fontFamily:'monospace', fontWeight:700 }}>{val}</div>
                                                    </div>
                                                ))}
                                                {ce.operario && <div style={{ marginTop:'6px', fontSize:'10px', color:C.textDim }}>👤 {ce.operario}{ce.fecha ? ` · 📅 ${ce.fecha}` : ''}</div>}
                                                {ce.fotos?.length > 0 && <div style={{ marginTop:'4px', fontSize:'10px', color:'#9333ea' }}>📸 {`${ce.fotos.length} photo${ce.fotos.length!==1?"s":""}`}</div>}
                                                {ce.notas && <div style={{ marginTop:'4px', fontSize:'11px', color:C.textDim, fontStyle:'italic', wordBreak:'break-word' }}>{ce.notas}</div>}
                                                <div style={{ marginTop:'10px', display:'flex', justifyContent:'flex-end', gap:'6px' }}>
                                                    <span style={{ fontSize:'9px', background: ce.hecho?C.orange:'transparent', color: ce.hecho?C.bgDark:C.textMuted, border:`1px solid ${ce.hecho?C.orange:C.borderMid}`, padding:'3px 8px', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', borderRadius:'4px' }}>
                                                        {ce.hecho ? '👁 VIEW' : 'Edit'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </main>
        </div>
    </TechContext.Provider>    
    );
}

